import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Video, Play, Pause, RefreshCw, Layers, CheckCircle, AlertTriangle, 
  Download, Send, Sparkles, Settings, Eye, Clock, BarChart2, Check, 
  Edit, Plus, Trash2, ArrowRight, Share2, Upload, FileText, Globe, 
  Volume2, HelpCircle, Laptop, Smartphone, Radio, Tv, FileCode, CheckSquare, 
  ShieldAlert, UserCheck, TrendingUp, Calendar, LayoutGrid, CheckSquare as CheckIcon,
  BookOpen
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { NexoraXVideoMonitor } from './NexoraXVideoMonitor';
import { NEXORAX_SCENE_TEMPLATES } from './NexoraXSceneTemplates';
import { authenticatedFetch } from './direct-delivery/authenticatedApiHelper';

export const GUIDE_VIDEO_ASSETS = {
  "welcome-to-nexorax": {
    videoSrc: "/guides/videos/welcome-to-nexorax.mp4",
    thumbnailSrc: "/guides/thumbnails/welcome-to-nexorax.jpg"
  },
  "nexus-search-guide": {
    videoSrc: "/guides/videos/nexus-search-guide.mp4",
    thumbnailSrc: "/guides/thumbnails/nexus-search-guide.jpg"
  },
  "nexus-hosting-guide": {
    videoSrc: "/guides/videos/nexus-hosting-guide.mp4",
    thumbnailSrc: "/guides/thumbnails/nexus-hosting-guide.jpg"
  },
  "mail-domains-guide": {
    videoSrc: "/guides/videos/mail-domains-guide.mp4",
    thumbnailSrc: "/guides/thumbnails/mail-domains-guide.jpg"
  },
  "nexus-chat-guide": {
    videoSrc: "/guides/videos/nexus-chat-guide.mp4",
    thumbnailSrc: "/guides/thumbnails/nexus-chat-guide.jpg"
  },
  "social-automation-guide": {
    videoSrc: "/guides/videos/social-automation-guide.mp4",
    thumbnailSrc: "/guides/thumbnails/social-automation-guide.jpg"
  },
  "ai-automation-guide": {
    videoSrc: "/guides/videos/ai-automation-guide.mp4",
    thumbnailSrc: "/guides/thumbnails/ai-automation-guide.jpg"
  },
  "voice-matrix-guide": {
    videoSrc: "/guides/videos/voice-matrix-guide.mp4",
    thumbnailSrc: "/guides/thumbnails/voice-matrix-guide.jpg"
  },
  "billing-guide": {
    videoSrc: "/guides/videos/billing-guide.mp4",
    thumbnailSrc: "/guides/thumbnails/billing-guide.jpg"
  },
  "privacy-consent-guide": {
    videoSrc: "/guides/videos/privacy-consent-guide.mp4",
    thumbnailSrc: "/guides/thumbnails/privacy-consent-guide.jpg"
  }
};

// Standard mock guide videos
interface GuideVideo {
  id: string;
  title: string;
  duration: string;
  category: string;
  difficulty: string;
  updatedAt?: string;
  lastUpdated?: string;
  thumbnail: string;
  thumbnailSrc?: string;
  description?: string;
  videoSrc?: string;
  videoStatus?: 'ready' | 'coming-soon' | 'missing-source' | 'draft';
  steps?: string[];
  transcript?: string;
  relatedProducts?: string[];
  ctaLabel?: string;
  ctaTarget?: string;
  status?: 'published' | 'coming-soon';
  script?: {
    intro: string;
    step1: string;
    step2: string;
    step3: string;
    outro: string;
  };
  helpLinks?: string[];
}

// Promotional videos
interface PromoVideo {
  id: string;
  title: string;
  service: string;
  duration: string;
  format: '16:9 Horizontal' | '9:16 Vertical' | '1:1 Square';
  status: 'Draft Generated' | 'Rendering' | 'Needs Review' | 'Approved' | 'Scheduled' | 'Published' | 'Needs Update' | 'Archived' | 'Failed' | 'Connection Required' | 'Permission Required';
  script: {
    hook: string;
    problem: string;
    solution: string;
    benefit: string;
    cta: string;
  };
  hashtags: string[];
  views: number;
  
  // Custom automated distribution & queue parameters
  updateSource?: string;
  websitePageAffected?: string;
  generatedDate?: string;
  platformsSelected?: string[];
  failedPlatformCount?: number;
  analyticsStatus?: string;
  scheduledTime?: string;
  videoQualityScore?: number;

  // Style Engine Properties
  selectedTemplate?: string;
  voiceoverScript?: string;
  platformCaption?: string;
  thumbnailTitle?: string;
}

interface StyleTemplate {
  id: string;
  name: string;
  description: string;
  visualSpec: string;
  thumbnail: string;
}

const styleTemplatesList: StyleTemplate[] = [
  {
    id: 'cyber_city',
    name: 'Cyber City Launch',
    description: 'Neon city silhouette, high-speed vertical data streams, electric cyan overlay, glowing NexoraX logo watermark.',
    visualSpec: 'cyber-city',
    thumbnail: '🌆'
  },
  {
    id: 'neural_brain',
    name: 'Neural Brain Pulse',
    description: 'Animated brain network network nodes, AI circuit lines, purple and deep blue digital pulse waves.',
    visualSpec: 'neural-brain',
    thumbnail: '🧠'
  },
  {
    id: 'purple_reveal',
    name: 'Purple Glass Product Reveal',
    description: 'Wireframe app panels, floating frosted glass containers, smooth zooming isometric views.',
    visualSpec: 'purple-glass',
    thumbnail: '💎'
  },
  {
    id: 'ai_explainer',
    name: 'AI Automation Explainer',
    description: 'Bold typographic keywords, rotating gears/workflow connectivity indicators, text caption popups.',
    visualSpec: 'ai-explainer',
    thumbnail: '⚙️'
  },
  {
    id: 'social_growth',
    name: 'Social Media Growth Promo',
    description: 'Jiggling social media icons, climbing analytical bars, viral rocket/heart particle particles.',
    visualSpec: 'social-growth',
    thumbnail: '🚀'
  },
  {
    id: 'premium_enterprise',
    name: 'Premium Enterprise Promo',
    description: 'Sweeping golden metallic geometry, deep matte-obsidian base, majestic corporate typography.',
    visualSpec: 'premium-enterprise',
    thumbnail: '🏢'
  }
];

interface GuidesAndVideosProps {
  onNavigateLandingTab?: (tab: string) => void;
}

const generateScenesForGuide = (guideId: string) => {
  return NEXORAX_SCENE_TEMPLATES[guideId] || null;
};

const enrichAndCheckDuplicates = (list: any[]) => {
  // Collect stats to detect duplicates programmatically
  const durationCount: Record<string, number> = {};
  const thumbCount: Record<string, number> = {};
  const srcCount: Record<string, number> = {};

  list.forEach(g => {
    if (g.duration) durationCount[g.duration] = (durationCount[g.duration] || 0) + 1;
    if (g.thumbnailSrc) thumbCount[g.thumbnailSrc] = (thumbCount[g.thumbnailSrc] || 0) + 1;
    if (g.videoSrc) srcCount[g.videoSrc] = (srcCount[g.videoSrc] || 0) + 1;
  });

  return list.map(g => {
    const template = generateScenesForGuide(g.id);
    
    // Identify duplicate standard placeholders
    const isDuplicate = 
      (g.duration && durationCount[g.duration] > 1 && g.id !== 'welcome-to-nexorax') ||
      (g.thumbnailSrc && thumbCount[g.thumbnailSrc] > 1 && g.id !== 'welcome-to-nexorax') ||
      (g.videoSrc && srcCount[g.videoSrc] > 1 && g.id !== 'welcome-to-nexorax') ||
      (g.videoSrc && g.videoSrc.includes('welcome') && g.id !== 'welcome-to-nexorax') ||
      (!g.videoIsUnique && template && g.id !== 'welcome-to-nexorax');

    return {
      ...g,
      generatedScenes: template || g.generatedScenes || null,
      videoIsUnique: isDuplicate ? false : (g.videoIsUnique ?? (g.id === 'welcome-to-nexorax')),
      videoDuplicateDetected: !!isDuplicate,
      videoMode: isDuplicate ? 'generated-scenes' : (g.videoMode || 'rendered-mp4')
    };
  });
};

const isAdminOrPreview = () => {
  if (typeof window === 'undefined') return false;
  const url = window.location.href.toLowerCase();
  const host = window.location.hostname.toLowerCase();
  const hasSession = !!sessionStorage.getItem('admin session verification');
  return (
    import.meta.env.DEV ||
    hasSession ||
    host.includes('ais-dev') ||
    host.includes('ais-pre') ||
    host.includes('localhost') ||
    host.includes('127.0.0.1') ||
    url.includes('admin') ||
    url.includes('preview') ||
    url.includes('debug')
  );
};

export default function GuidesAndVideos({ onNavigateLandingTab }: GuidesAndVideosProps) {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'guides' | 'promos' | 'watcher' | 'approval' | 'analytics' | 'settings' | 'factory'>('guides');
  const [selectedGuideCategory, setSelectedGuideCategory] = useState<string>('All');
  const [selectedPromoCategory, setSelectedPromoCategory] = useState<string>('All');
  const [completedSteps, setCompletedSteps] = useState<Record<string, Record<number, boolean>>>({});
  const [activeGuideDetailTab, setActiveGuideDetailTab] = useState<'steps' | 'transcript'>('steps');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const guideVideoRef = useRef<HTMLVideoElement | null>(null);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1.0);
  
  // Custom states for interactive preview player
  const [previewingVideo, setPreviewingVideo] = useState<any | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [playerProgress, setPlayerProgress] = useState<number>(0);
  const [activePlaybackFormat, setActivePlaybackFormat] = useState<'16:9' | '9:16' | '1:1'>('16:9');

  useEffect(() => {
    if (guideVideoRef.current) {
      guideVideoRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed, previewingVideo]);
  
  // Audio waveform visualizer animation state
  const [waveformBars, setWaveformBars] = useState<number[]>(new Array(15).fill(20));

  // Auto sitemap watcher simulations and histories synced to backend
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [scanProgress, setScanProgress] = useState<number>(0);
  const [scanLogs, setScanLogs] = useState<string[]>([]);
  const [detectedChanges, setDetectedChanges] = useState<any[]>([]);
  const [watcherHistory, setWatcherHistory] = useState<any[]>([]);

  // Brand profile settings - Safe public info only synced to backend
  const [publicBrandSettings, setPublicBrandSettings] = useState({
    websiteUrl: 'https://nexoraxdigital.co.uk',
    publicEmail: 'info@nexoraxdigital.co.uk',
    publicPhone: '08000 622827',
    supportLink: 'https://support.nexoraxdigital.co.uk',
    bookingLink: 'https://nexoraxdigital.co.uk/book-demo'
  });

  // Admin controls synced to backend
  const [adminControls, setAdminControls] = useState({
    autoGenerateGuides: true,
    autoGeneratePromos: true,
    requireApprovalBeforePublish: true,
    autoPublishEnabled: false,
    defaultVideoLength: '20 Seconds',
    voiceoverStyle: 'British Director Echo',
    logoPlacement: 'Top Right Corner',
    scanFrequency: 'Every 6 hours',
    platforms: {
      website: true,
      youtube: true,
      tiktok: true,
      instagram: true,
      linkedin: true,
      x: true
    }
  });

  // Auto Publish Fine-Grained Rules synced to backend
  const [autoPublishRules, setAutoPublishRules] = useState({
    requireApproval: true,
    autoPostSelectedPlatformsOnly: false,
    autoPostLowRiskOnly: true,
    neverAutoPostPricingLegal: true,
    pauseIfContactDetailsMissing: true,
    pauseIfQualityScoreBelowThreshold: false,
    pauseIfCaptionsCtaMissing: true
  });

  // Social Posting Connector Dashboard Data synced to backend
  const [connectors, setConnectors] = useState<any[]>([]);

  // Analytics feedback metrics synced from backend
  const [analyticsFeedback, setAnalyticsFeedback] = useState({
    views: 8060,
    watchTime: "44h 30m",
    engagement: "12.4%",
    conversionsRate: "3.8%",
    bookings: 18,
    topPlatforms: ["LinkedIn", "TikTok", "YouTube Shorts"],
    recommendations: [
      { id: "r-1", type: "positive", text: "Deploy Cyber City Launch template more often (engagement +32%)", weight: 0.8 },
      { id: "r-2", type: "actionive", text: "Prioritize vertical 9:16 Shorts over horizontal 16:9 for faster conversions", weight: 0.95 },
      { id: "r-3", type: "warning", text: "Shorten intro hooks on Website performance promos to reduce initial drop-offs", weight: 0.6 }
    ]
  });

  // Sync state with backend on tab changes or mount
  const refreshPromoState = async () => {
    try {
      const res = await fetch('/api/promos/state');
      if (res.ok) {
        const data = await res.json();
        if (data.promos) setPromos(data.promos);
        if (data.brandProfile) setPublicBrandSettings(data.brandProfile);
        if (data.connectors) setConnectors(data.connectors);
        if (data.watcherHistory) setWatcherHistory(data.watcherHistory);
        if (data.detectedChanges) setDetectedChanges(data.detectedChanges);
        if (data.adminControls) setAdminControls(data.adminControls);
        if (data.autoPublishRules) setAutoPublishRules(data.autoPublishRules);
        if (data.analyticsFeedback) setAnalyticsFeedback(data.analyticsFeedback);
      }

      // Sync central dynamic guides from our automated admin factory backend
      const guidesRes = await fetch('/api/guides/videos');
      if (guidesRes.ok) {
        const guidesData = await guidesRes.json();
        if (guidesData.videos) {
          setRawVideos(guidesData.videos);
          
          const mapped = guidesData.videos.map((v: any, idx: number) => {
            // Unpack step text lines from script StepByStep block safely
            let stepsArray: string[] = [];
            if (v.script?.stepByStep && typeof v.script.stepByStep === 'string') {
              stepsArray = v.script.stepByStep
                .split('.')
                .map((s: string) => s.trim())
                .filter((s: string) => s.length > 0 && !s.toLowerCase().startsWith('step '));
            }
            if (stepsArray.length === 0) {
              stepsArray = [
                v.script?.walkthrough || `Launch standard ${v.product} operations.`,
                "Review visual configurations on main display sectors.",
                "Execute direct actions and secure logs."
              ];
            }

            const fallbackUrls = [
              "https://assets.mixkit.co/videos/preview/mixkit-rotating-planet-earth-in-outer-space-43034-large.mp4",
              "https://assets.mixkit.co/videos/preview/mixkit-abstract-laser-lights-background-31123-large.mp4",
              "https://assets.mixkit.co/videos/preview/mixkit-futuristic-subway-station-with-digital-screens-43950-large.mp4",
              "https://assets.mixkit.co/videos/preview/mixkit-tech-animation-of-lines-and-numbers-41718-large.mp4",
              "https://assets.mixkit.co/videos/preview/mixkit-circuit-board-lights-running-43572-large.mp4",
              "https://assets.mixkit.co/videos/preview/mixkit-digital-server-room-blinking-lights-30114-large.mp4",
              "https://assets.mixkit.co/videos/preview/mixkit-matrix-hologram-effect-with-numbers-32900-large.mp4"
            ];

            const prod = v.product || "";
            let mappedVideoSrc: string | undefined = undefined;
            let mappedThumbnailSrc: string | undefined = undefined;
            let mappedVideoStatus: 'ready' | 'coming-soon' = 'coming-soon';
            let mappedStatus: 'published' | 'coming-soon' = 'coming-soon';

            if (prod === 'Nexus Search' || prod.toLowerCase().includes('search')) {
              mappedVideoSrc = '/guides/videos/nexus-search-guide.mp4';
              mappedThumbnailSrc = '/guides/thumbnails/nexus-search-guide.jpg';
              mappedVideoStatus = 'ready';
              mappedStatus = 'published';
            } else if (prod === 'Hosting' || prod.toLowerCase().includes('hosting')) {
              mappedVideoSrc = '/guides/videos/nexus-hosting-guide.mp4';
              mappedThumbnailSrc = '/guides/thumbnails/nexus-hosting-guide.jpg';
              mappedVideoStatus = 'ready';
              mappedStatus = 'published';
            } else if (prod === 'Mail & Domains Platform' || prod === 'Mail & Domains' || prod.toLowerCase().includes('mail') || prod.toLowerCase().includes('domain')) {
              mappedVideoSrc = '/guides/videos/mail-domains-guide.mp4';
              mappedThumbnailSrc = '/guides/thumbnails/mail-domains-guide.jpg';
              mappedVideoStatus = 'ready';
              mappedStatus = 'published';
            } else if (prod === 'Chat' || prod.toLowerCase().includes('chat')) {
              mappedVideoSrc = '/guides/videos/nexus-chat-guide.mp4';
              mappedThumbnailSrc = '/guides/thumbnails/nexus-chat-guide.jpg';
              mappedVideoStatus = 'ready';
              mappedStatus = 'published';
            } else if (prod === 'Social Media Automation' || prod.toLowerCase().includes('social')) {
              mappedVideoSrc = '/guides/videos/social-automation-guide.mp4';
              mappedThumbnailSrc = '/guides/thumbnails/social-automation-guide.jpg';
              mappedVideoStatus = 'ready';
              mappedStatus = 'published';
            } else if (prod === 'AI Automation' || prod.toLowerCase().includes('ai-automation')) {
              mappedVideoSrc = '/guides/videos/ai-automation-guide.mp4';
              mappedThumbnailSrc = '/guides/thumbnails/ai-automation-guide.jpg';
              mappedVideoStatus = 'ready';
              mappedStatus = 'published';
            } else if (prod === 'Voice Matrix' || prod === 'NexoraX Voice' || prod.toLowerCase().includes('voice')) {
              mappedVideoSrc = '/guides/videos/voice-matrix-guide.mp4';
              mappedThumbnailSrc = '/guides/thumbnails/voice-matrix-guide.jpg';
              mappedVideoStatus = 'ready';
              mappedStatus = 'published';
            } else if (prod === 'Tiers' || prod === 'Billing' || prod.toLowerCase().includes('billing') || prod.toLowerCase().includes('tier') || prod.toLowerCase().includes('payment')) {
              mappedVideoSrc = '/guides/videos/billing-guide.mp4';
              mappedThumbnailSrc = '/guides/thumbnails/billing-guide.jpg';
              mappedVideoStatus = 'ready';
              mappedStatus = 'published';
            } else if (prod === 'Privacy & Consent' || prod === 'Privacy' || prod.toLowerCase().includes('privacy') || prod.toLowerCase().includes('consent')) {
              mappedVideoSrc = '/guides/videos/privacy-consent-guide.mp4';
              mappedThumbnailSrc = '/guides/thumbnails/privacy-consent-guide.jpg';
              mappedVideoStatus = 'ready';
              mappedStatus = 'published';
            }

            return {
              id: v.id,
              title: v.title,
              duration: v.durationTarget || "2m 30s",
              category: v.category || "Platform Guides",
              difficulty: v.difficulty || "Beginner",
              lastUpdated: v.updatedAt ? new Date(v.updatedAt).toLocaleDateString() : "Present",
              thumbnail: "📹",
              description: v.script?.intro || `Guided product walkthrough for ${v.product}`,
              videoSrc: mappedVideoSrc,
              thumbnailSrc: mappedThumbnailSrc,
              videoStatus: mappedVideoStatus,
              steps: stepsArray,
              transcript: v.transcript || v.script?.walkthrough || "Dynamic transcript draft prepared.",
              relatedProducts: [v.product],
              ctaLabel: v.ctaLabel || "Learn More",
              ctaTarget: v.ctaTarget || "/home",
              status: mappedStatus,
              script: {
                intro: v.script?.intro || "Premium guide walkthrough initialized.",
                step1: stepsArray[0] || "Step 1.",
                step2: stepsArray[1] || "Step 2.",
                step3: stepsArray[2] || "Step 3.",
                outro: v.script?.closing || "Launch automated sequence."
              },
              helpLinks: ["Client Dashboard", "Infrastructure Config"],
              raw: v
            };
          });

          setGuides(prev => {
            const merged = [...prev];
            mapped.forEach((g: any) => {
              const existingIdx = merged.findIndex(m => m.id === g.id || m.title.toLowerCase() === g.title.toLowerCase());
              if (existingIdx !== -1) {
                const oldGuide = merged[existingIdx];
                const finalVideoSrc = oldGuide.videoSrc && oldGuide.videoSrc.startsWith('/') ? oldGuide.videoSrc : (g.videoSrc || oldGuide.videoSrc);
                const finalThumbnailSrc = oldGuide.thumbnailSrc && oldGuide.thumbnailSrc.startsWith('/') ? oldGuide.thumbnailSrc : (g.thumbnailSrc || oldGuide.thumbnailSrc);
                const finalVideoStatus = oldGuide.videoStatus && finalVideoSrc ? oldGuide.videoStatus : (g.videoStatus || oldGuide.videoStatus);
                const finalStatus = oldGuide.status && finalVideoSrc ? oldGuide.status : (g.status || oldGuide.status);

                merged[existingIdx] = { 
                  ...oldGuide, 
                  ...g,
                  videoSrc: finalVideoSrc,
                  thumbnailSrc: finalThumbnailSrc,
                  videoStatus: finalVideoStatus,
                  status: finalStatus,
                  id: oldGuide.id // Keep existing frontend id
                };
              } else {
                merged.push(g);
              }
            });

            return enrichAndCheckDuplicates(merged.map(guide => {
              const asset = (GUIDE_VIDEO_ASSETS as any)[guide.id];
              return {
                ...guide,
                videoSrc: asset?.videoSrc ?? guide.videoSrc ?? "",
                thumbnailSrc: asset?.thumbnailSrc ?? guide.thumbnailSrc ?? "",
                videoStatus: asset?.videoSrc ? "ready" : "coming-soon",
                status: asset?.videoSrc ? "published" : guide.status
              };
            }));
          });
        }
      }
    } catch (err) {
      console.error("Failed to load backend promo or guides state", err);
    }
  };

  useEffect(() => {
    refreshPromoState();
  }, [activeTab]);

  // Wrapper to update single promo script parameters to backend
  const updatePromoBackend = async (id: string, payload: Partial<PromoVideo>) => {
    try {
      const res = await fetch(`/api/promos/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'success' && data.promo) {
          setPromos(prev => prev.map(p => p.id === id ? data.promo : p));
          return data.promo;
        }
      }
    } catch (err) {
      console.error("Failed to update promo backend", err);
    }
    // local fallback
    setPromos(prev => prev.map(p => p.id === id ? { ...p, ...payload } as any : p));
    return null;
  };

  // Wrapper to delete promo script
  const deletePromoBackend = async (id: string) => {
    try {
      const res = await fetch(`/api/promos/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setPromos(prev => prev.filter(p => p.id !== id));
        showToast("Campaign asset removed from general queues.");
      }
    } catch (err) {
      console.error("Failed to delete promo", err);
    }
  };

  // Wrapper to toggle social connectors details
  const handleToggleConnectorBackend = async (connectorId: string, field: string) => {
    try {
      const res = await fetch('/api/promos/connectors/toggle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: connectorId, field })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'success' && data.connectors) {
          setConnectors(data.connectors);
          showToast(`Handshake updated for social node parameter: ${field}`);
        }
      }
    } catch (err) {
      console.error("Failed to toggle connector handshake", err);
    }
  };

  // Wrapper to save brand configurations
  const handleSaveBrandProfileBackend = async (updatedProfile: typeof publicBrandSettings) => {
    try {
      const res = await fetch('/api/promos/brand-profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedProfile)
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setPublicBrandSettings(data.brandProfile);
        showToast("✓ Brand integrity credentials validated and synchronized.");
      } else {
        showToast(`⚠️ Alignment Warning: ${data.message || 'Verification rejected'}`);
      }
    } catch (err) {
      console.error("Failed to save brand profile", err);
      showToast("❌ Unable to communicate with Brand Verification nodes.");
    }
  };

  // Wrapper to save Settings
  const handleSaveSettingsBackend = async (controls?: typeof adminControls, rules?: typeof autoPublishRules) => {
    try {
      const res = await fetch('/api/promos/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminControls: controls, autoPublishRules: rules })
      });
      if (res.ok) {
        showToast("✓ System configurations and publishing schedules synchronized.");
      }
    } catch (err) {
      console.error("Failed to save marketing configs", err);
    }
  };

  // Selected Platform Version for Assets Builder (Requested)
  const [activeVersionPlatform, setActiveVersionPlatform] = useState<string>('YouTube');
  const [selectedPreviewPlatform, setSelectedPreviewPlatform] = useState<string>('YouTube');
  const [selectedDefaultStyles, setSelectedDefaultStyles] = useState<string[]>([
    'Cyber City Launch', 'Neural Brain Pulse', 'Purple Glass Product Reveal'
  ]);

  // Interactive scheduler dialog state (Requested)
  const [activeSchedulingId, setActiveSchedulingId] = useState<string | null>(null);
  const [schedulerConfig, setSchedulerConfig] = useState({
    timeOption: 'Publish now',
    scheduleTime: '2026-06-15T18:00',
    stagger: false,
    repeat: false,
    repostAfter7Days: false,
    addToCalendar: false
  });

  // Edit script inline state (Requested)
  const [editingScriptId, setEditingScriptId] = useState<string | null>(null);
  const [editingScriptValues, setEditingScriptValues] = useState({
    hook: '',
    problem: '',
    solution: '',
    benefit: '',
    cta: ''
  });

  // ==========================================
  // GUIDE VIDEO FACTORY STATES (SCREEN 1 - 10)
  // ==========================================
  const [factoryActiveSubTab, setFactoryActiveSubTab] = useState<'dashboard' | 'templates' | 'studio' | 'render-queue' | 'approval-center' | 'social'>('dashboard');
  const [rawVideos, setRawVideos] = useState<any[]>([]);
  const [selectedProductTemplate, setSelectedProductTemplate] = useState<string>('Nexus Search');
  
  const [isCompilingScript, setIsCompilingScript] = useState<boolean>(false);
  const [isCompilingStoryboard, setIsCompilingStoryboard] = useState<boolean>(false);
  const [isCompilingCaptions, setIsCompilingCaptions] = useState<boolean>(false);
  const [isTriggeringRender, setIsTriggeringRender] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [isSendingToSocial, setIsSendingToSocial] = useState<boolean>(false);

  // Custom template input state
  const [newTemplateForm, setNewTemplateForm] = useState({
    product: 'Nexus Search',
    title: '',
    category: 'Platform Guides',
    targetAudience: 'General business users',
    difficulty: 'Beginner' as const,
    durationTarget: '2m 30s',
    videoMode: 'Full Walkthrough' as const,
    ctaLabel: 'Learn More',
    ctaTarget: '/home'
  });

  // Guides state
  const [guides, setGuides] = useState<GuideVideo[]>(enrichAndCheckDuplicates([
    {
      id: 'welcome-to-nexorax',
      title: 'Welcome to NexoraX',
      category: 'Getting Started',
      description: 'Get started with the NexoraX ecosystem. Learn how your workspace connects your website, custom tools, and automated assets in one dashboard.',
      difficulty: 'Easy',
      duration: '1:45',
      updatedAt: '10 June 2026',
      videoSrc: '/guides/videos/welcome-to-nexorax.mp4',
      thumbnailSrc: '/guides/thumbnails/welcome-to-nexorax.jpg',
      videoStatus: 'ready',
      thumbnail: '👋',
      status: 'published',
      steps: [
        'Open nexoraxdigital.co.uk and discover the workspace features.',
        'Navigate the central console and functional dashboard channels.',
        'Configure your public contact card and customer support details.',
        'Explore the different service modules available for your business.'
      ],
      transcript: 'Welcome to NexoraX. In this guide, we walk you through your central dashboard. We show how to connect your public channels, invite teammates, and manage customer actions smoothly.',
      relatedProducts: ['Ecosystem', 'Workspace'],
      ctaLabel: 'Explore Ecosystem',
      ctaTarget: 'Ecosystem'
    },
    {
      id: 'nexus-search-guide',
      title: 'How to Use Nexus Search',
      category: 'Nexus Search',
      description: 'Learn how to search, discover business opportunities, save results, and send findings to Nexus Agents or WorkSuite.',
      difficulty: 'Easy',
      duration: '3:20',
      updatedAt: '11 June 2026',
      videoSrc: '/guides/videos/nexus-search-guide.mp4',
      thumbnailSrc: '/guides/thumbnails/nexus-search-guide.jpg',
      videoStatus: 'ready',
      thumbnail: '🔍',
      status: 'published',
      steps: [
        'Open the Nexus Search panel from your main navigation menu.',
        'Enter target search criteria such as industry or location.',
        'Review the compiled list of potential business opportunities.',
        'Save results of interest to keep them organized inside your workspace.',
        'Forward findings directly to customer support assistants or active tasks.'
      ],
      transcript: 'Nexus Search helps you quickly find business opportunities across the web. Specify target terms, collect relevant entries, and save them for instant reference or followup.',
      relatedProducts: ['Nexus Search', 'WorkSuite'],
      ctaLabel: 'Open Nexus Search',
      ctaTarget: 'Nexus Search'
    },
    {
      id: 'nexus-hosting-guide',
      title: 'How to Set Up Nexus Hosting',
      category: 'Hosting',
      description: 'Learn how hosting, SSL, domain connection, deployment checks, and public-preview readiness work inside NexoraX.',
      difficulty: 'Medium',
      duration: '3:05',
      updatedAt: '12 June 2026',
      videoSrc: '/guides/videos/nexus-hosting-guide.mp4',
      thumbnailSrc: '/guides/thumbnails/nexus-hosting-guide.jpg',
      videoStatus: 'ready',
      thumbnail: '🌐',
      status: 'published',
      steps: [
        'Navigate to the Hosting section in your workspace controls.',
        'Enter your purchased web address or domain name.',
        'Confirm successful SSL configuration to guarantee secure browsing.',
        'Run the integrated deployment checks to find layout or link issues.',
        'Publish the live web pages to your global audience.'
      ],
      transcript: 'Nexus Hosting makes deploying websites intuitive. Connect your custom domains, access continuous SSL health updates, and launch custom designs with built-in speed and search-engine friendly settings.',
      relatedProducts: ['Hosting', 'Mail & Domains Platform'],
      ctaLabel: 'Open Nexus Hosting',
      ctaTarget: 'Hosting'
    },
    {
      id: 'mail-domains-guide',
      title: 'How to Set Up Business Email & Domains',
      category: 'Mail & Domains',
      description: 'Connect your domain, request business email addresses, review DNS records, and prepare SPF, DKIM, and DMARC checks.',
      difficulty: 'Medium',
      duration: '2:40',
      updatedAt: '13 June 2026',
      videoSrc: '/guides/videos/mail-domains-guide.mp4',
      thumbnailSrc: '/guides/thumbnails/mail-domains-guide.jpg',
      videoStatus: 'ready',
      thumbnail: '📬',
      status: 'published',
      steps: [
        'Go to the Mail & Domains management tab.',
        'Request secure business email mailboxes matching your active company domain.',
        'Open your external domain records provider console.',
        'Input the required DNS destination and record parameters.',
        'Wait for SPF, DKIM, and DMARC check marks to clear on our dashboard.',
        'Send a test email to verify correct incoming and outgoing traffic.'
      ],
      transcript: 'Professional communication starts with a branded address. Connect custom DNS, activate mail routing, and secure your emails with standard DMARC policies in just a few minutes.',
      relatedProducts: ['Mail & Domains Platform'],
      ctaLabel: 'Set Up Mail & Domains',
      ctaTarget: 'Mail & Domains Platform'
    },
    {
      id: 'nexus-chat-guide',
      title: 'How to Use Nexus Chat',
      category: 'Chat',
      description: 'Use Nexus Chat as a website assistant, support assistant, pricing assistant, and enquiry capture assistant.',
      difficulty: 'Easy',
      duration: '1:50',
      updatedAt: '13 June 2026',
      videoSrc: '/guides/videos/nexus-chat-guide.mp4',
      thumbnailSrc: '/guides/thumbnails/nexus-chat-guide.jpg',
      videoStatus: 'ready',
      thumbnail: '💬',
      status: 'published',
      steps: [
        'Navigate to the Chat Assistant designer page.',
        'Define assistant guidelines such as greeting tone and answering range.',
        'Incorporate public FAQ lists, common help topics, and booking procedures.',
        'Set up contact info fields to secure prospective visitor details.',
        'Test live chat response behavior inside your preview console.',
        'Embed the chat widget onto your public website using the copyable tag.'
      ],
      transcript: 'Improve client support instantaneously. Nexus Chat operates as an automated workspace companion, capturing leads, answering frequently asked questions, and forwarding messages to your mailbox.',
      relatedProducts: ['Chat', 'AI Automation'],
      ctaLabel: 'Open Nexus Chat',
      ctaTarget: 'Chat'
    },
    {
      id: 'social-automation-guide',
      title: 'How to Create Social Media Automation',
      category: 'Social Automation',
      description: 'Create captions, campaign drafts, approval queues, and social content packs from one workspace.',
      difficulty: 'Medium',
      duration: '3:15',
      updatedAt: '14 June 2026',
      videoSrc: '/guides/videos/social-automation-guide.mp4',
      thumbnailSrc: '/guides/thumbnails/social-automation-guide.jpg',
      videoStatus: 'ready',
      thumbnail: '📊',
      status: 'published',
      steps: [
        'Access the Social Media Automation hub in your sidebar.',
        'Draft your promotional goals or highlight specific product additions.',
        'Let the built-in copy generator draft diverse social captions and tags.',
        'Set up campaign schedules inside your collective calendar grid.',
        'Check approval queues to verify consistency before queueing posts.',
        'Publish your curated social blocks to all connected networks.'
      ],
      transcript: 'Social Media Automation coordinates your digital presence. Generate creative copy, plan visual posts, and manage bulk publication lists without manual overhead or repetitive posting chores.',
      relatedProducts: ['Social Media Automation'],
      ctaLabel: 'Launch Social Automation',
      ctaTarget: 'Social Media Automation'
    },
    {
      id: 'ai-automation-guide',
      title: 'How to Build an AI Automation Workflow',
      category: 'AI Automation',
      description: 'Create workflows that connect triggers, Nexus Agents, approvals, tasks, and outputs.',
      difficulty: 'Medium',
      duration: '3:45',
      updatedAt: '15 June 2026',
      videoSrc: '/guides/videos/ai-automation-guide.mp4',
      thumbnailSrc: '/guides/thumbnails/ai-automation-guide.jpg',
      videoStatus: 'ready',
      thumbnail: '⚙️',
      status: 'published',
      steps: [
        'Open the AI Automation designer module.',
        'Choose a starting trigger, like a new contact form entry.',
        'Add a custom processing rule or helper block to evaluate the entry.',
        'Define key workflow outputs such as email alerts, tasks, or followups.',
        'Conduct a testing loop to inspect data variables.',
        'Turn on the live workflow to keep your background tasks automated.'
      ],
      transcript: 'AI Automation workflows ensure operations run 24/7 without delays. Link actions, parse files, and optimize business processes safely and efficiently.',
      relatedProducts: ['AI Automation'],
      ctaLabel: 'Start AI Automation',
      ctaTarget: 'AI Automation'
    },
    {
      id: 'voice-matrix-guide',
      title: 'How to Use Voice Matrix',
      category: 'Voice Matrix',
      description: 'Set up voice assistant flows for customer questions, website support, bookings, and service guidance.',
      difficulty: 'Medium',
      duration: '2:50',
      updatedAt: '15 June 2026',
      videoSrc: '/guides/videos/voice-matrix-guide.mp4',
      thumbnailSrc: '/guides/thumbnails/voice-matrix-guide.jpg',
      videoStatus: 'ready',
      thumbnail: '🎙️',
      status: 'published',
      steps: [
        'Access the Voice Matrix setup and dashboard.',
        'Design custom voice configurations and greetings.',
        'Structure automated response steps for common user enquiries.',
        'Connect local call forwarding endpoints to your active team lines.',
        'Validate speech options and script paths from the tester drawer.',
        'Bind your customized assistant rules to public business hotlines.'
      ],
      transcript: 'Streamline answering queues with absolute precision. The Voice Matrix handles customer assistance, guides callers through menu options, and documents messages effortlessly.',
      relatedProducts: ['Voice Matrix', 'Chat'],
      ctaLabel: 'Explore Voice Matrix',
      ctaTarget: 'NexoraX Voice'
    },
    {
      id: 'billing-guide',
      title: 'Understanding Search Credits, Plans, and Invoices',
      category: 'Billing',
      description: 'Learn how search credits, plans, invoices, usage records, and payment reconciliation work.',
      difficulty: 'Easy',
      duration: '1:55',
      updatedAt: '15 June 2026',
      videoSrc: '/guides/videos/billing-guide.mp4',
      thumbnailSrc: '/guides/thumbnails/billing-guide.jpg',
      videoStatus: 'ready',
      thumbnail: '💳',
      status: 'published',
      steps: [
        'Open the Billing and Tiers panel in your workspace settings.',
        'Monitor available search credits and check remaining daily limits.',
        'Examine historical payments and direct billing templates.',
        'Compare plan details from standard to customized categories.',
        'Retrieve official billing statements and invoices for corporate records.'
      ],
      transcript: 'This walkthrough explains credit management and invoice tracking. Keep business systems running smoothly by understanding usage records, adjusting allowances, and retrieving receipts.',
      relatedProducts: ['Tiers', 'Ecosystem'],
      ctaLabel: 'View Payment Tiers',
      ctaTarget: 'Tiers'
    },
    {
      id: 'privacy-consent-guide',
      title: 'Privacy & Consent Guide',
      category: 'Privacy',
      description: 'Manage cookie consents, submit GDPR/CCPA data requests, and review general privacy policies.',
      difficulty: 'Easy',
      duration: '1:30',
      updatedAt: '15 June 2026',
      videoSrc: '/guides/videos/privacy-consent-guide.mp4',
      thumbnailSrc: '/guides/thumbnails/privacy-consent-guide.jpg',
      videoStatus: 'ready',
      thumbnail: '🛡️',
      status: 'published',
      steps: [
        'Click the Cookie and Consent indicator at the bottom footer.',
        'Examine cookie details of our active public channels.',
        'Toggle tracking preferences to lock down your settings.',
        'Open the main Privacy & Consent informational pages.',
        'Learn how data requests or removal submissions are processed.'
      ],
      transcript: 'We prioritize absolute user trust and direct compliance. This guide reviews cookie categories and demonstrates how visitors can submit data queries to protect their privacy.',
      relatedProducts: ['Privacy & Consent'],
      ctaLabel: 'Open Cookie Settings',
      ctaTarget: 'Privacy'
    }
  ]));

  const [selectedGuide, setSelectedGuide] = useState<any>(guides[0]);

  const handleSelectGuide = (guide: any) => {
    const asset = (GUIDE_VIDEO_ASSETS as any)[guide.id];

    const nextGuide = {
      ...guide,
      videoSrc: asset?.videoSrc ?? guide.videoSrc ?? "",
      thumbnailSrc: asset?.thumbnailSrc ?? guide.thumbnailSrc ?? "",
      videoStatus: asset?.videoSrc ? "ready" : guide.videoStatus ?? "coming-soon",
      status: asset?.videoSrc ? "published" : guide.status
    };

    setSelectedGuide(nextGuide);
    setPreviewingVideo(nextGuide);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedGuideCategory(category);

    const nextList =
      category === "All"
        ? guides
        : guides.filter((g) => {
            const cat = category.toLowerCase();
            const gCat = g.category.toLowerCase();
            if (cat === 'hosting') return gCat.includes('hosting');
            if (cat === 'chat') return gCat.includes('chat');
            if (cat === 'social automation') return gCat.includes('social');
            if (cat === 'ai automation') return gCat.includes('ai automation');
            if (cat === 'billing') return gCat.includes('billing') || gCat.includes('subscription');
            if (cat === 'privacy') return gCat.includes('privacy') || gCat.includes('consent');
            if (cat === 'voice matrix') return gCat.includes('voice');
            if (cat === 'getting started') return gCat.includes('start');
            if (cat === 'nexus search') return gCat.includes('search');
            if (cat === 'mail & domains') return gCat.includes('mail') || gCat.includes('domain');
            
            return g.category === category;
          });

    if (nextList.length > 0) {
      handleSelectGuide(nextList[0]);
    }
  };

  // Default to selecting the first guide on mount
  useEffect(() => {
    if (guides && guides.length > 0) {
      const currentMatched = selectedGuide ? guides.find(g => g.id === selectedGuide.id) : null;
      if (currentMatched) {
        setSelectedGuide(currentMatched);
      } else if (!selectedGuide) {
        handleSelectGuide(guides[0]);
      }
    }
  }, [guides]);

  // Promos state / Complete social marketing campaign queue (Requested)
  const [promos, setPromos] = useState<PromoVideo[]>([
    {
      id: 'p-1',
      title: 'AI Workflow Automation Promo',
      service: 'AI Automation',
      duration: '20s',
      format: '9:16 Vertical',
      status: 'Needs Review',
      script: {
        hook: 'Still doing repetitive business tasks manually?',
        problem: 'Slowing down operations, losing leads, and drowning in administrative bottlenecks.',
        solution: 'NexoraX Digital automates workflows, customer replies, and content creation using AI.',
        benefit: 'Save time, reduce admin errors, and scale your business faster with 0.8s speed.',
        cta: `Visit ${publicBrandSettings.websiteUrl} or contact ${publicBrandSettings.publicPhone} today.`
      },
      hashtags: ['AIAutomation', 'SaaS', 'BusinessOptimization', 'NexoraxDigital'],
      views: 1240,
      updateSource: 'New website service added',
      websitePageAffected: '/services/ai-automation',
      generatedDate: '2026-06-11 12:45',
      platformsSelected: ['YouTube Shorts', 'TikTok', 'Instagram Reels'],
      failedPlatformCount: 0,
      analyticsStatus: 'Awaiting Publish',
      videoQualityScore: 92,
      selectedTemplate: 'Cyber City Launch',
      voiceoverScript: '[Computer sounds humming] Introducing the future of digital automation. Still doing business tasks manually? AI Automation by NexoraX helps you automate workflows, customer replies, content, and daily operations. Save time. Reduce admin. Scale faster with intelligent automation. Visit nexoraxdigital.co.uk and launch smarter today.',
      thumbnailTitle: 'AI WORKFLOW AUTOMATION',
      platformCaption: '🎯 Banish manual admin chores forever. NexoraX Digital deploys robust self-driving routines to cut workflow latencies to 0.8 seconds FLAT. Lock in your setup today! nexoraxdigital.co.uk | 08000 622827'
    },
    {
      id: 'p-2',
      title: 'Decentralized Landing Page Speed',
      service: 'Website Development',
      duration: '15s',
      format: '9:16 Vertical',
      status: 'Approved',
      script: {
        hook: 'Is your business website loading too slowly?',
        problem: 'Every additional second of load time cuts your customer conversions by 20%.',
        solution: 'NexoraX answers with zero-friction, ultra-fast custom static portals on bare-metal racks.',
        benefit: 'Secure a 100% Core Web Vitals score and capture every prospective visitor.',
        cta: `Secure your site now via ${publicBrandSettings.websiteUrl}.`
      },
      hashtags: ['WebPerformance', 'NexoraXSites', 'FastLoading', 'HetznerBareMetal'],
      views: 2315,
      updateSource: 'Homepage section updated',
      websitePageAffected: '/',
      generatedDate: '2026-06-10 16:30',
      platformsSelected: ['YouTube Shorts', 'TikTok', 'Instagram Reels', 'X / Twitter'],
      failedPlatformCount: 0,
      analyticsStatus: 'Pending Feed',
      videoQualityScore: 88,
      selectedTemplate: 'Neural Brain Pulse',
      voiceoverScript: '[Synth wave pulse] Fast loading times drive customer retention. Is your business website running slow? Let NexoraX construct ultra-fast static portals on bare-metal nodes. Maintain 100% core indicators and secure every conversion. Secure your NexoraX landing page now.',
      thumbnailTitle: '100% CORE WEB VITALS',
      platformCaption: '⚡ Slow loads drop revenue on impact. NexoraX builds decentralized static pages mapped to direct CDN backbones for instantaneous 0.8s load-outs. Join the speed circle: nexoraxdigital.co.uk'
    },
    {
      id: 'p-3',
      title: 'Interactive Chatbot Assistants Promo',
      service: 'AI Agents',
      duration: '18s',
      format: '16:9 Horizontal',
      status: 'Published',
      script: {
        hook: 'Customer queries landing outside business hours?',
        problem: 'Unanswered chat tickets mean lost revenue and frustrated clients.',
        solution: 'Deploy autonomous custom-trained AI bots to handle replies 24/7.',
        benefit: 'Retain 100% customer satisfaction while bots route leads straight to CRM.',
        cta: `Build your custom bot today at ${publicBrandSettings.websiteUrl} or mail us: ${publicBrandSettings.publicEmail}`
      },
      hashtags: ['AIAgents', 'CustomerSupport', 'NexoraXtyBots', 'LeadGen'],
      views: 4505,
      updateSource: 'New app module released',
      websitePageAffected: '/workspace/chatbots',
      generatedDate: '2026-06-08 10:15',
      platformsSelected: ['YouTube', 'LinkedIn', 'X / Twitter', 'Blog', 'Newsletter'],
      failedPlatformCount: 0,
      analyticsStatus: 'views: 4505 | ctr: 4.5%',
      videoQualityScore: 95,
      selectedTemplate: 'Purple Glass Product Reveal',
      voiceoverScript: '[Distant cyber chimes] Customer queries landing after hours? Unanswered chat tickets mean lost revenue. Deploy autonomous, custom-trained AI chat agents to assist 24/7. Retain customer satisfaction and capture every single lead. Build yours today at nexoraxdigital.co.uk.',
      thumbnailTitle: 'COSMIC CHATBOTS ACTIVE',
      platformCaption: '🤖 Stop letting prospective leads drift into silence when your offices close. Deploy custom neural chatbots trained natively on your operations. Fully secured. Launch: nexoraxdigital.co.uk'
    },
    {
      id: 'p-4',
      title: 'Custom UGC Campaign Factory Showcase',
      service: 'AI Ad Factory',
      duration: '20s',
      format: '9:16 Vertical',
      status: 'Draft Generated',
      script: {
        hook: 'Struggling to feed your social channels with high-quality media?',
        problem: 'Relying on expensive production crews limits your posting velocity.',
        solution: 'Our AI Ad Factory synthesizes platform-ready UGC style clips instantly.',
        benefit: 'Maintain constant visibility on TikTok and Reels with compliant assets.',
        cta: `Contact ${publicBrandSettings.publicPhone} to initiate your UGC channel.`
      },
      hashtags: ['UGCStudio', 'SocialVelocity', 'AdAutomation', 'ViralSwarm'],
      views: 0,
      updateSource: 'Manual admin trigger',
      websitePageAffected: '/admin/ugc-studio',
      generatedDate: '2026-06-12 05:00',
      platformsSelected: ['YouTube Shorts', 'TikTok', 'Instagram Feed', 'Facebook Reels'],
      failedPlatformCount: 1,
      analyticsStatus: 'Awaiting Review',
      videoQualityScore: 91,
      selectedTemplate: 'Premium Enterprise Promo',
      voiceoverScript: '[Deep cinematic low-end drone] Need constant high-fidelity social content? Traditional crews drag down your publishing cadence. The NexoraX UGC Studio creates vertical high-conversion video assets within 0.8 seconds. Boost viewer retention and conquer the algorithm. Initiate yours today.',
      thumbnailTitle: 'VIRAL SWARM ENGINE',
      platformCaption: '🎥 Content velocity is the ultimate brand growth hack. The NexoraX UGC generator uses advanced template libraries to assemble platform-optimized social shorts on repeat. Dial 08000 622827'
    }
  ]);

  // Guide Categories list
  const guideCategories = [
    'All',
    'Getting Started',
    'Nexus Search',
    'Hosting',
    'Mail & Domains',
    'Chat',
    'Social Automation',
    'AI Automation',
    'Voice Matrix',
    'Billing',
    'Privacy'
  ];

  // Promo Categories list
  const promoCategories = [
    'All',
    'AI Automation',
    'Website Development',
    'AI Agents',
    'Nexora Chat',
    'Media Generation Studio',
    'Social Media Automation',
    'Client Campaign Workspace',
    'AI Ad Factory',
    'UGC Creator Studio',
    'Business Automation',
    'Marketplace Updates',
    'New Feature Releases'
  ];

  // Handle Play/Stop in simulated video modal
  useEffect(() => {
    let intervalId: any;
    if (isPlaying && previewingVideo) {
      intervalId = setInterval(() => {
        setPlayerProgress((prev) => {
          if (prev >= 100) {
            setIsPlaying(false);
            return 0;
          }
          return prev + 1.5;
        });
        // Jitter waveform bars
        setWaveformBars(prev => prev.map(() => Math.floor(Math.random() * 45) + 10));
      }, 100);
    } else {
      setPlayerProgress(0);
      setWaveformBars(new Array(15).fill(12));
    }
    return () => clearInterval(intervalId);
  }, [isPlaying, previewingVideo]);

  // Synchronize playing state with HTML5 Video element
  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.play().catch(err => {
          console.log("Auto-playback deferred (requires user interaction first)", err);
        });
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying, previewingVideo]);

  // Emitters to publish real-time diagnostic telemetry to Node-0 (Architect Terminal)
  const emitVideoErrorDiagnostic = (errorCode: number, errorMsg: string) => {
    if (typeof window !== 'undefined') {
      const diagnostic = {
        status: 'error',
        timestamp: new Date().toLocaleTimeString(),
        videoSrc: previewingVideo?.videoSrc || 'N/A',
        videoTitle: previewingVideo?.title || 'N/A',
        isPlaying: false,
        playerProgress: playerProgress,
        error: true,
        errorMessage: errorMsg,
        errorCode: errorCode
      };
      (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__ = diagnostic;
      window.dispatchEvent(new CustomEvent('nexorax-video-diagnostic', { detail: diagnostic }));
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Avoid overriding error state if videoRef says it's in an active error state
      const currentErrorMsg = (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__?.status === 'error' 
        ? (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__?.errorMessage 
        : null;
      const currentErrorCode = (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__?.status === 'error'
        ? (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__?.errorCode
        : null;

      let currentStatus = 'idle';
      if (currentErrorMsg) {
        currentStatus = 'error';
      } else if (isPlaying) {
        currentStatus = 'playing';
      } else if (previewingVideo) {
        currentStatus = 'paused';
      } else {
        currentStatus = 'no active video';
      }

      const diagnostic = {
        status: currentStatus,
        timestamp: new Date().toLocaleTimeString(),
        videoSrc: previewingVideo?.videoSrc || 'N/A',
        videoTitle: previewingVideo?.title || 'N/A',
        isPlaying: isPlaying,
        playerProgress: playerProgress,
        error: currentStatus === 'error',
        errorMessage: currentErrorMsg,
        errorCode: currentErrorCode
      };

      (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__ = diagnostic;
      window.dispatchEvent(new CustomEvent('nexorax-video-diagnostic', { detail: diagnostic }));
    }
  }, [isPlaying, previewingVideo, playerProgress]);

  const handleOpenPreview = (video: any, forceFormat?: '16:9' | '9:16' | '1:1') => {
    const isGuide = video.id && (video.id in GUIDE_VIDEO_ASSETS);
    if (isGuide) {
      handleSelectGuide(video);
    } else {
      setPreviewingVideo(video);
    }
    setIsPlaying(true);
    setPlayerProgress(0);
    if (forceFormat) {
      setActivePlaybackFormat(forceFormat);
    } else if (video.format) {
      if (video.format.includes('9:16')) setActivePlaybackFormat('9:16');
      else if (video.format.includes('1:1')) setActivePlaybackFormat('1:1');
      else setActivePlaybackFormat('16:9');
    } else {
      setActivePlaybackFormat('16:9');
    }
  };

  // Simulated 10 active triggers (Requested)
  const triggersList = [
    { label: 'New website service added', spec: 'NexoraX Chat Node', type: 'Service Added', page: '/services/chat-node' },
    { label: 'New website feature added', spec: 'Antigravity Multi-Agent Workflow', type: 'Feature Added', page: '/workspace/antigravity' },
    { label: 'New blog post published', spec: 'The 15-2-8 God-Mode Funnel Strategy', type: 'Blog Published', page: '/blog/god-mode-funnel' },
    { label: 'New marketplace item added', spec: 'Bare-Metal React Template V4', type: 'Marketplace Added', page: '/marketplace/react-bare-metal' },
    { label: 'New pricing plan added', spec: 'Vanguard NexoraX Tier', type: 'Pricing Changed', page: '/tiers' },
    { label: 'Homepage section updated', spec: 'Decentralized Grid Mapping UI', type: 'Homepage Updated', page: '/' },
    { label: 'New promotional offer added', spec: 'Zero-Cost Access Setup', type: 'Offer Added', page: '/offers/access-to-work' },
    { label: 'New app module released', spec: 'Daily Revenue Ticker Summarizer', type: 'Module Released', page: '/workspace/revenue-ticker' },
    { label: 'Manual admin trigger', spec: 'Manual Custom Campaign', type: 'Manual Trigger', page: '/admin/custom' },
    { label: 'Deployment webhook trigger', spec: 'Bare-Metal Container Build Webhook', type: 'Webhook Triggered', page: '/deployment/webhook' }
  ];

  const simulateTrigger = async (trig: any) => {
    if (isScanning) return;
    showToast(`Initiating Change Sync node for: [${trig.label}]`);
    setIsScanning(true);
    setScanProgress(0);
    setScanLogs([]);
    setDetectedChanges([]);

    try {
      const isPricingOrLegal = trig.page === '/tiers' || trig.label.toLowerCase().includes('pricing') || trig.label.toLowerCase().includes('legal');
      const mockSpecValue = isPricingOrLegal 
        ? "Revised fee structures and added direct NexoraX billing records direct billing alignment." 
        : `Deployed high-performance ${trig.spec} capability with absolute speed and 0.8s transactional latency.`;

      const data = await authenticatedFetch('/api/promos/scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          triggerType: trig.type,
          triggerField: trig.spec,
          triggerPage: trig.page,
          triggerSpec: mockSpecValue
        })
      });

      if (data) {
        let currentStep = 0;
        const allLogs = data.logs || [];
        
        const interval = setInterval(() => {
          if (currentStep < allLogs.length) {
            setScanLogs(prev => [...prev, allLogs[currentStep]]);
            setScanProgress(Math.floor(((currentStep + 1) / allLogs.length) * 100));
            currentStep++;
          } else {
            clearInterval(interval);
            setIsScanning(false);
            if (data.detectedChanges) setDetectedChanges(data.detectedChanges);
            if (data.watcherHistory) setWatcherHistory(data.watcherHistory);
            showToast(`✓ Sync complete: detected structural modification for ${trig.spec}.`);
          }
        }, 150);
      } else {
        setIsScanning(false);
        showToast("Error establishing connection with edge scanning node.");
      }
    } catch (err) {
      console.error(err);
      setIsScanning(false);
      showToast("Verification sync timed out.");
    }
  };

  // Run a real comparison scan check against webrtc and sitemaps index
  const startWebsiteScan = async () => {
    if (isScanning) return;
    setIsScanning(true);
    setScanProgress(0);
    setScanLogs([]);
    setDetectedChanges([]);

    try {
      const data = await authenticatedFetch('/api/promos/scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });

      if (data) {
        let currentStep = 0;
        const allLogs = data.logs || [];
        
        const interval = setInterval(() => {
          if (currentStep < allLogs.length) {
            setScanLogs(prev => [...prev, allLogs[currentStep]]);
            setScanProgress(Math.floor(((currentStep + 1) / allLogs.length) * 100));
            currentStep++;
          } else {
            clearInterval(interval);
            setIsScanning(false);
            if (data.detectedChanges) setDetectedChanges(data.detectedChanges);
            if (data.watcherHistory) setWatcherHistory(data.watcherHistory);
            showToast("✓ Sitemap and bare-metal cache successfully reconciled.");
          }
        }, 150);
      } else {
        setIsScanning(false);
        showToast("Error contacting website watcher network.");
      }
    } catch (err) {
      console.error(err);
      setIsScanning(false);
    }
  };

  // Invoke our full-stack video synthesis nodes
  const generateVideoFromChange = async (change: any, type: 'guide' | 'promo') => {
    showToast(`Initializing 0.8s Video Generator Node for ${change.field}...`);
    
    try {
      const response = await fetch('/api/promos/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          changeId: change.id,
          type: change.type,
          videoType: type
        })
      });

      const data = await response.json();
      if (response.ok && data.status === 'success') {
        if (data.newJob) {
          setPromos(prev => [data.newJob, ...prev]);
        }
        if (data.detectedChanges) {
          setDetectedChanges(data.detectedChanges);
        }
        showToast(`✓ Success: Real AI-powered 15-20s "${data.newJob?.selectedTemplate || 'Cyber City'}" Promo template generated in queue!`);
      } else {
        showToast(`⚠️ Generator Alert: ${data.message || 'Verification mismatch'}`);
      }
    } catch (err) {
      console.error("Generator block failure:", err);
      showToast("❌ Unable to invoke central Video Generation clusters.");
    }
  };

  // Update status in Approval center
  const updatePromoStatus = async (id: string, newStatus: PromoVideo['status']) => {
    const updated = await updatePromoBackend(id, { status: newStatus });
    if (updated) {
      showToast(`Campaign status verified: ${newStatus}`);
    }
  };

  // Filter video list based on search/category
  const filteredGuides = guides.filter(g => {
    if (selectedGuideCategory === 'All') return true;
    
    // Map selected filter tab to category inside guides
    const cat = selectedGuideCategory.toLowerCase();
    const gCat = g.category.toLowerCase();
    
    if (cat === 'hosting') return gCat.includes('hosting');
    if (cat === 'chat') return gCat.includes('chat');
    if (cat === 'social automation') return gCat.includes('social');
    if (cat === 'ai automation') return gCat.includes('ai automation');
    if (cat === 'billing') return gCat.includes('billing') || gCat.includes('subscription');
    if (cat === 'privacy') return gCat.includes('privacy') || gCat.includes('consent');
    if (cat === 'voice matrix') return gCat.includes('voice');
    if (cat === 'getting started') return gCat.includes('start');
    if (cat === 'nexus search') return gCat.includes('search');
    if (cat === 'mail & domains') return gCat.includes('mail') || gCat.includes('domain');
    
    return g.category === selectedGuideCategory;
  });

  const filteredPromos = promos.filter(p => {
    if (selectedPromoCategory === 'All') return true;
    return p.service === selectedPromoCategory;
  });

  return (
    <div className="w-full flex flex-col justify-start relative z-10 px-4 md:px-8 pt-8 text-white max-w-[1550px] mx-auto select-none" id="guides-and-videos-tab">
      
      {/* HEADER HERO AREA */}
      <div className="text-center md:text-left md:flex justify-between items-center bg-gradient-to-r from-purple-950/20 via-indigo-950/25 to-black/30 border border-purple-550/15 p-6 md:p-8 rounded-3xl backdrop-blur-md mb-8 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-purple-650/5 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-650/5 rounded-full blur-[100px] pointer-events-none" />
        
        <div className="space-y-3 z-10 relative">
          <div className="flex items-center justify-center md:justify-start gap-2 text-cyan-450 font-mono text-[10px] tracking-[0.25em] font-black uppercase">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <span>NEXORAX NEXORAX VIDEO MARKETING HUB</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-sans font-black uppercase tracking-tight">
            Guides & Videos
          </h1>
          <p className="text-sm text-slate-450 font-mono">
            Learn NexoraX. Watch Updates. Launch Faster.
          </p>
          <p className="text-xs text-slate-400 max-w-2xl font-sans leading-relaxed">
            Explore step-by-step guides, platform walkthroughs, promotional videos, feature explainers, and short AI-generated updates about NexoraX Digital services and tools.
          </p>
        </div>

        <div className="mt-6 md:mt-0 flex flex-wrap gap-2.5 shrink-0 z-10 relative justify-center">
          <button 
            onClick={() => {
              setActiveTab('watcher');
              startWebsiteScan();
            }}
            className="flex items-center gap-2 px-5 py-3 text-xs font-mono font-bold uppercase rounded-xl border border-cyan-500/30 bg-cyan-950/30 text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[0_0_15px_rgba(6,182,212,0.25)] transition-all cursor-pointer"
          >
            <RefreshCw className="w-4 h-4 animate-spin-slow text-[#00f0ff]" />
            Scan Website Now
          </button>
          
          <button 
            onClick={() => setActiveTab('settings')}
            className="flex items-center gap-2 px-4 py-3 text-xs font-mono font-bold uppercase rounded-xl border border-zinc-800 bg-zinc-950/30 text-zinc-400 hover:border-zinc-700 hover:text-white transition-all cursor-pointer"
          >
            <Settings className="w-4 h-4" />
            Config Control
          </button>
        </div>
      </div>

      {/* HORIZONTAL GLASS NAVIGATION BAR */}
      <div className="border border-white/5 bg-zinc-950/45 p-2 rounded-2xl flex flex-wrap gap-1 mb-8 backdrop-blur-sm z-20">
        {[
          { id: 'guides', label: 'Guide Video Library', icon: BookOpenIcon },
          { id: 'promos', label: 'Promotional Videos', icon: PlayCircleIcon },
          { id: 'watcher', label: 'Website Watcher', icon: WatcherIcon },
          { id: 'approval', label: 'Approval Center', icon: VerifyIcon },
          { id: 'factory', label: 'Guide Video Factory', icon: SparklesIcon },
          { id: 'analytics', label: 'Video Analytics', icon: AnalyticsIcon },
          { id: 'settings', label: 'Settings & Safes', icon: SettingsIcon }
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex-1 min-w-[130px] flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200 cursor-pointer ${
                isActive 
                  ? "bg-purple-950/40 text-purple-400 border border-purple-500/30 shadow-[0_0_15px_rgba(232,64,243,0.15)]" 
                  : "text-zinc-400 hover:text-zinc-200 hover:bg-white/5 border border-transparent"
              }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? "text-purple-400" : "text-zinc-500"}`} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* CORE SECTOR DETAILS BASED ON ACTIVE TAB */}
      <div className="w-full space-y-8 mb-16">
        
        {/* PRIMARY WORKSPACE PANEL */}
        <div className="w-full space-y-6">
          <AnimatePresence mode="wait">
            
            {/* TAB 1: GUIDE VIDEO LIBRARY */}
            {activeTab === 'guides' && (
              <motion.div
                key="guides-panel"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-6"
              >
                {/* TWO-COLUMN LAYOUT */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  
                  {/* LEFT SIDE: FILTERS, CARDS, WRITTEN DETAIL */}
                  <div className="lg:col-span-7 xl:col-span-8 space-y-6">
                    
                    {/* Visual Category Pill Badges */}
                    <div className="bg-[#040915]/50 border border-white/5 rounded-2xl p-4 flex flex-row overflow-x-auto whitespace-nowrap scrollbar-none gap-2 pb-2.5 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap md:overflow-visible" id="guides-categories-scroll">
                      {guideCategories.map(cat => (
                        <button
                          key={cat}
                          onClick={() => handleCategoryChange(cat)}
                          className={`px-3 py-1.5 rounded-lg text-[10px] font-mono font-semibold tracking-wider uppercase border transition-all cursor-pointer inline-block shrink-0 ${
                            selectedGuideCategory === cat
                              ? "bg-cyan-950 text-cyan-400 border-cyan-500/30 shadow-[0_0_10px_rgba(0,195,255,0.1)]"
                              : "bg-black/20 text-zinc-400 border-transparent hover:border-zinc-800"
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>

                    {/* Admin-only status messages & developer audit panel */}
                    {(() => {
                      if (!isAdminOrPreview()) return null;
                      
                      return (
                        <div className="bg-[#030614]/75 border border-amber-500/20 rounded-2xl p-4 space-y-3 shadow-[0_0_15px_rgba(245,158,11,0.05)]">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
                            <div>
                              <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Admin video generation controls
                              </h4>
                              <p className="text-[10px] text-zinc-400 font-sans mt-0.5 font-medium">
                                verify guide file uniqueness and manage automated multi-scene rendering states.
                              </p>
                            </div>
                            
                            <button
                              id="btn-generate-all-guides"
                              onClick={() => {
                                setGuides(prev => {
                                  const updatedList = prev.map(g => {
                                    const template = NEXORAX_SCENE_TEMPLATES[g.id];
                                    if (!template) return g;
                                    return {
                                      ...g,
                                      videoMode: 'generated-scenes' as const,
                                      videoIsUnique: true, // Mark unique generated walkthrough session active!
                                      videoDuplicateDetected: false, // Override duplicate!
                                      generatedScenes: template,
                                      transcript: `[SYSTEM: Generated Video Active] ${template.introLine} ` + template.scenes.map((s, idx) => `Step ${idx+1}: ${s.voiceover}`).join(' '),
                                      videoStatus: 'ready' as const
                                    };
                                  });
                                  if (selectedGuide) {
                                    const found = updatedList.find(gx => gx.id === selectedGuide.id);
                                    if (found) setSelectedGuide(found);
                                  }
                                  return updatedList;
                                });
                                showToast("Successfully generated interactive scene walkthroughs for all 10 guides.");
                              }}
                              className="px-3.5 py-1.5 rounded-lg text-[9.5px] font-mono font-bold uppercase bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 transition-all cursor-pointer shadow-sm flex items-center gap-1.5 shrink-0 self-start sm:self-auto font-semibold"
                            >
                              <Sparkles className="w-3 h-3 text-amber-400 shrink-0" />
                              Generate All Guide Videos
                            </button>
                          </div>

                          {guides.some(g => g.videoDuplicateDetected) && (
                            <div className="p-2.5 bg-amber-950/25 border border-amber-500/20 rounded-lg text-[9.5px] leading-relaxed text-amber-300 font-mono">
                              <span className="font-bold flex items-center gap-1">
                                ⚠️ Duplicate placeholder video detected:
                              </span>
                              <span className="block mt-0.5 text-[9px] text-zinc-400 font-sans">
                                Guides with duplicate MP4 files have been automatically switched to the dynamic NexoraX Interactive Scene Player experience to ensure unique content.
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Grid of Guide Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-sans max-h-[480px] overflow-y-auto pr-1 custom-scrollbar">
                      {filteredGuides.map(guide => {
                        const isSelected = selectedGuide?.id === guide.id;
                        return (
                          <div 
                            key={guide.id}
                            id={`guide-card-${guide.id}`}
                            onClick={() => handleSelectGuide(guide)}
                            className={`group relative border rounded-2xl p-4 transition-all duration-300 shadow-lg overflow-hidden flex flex-col justify-between min-h-[180px] cursor-pointer ${
                              isSelected 
                                ? "bg-[#110c2c]/80 border-purple-500/60 ring-1 ring-purple-500/20" 
                                : "bg-[#04081c]/70 border-purple-950/40 hover:border-purple-650/40"
                            }`}
                          >
                            <div className="absolute top-0 right-0 w-24 h-24 bg-purple-650/5 rounded-full blur-2xl group-hover:bg-purple-650/10 transition-all pointer-events-none" />
                            
                            <div className="space-y-2">
                              <div className="flex justify-between items-start gap-2">
                                <span className="text-[9px] font-mono uppercase bg-[#1a0f30]/65 border border-purple-550/20 px-2 py-0.5 rounded text-purple-400 font-bold">
                                  {guide.category}
                                </span>
                                <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${
                                  guide.difficulty === 'Easy' ? 'text-emerald-450 border-emerald-500/20 bg-emerald-950/20' :
                                  guide.difficulty === 'Medium' ? 'text-amber-450 border-amber-500/20 bg-amber-950/20' :
                                  'text-red-450 border-red-500/20 bg-red-950/20'
                                }`}>
                                  {guide.difficulty}
                                </span>
                              </div>

                              <div className="flex gap-2.5 items-center">
                                <div className="w-8 h-8 rounded-lg bg-purple-900/20 border border-purple-500/25 flex items-center justify-center text-sm shrink-0">
                                  {guide.thumbnail || '🤖'}
                                </div>
                                <div className="min-w-0">
                                  <h3 className="text-xs font-sans font-bold text-white leading-snug truncate group-hover:text-purple-300 transition-colors">
                                    {guide.title}
                                  </h3>
                                  <span className="text-[8px] font-mono text-slate-500 block uppercase mt-0.5">
                                    Duration: {guide.duration} • Updated {guide.updatedAt}
                                  </span>
                                </div>
                              </div>

                              <p className="text-[11px] text-slate-400 line-clamp-2 font-sans leading-relaxed">
                                {guide.description}
                              </p>
                            </div>

                            <div className="flex gap-2 mt-3 pt-3 border-t border-white/5 font-mono">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectGuide(guide);
                                }}
                                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-cyan-600/10 hover:bg-cyan-600/25 border border-cyan-500/20 text-cyan-300 text-[10px] font-bold uppercase transition-all cursor-pointer"
                              >
                                <BookOpen className="w-3 h-3 text-cyan-400" />
                                View Guide
                              </button>
                              
                              {guide.videoStatus === 'ready' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectGuide(guide);
                                    setTimeout(() => {
                                      if (guideVideoRef.current) {
                                        guideVideoRef.current.scrollIntoView({ behavior: 'smooth' });
                                        guideVideoRef.current.play().catch(() => {});
                                      }
                                    }, 150);
                                  }}
                                  className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-purple-600/20 hover:bg-purple-600/35 border border-purple-500/30 text-purple-300 text-[10px] font-bold uppercase transition-all cursor-pointer"
                                >
                                  <Play className="w-3 h-3 text-purple-400" />
                                  Watch Video
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* WRITTEN GUIDE DETAIL PANEL (IF SELECTED) */}
                    <AnimatePresence mode="wait">
                      {selectedGuide && (selectedGuide.id?.startsWith('g-') || selectedGuide.id?.startsWith('guide-') || selectedGuide.id in GUIDE_VIDEO_ASSETS) && (
                        <motion.div
                          key={`guide-detail-${selectedGuide.id}`}
                          initial={{ opacity: 0, y: 15 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 15 }}
                          className="bg-[#04081c]/90 border border-purple-550/20 rounded-3xl p-5 shadow-2xl relative overflow-hidden backdrop-blur-md"
                        >
                          <div className="absolute top-0 left-0 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl pointer-events-none" />
                          
                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-white/5 pb-4 mb-4">
                            <div>
                              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                <span className="text-[9px] font-mono uppercase bg-[#1a0f30]/65 border border-purple-555/20 px-2 py-0.5 rounded text-purple-400 font-bold">
                                  {selectedGuide.category}
                                </span>
                                <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded border ${
                                  selectedGuide.difficulty === 'Easy' ? 'text-emerald-450 border-emerald-500/20 bg-emerald-950/20' :
                                  selectedGuide.difficulty === 'Medium' ? 'text-amber-450 border-amber-500/20 bg-amber-950/20' :
                                  'text-red-450 border-red-500/20 bg-red-950/20'
                                }`}>
                                  {selectedGuide.difficulty}
                                </span>
                              </div>
                              <h2 className="text-lg md:text-xl font-sans font-black uppercase text-white tracking-tight">
                                {selectedGuide.title}
                              </h2>
                              <p className="text-[10px] text-zinc-400 mt-1 font-mono">
                                Duration: {selectedGuide.duration} • Updated {selectedGuide.updatedAt}
                              </p>
                            </div>
                            <button
                              onClick={() => setSelectedGuide(null)}
                              className="px-2.5 py-1 bg-white/5 hover:bg-white/10 rounded-lg text-[9px] font-mono text-zinc-400 hover:text-white transition-all uppercase text-center"
                            >
                              [ CLOSE WRITTEN GUIDE ]
                            </button>
                          </div>

                          {/* Written Steps Checklist & Transcript tabs */}
                          <div className="bg-[#030615]/85 border border-white/5 p-4 rounded-2xl space-y-4">
                            <div className="flex border-b border-white/5 text-[10px] font-mono">
                              <button 
                                onClick={() => setActiveGuideDetailTab('steps')}
                                className={`flex-1 pb-2 text-center font-bold uppercase transition-all border-b-2 ${
                                  activeGuideDetailTab === 'steps' 
                                    ? 'border-cyan-400 text-cyan-400 font-extrabold' 
                                    : 'border-transparent text-zinc-500 hover:text-zinc-400'
                                }`}
                              >
                                Step-By-Step Checklist
                              </button>
                              <button 
                                onClick={() => setActiveGuideDetailTab('transcript')}
                                className={`flex-1 pb-2 text-center font-bold uppercase transition-all border-b-2 ${
                                  activeGuideDetailTab === 'transcript' 
                                    ? 'border-purple-400 text-purple-400 font-extrabold' 
                                    : 'border-transparent text-zinc-500 hover:text-zinc-400'
                                }`}
                              >
                                Walkthrough Transcript / Script
                              </button>
                            </div>

                            {activeGuideDetailTab === 'steps' ? (
                              <div className="space-y-2">
                                {selectedGuide.steps?.map((step: string, sIdx: number) => {
                                  const isDone = !!completedSteps[selectedGuide.id]?.[sIdx];
                                  return (
                                    <div 
                                      key={sIdx}
                                      onClick={() => {
                                        setCompletedSteps(prev => {
                                          const gSteps = prev[selectedGuide.id] || {};
                                          return {
                                            ...prev,
                                            [selectedGuide.id]: {
                                              ...gSteps,
                                              [sIdx]: !gSteps[sIdx]
                                            }
                                          };
                                        });
                                      }}
                                      className={`flex items-start gap-2.5 p-2.5 bg-black/45 rounded-xl border border-white/5 hover:bg-black/65 transition-all cursor-pointer text-[11px] ${
                                        isDone ? 'opacity-60' : ''
                                      }`}
                                    >
                                      <div className={`w-4 h-4 rounded flex items-center justify-center border shrink-0 transition-all ${
                                        isDone 
                                          ? 'bg-emerald-950 border-emerald-500 text-emerald-400' 
                                          : 'border-zinc-850 text-zinc-600 hover:border-zinc-700'
                                      }`}>
                                        {isDone && <Check className="w-2.5 h-2.5" />}
                                      </div>
                                      <span className={`leading-relaxed ${isDone ? 'line-through text-zinc-500' : 'text-slate-200'}`}>
                                        {step}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="bg-black/50 p-3.5 rounded-xl border border-white/5 text-[11px] text-slate-350 leading-relaxed italic pr-1 whitespace-pre-wrap">
                                "{selectedGuide.transcript || 'No transcript currently registered for this guide.'}"
                              </div>
                            )}

                            {/* CTA block in Left side written detail */}
                            {selectedGuide.ctaLabel && (
                              <div className="flex justify-end pt-2">
                                <button 
                                  onClick={() => {
                                    const mapCtaToTab = (ctaTarget: string): string | null => {
                                      if (!ctaTarget) return null;
                                      const matches = ctaTarget.toLowerCase();
                                      if (matches.includes('search')) return 'NexoraX Multiverse Search Engine';
                                      if (matches.includes('hosting')) return 'NexoraX Backup Storage Blocks';
                                      if (matches.includes('mail') || matches.includes('domain')) return 'Mail & Domains Platform';
                                      if (matches.includes('chat')) return 'NexoraX AI Agent Automation';
                                      if (matches.includes('social')) return 'Social Media Automation';
                                      if (matches.includes('ai-automation')) return 'NexoraX AI Agent Automation';
                                      if (matches.includes('voice')) return 'NexoraX Voice';
                                      if (matches.includes('workspace') || matches.includes('tasks')) return 'NexoraX Workspace Suite';
                                      if (matches.includes('media') || matches.includes('studio')) return 'Nexorax Studio';
                                      if (matches.includes('billing') || matches.includes('tiers') || matches.includes('payment')) return 'Tiers';
                                      if (matches.includes('contact') || matches.includes('book')) return 'Contact';
                                      if (matches.includes('ecosystem')) return 'Ecosystem';
                                      return null;
                                    };
                                    const targetTab = mapCtaToTab(selectedGuide.ctaTarget || selectedGuide.ctaLabel);
                                    if (targetTab) {
                                      if (onNavigateLandingTab) {
                                        onNavigateLandingTab(targetTab);
                                      } else {
                                        showToast(`Launching target platform: ${targetTab}`);
                                      }
                                    } else {
                                      showToast(`Executing CTA action: ${selectedGuide.ctaLabel}`);
                                    }
                                  }}
                                  className="flex items-center gap-1.5 py-2 px-5 rounded-xl bg-gradient-to-r from-cyan-650 to-indigo-650 hover:from-cyan-550 hover:to-indigo-550 border border-cyan-550/30 text-white font-mono font-bold text-[10px] uppercase tracking-wide cursor-pointer transition-all"
                                >
                                  <span>{selectedGuide.ctaLabel}</span>
                                  <ArrowRight className="w-3 h-3 text-white" />
                                </button>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* RIGHT SIDE: NEXORAX VIDEO MONITOR & PLAYER */}
                  <div className="lg:col-span-5 xl:col-span-4 space-y-6 lg:sticky lg:top-8 order-2">
                    <NexoraXVideoMonitor 
                      guide={selectedGuide} 
                      onNavigateLandingTab={onNavigateLandingTab}
                      onViewTranscript={() => {
                        setActiveGuideDetailTab('transcript');
                        showToast("Switched left detail panel to transcript view");
                      }}
                      showToast={showToast}
                      onSelectGuideId={(id) => {
                        const found = guides.find(g => g.id === id);
                        if (found) {
                          handleSelectGuide(found);
                        }
                      }}
                      onUpdateGuide={(updated) => {
                        setGuides(prev => prev.map(g => g.id === updated.id ? updated : g));
                        setSelectedGuide(updated);
                      }}
                    />
                  </div>

                </div>
              </motion.div>
            )}

            {/* TAB 2: PROMOTIONAL VIDEOS QUEUE & AUTO-DISTRIBUTION MATRIX */}
            {activeTab === 'promos' && (
              <motion.div
                key="promos-panel"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-6"
              >
                {/* Category filters header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="bg-[#040915]/50 border border-white/5 rounded-2xl p-4 flex flex-wrap gap-1.5 leading-normal flex-1">
                    {promoCategories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => setSelectedPromoCategory(cat)}
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-mono font-semibold tracking-wider uppercase border transition-all cursor-pointer ${
                          selectedPromoCategory === cat
                            ? "bg-purple-950 text-purple-400 border-purple-500/30 shadow-[0_0_10px_rgba(232,64,243,0.1)]"
                            : "bg-black/20 text-zinc-400 border-transparent hover:border-zinc-800"
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>

                  {/* Manual video trigger */}
                  <button
                    onClick={() => {
                      showToast("Initializing Manual Promo Script Synthesizer...");
                      generateVideoFromChange({
                        id: `man-${Date.now()}`,
                        type: 'Manual Admin Campaign',
                        page: '/admin-custom',
                        field: 'Custom Enterprise Suite Launch',
                        newValue: 'Decentralized Bare-Metal Grid architecture scale up.',
                        impact: 'Low-risk profile. Ready for queue dispatch.',
                        titlePattern: 'Enterprise NexoraX Promo',
                        servicePattern: 'Manual Campaign',
                        durationPattern: '20s',
                        hashtagsPattern: ['NexoraX', 'NexoraXEnterprise', 'NexoraXAI'],
                        scriptPattern: {
                          hook: "Is your legacy cloud dragging performance downwards?",
                          problem: "Expensive middleman billing layers stall system speeds.",
                          solution: "Deploy direct-billing bare metal nodes.",
                          benefit: "Instant 0.8s standard operational latency limits.",
                          cta: `Connect with us today at ${publicBrandSettings.websiteUrl || 'support@nexorax.co.uk'}`
                        }
                      }, 'promo');
                    }}
                    className="px-4 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-mono font-bold text-xs uppercase cursor-pointer transition-all self-stretch md:self-auto text-center flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(232,64,243,0.2)]"
                  >
                    <Plus className="w-4 h-4" /> Synthesize Campaign
                  </button>
                </div>

                {/* Queue Lists of Active Promotion Pipeline */}
                <div className="space-y-6">
                  {filteredPromos.length === 0 ? (
                    <div className="bg-[#04081c]/40 border border-white/5 p-12 text-center rounded-3xl">
                      <Radio className="w-10 h-10 text-zinc-650 mx-auto animate-pulse mb-3" />
                      <p className="text-sm font-semibold text-zinc-400">No promotional campaign queue items detected in this slice.</p>
                      <button 
                        onClick={() => setSelectedPromoCategory('All Promos')}
                        className="text-xs text-[#00f0ff] underline mt-1 font-mono uppercase block mx-auto cursor-pointer"
                      >
                        Clear Filters
                      </button>
                    </div>
                  ) : (
                    filteredPromos.map(promo => {
                      // Calculate failed platform status based on real connector state
                      const selectedPlatforms = promo.platformsSelected || ['YouTube Shorts', 'Instagram Reels', 'LinkedIn'];
                      
                      const failedChannelCount = selectedPlatforms.filter(platName => {
                        const match = connectors.find(c => c.name.toLowerCase() === platName.toLowerCase() || c.id.toLowerCase() === platName.toLowerCase().replace(/\s+/g, ''));
                        return match ? match.status !== 'Ready to publish' : true;
                      }).length;

                      const isPricingOrLegal = promo.websitePageAffected === '/tiers' || promo.title.toLowerCase().includes('pricing') || promo.title.toLowerCase().includes('legal');

                      return (
                        <div 
                          key={promo.id}
                          className="relative bg-[#04081c]/70 border border-purple-950/40 rounded-3xl p-6 hover:border-purple-650/30 transition-all duration-300 shadow-xl overflow-hidden"
                        >
                          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />

                          {/* Header row metadata */}
                          <div className="flex flex-wrap justify-between items-start gap-3 border-b border-white/5 pb-4 mb-4">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[9px] font-mono uppercase bg-[#142930] border border-cyan-500/25 px-2 py-0.5 rounded text-cyan-400 font-bold">
                                  {promo.service || 'Promo Video'}
                                </span>
                                <span className="text-[9px] font-mono text-zinc-500 uppercase">
                                  Queue ID: {promo.id}
                                </span>
                              </div>
                              <h3 className="text-base font-sans font-extrabold text-white leading-snug group-hover:text-purple-300 transition-colors">
                                {promo.title}
                              </h3>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5 text-[10px] font-mono text-slate-400">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3.5 h-3.5 text-zinc-550" /> Duration: {promo.duration || '15-20s'}
                                </span>
                                <span>• Page: <strong className="text-zinc-300">{promo.websitePageAffected || '/'}</strong></span>
                                <span>• Source: <strong className="text-purple-400">{promo.updateSource || 'System Watcher'}</strong></span>
                              </div>
                            </div>

                            {/* Status and Quality Score badge */}
                            <div className="flex items-center gap-3.5">
                              <div className="text-right">
                                <span className="text-[8px] font-mono text-zinc-500 uppercase block">Quality Score</span>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <div className="w-12 bg-zinc-800 rounded-full h-1 border border-white/5 overflow-hidden">
                                    <div 
                                      className={`h-full ${
                                        (promo.videoQualityScore || 90) >= 95 ? 'bg-emerald-400' : 'bg-cyan-400'
                                      }`} 
                                      style={{ width: `${promo.videoQualityScore || 90}%` }} 
                                    />
                                  </div>
                                  <strong className="text-xs font-mono text-emerald-400 font-bold">{promo.videoQualityScore || 90}%</strong>
                                </div>
                              </div>

                              <span className={`text-[10px] font-mono uppercase px-2.5 py-1 rounded-lg border font-bold tracking-widest ${
                                promo.status === 'Published' ? 'bg-emerald-950/45 text-emerald-400 border-emerald-500/35' :
                                promo.status === 'Approved' ? 'bg-cyan-950/45 text-cyan-400 border-cyan-500/35 animate-pulse' :
                                promo.status === 'Scheduled' ? 'bg-amber-950/45 text-amber-450 border-amber-500/35' :
                                'bg-purple-950/45 text-purple-400 border-purple-500/35'
                              }`}>
                                {promo.status}
                              </span>
                            </div>
                          </div>

                          {/* Body Script & Dynamic platforms selections */}
                          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                            
                            {/* Script columns */}
                            <div className="lg:col-span-7 space-y-3">
                              <div className="bg-black/30 border border-white/5 p-4 rounded-2xl relative">
                                <strong className="text-[10px] font-mono uppercase text-[#00f0ff] tracking-widest block mb-2">
                                  Generated Social Script (15-20s structure)
                                </strong>
                                
                                <div className="space-y-2 text-xs text-slate-300 font-sans leading-relaxed">
                                  <p><strong className="text-[9px] font-mono uppercase text-zinc-550 mr-1.5">[0-3s HOOK]:</strong>"{promo.script.hook}"</p>
                                  <p><strong className="text-[9px] font-mono uppercase text-zinc-550 mr-1.5">[3-8s PROBLEM]:</strong>"{promo.script.problem || 'Standard manual inefficiencies bottleneck scalability.'}"</p>
                                  <p><strong className="text-[9px] font-mono uppercase text-zinc-550 mr-1.5">[8-14s SOLUTION]:</strong>"{promo.script.solution}"</p>
                                  <p><strong className="text-[9px] font-mono uppercase text-zinc-550 mr-1.5">[14-18s BENEFIT]:</strong>"{promo.script.benefit || 'Achieve enterprise-grade performance targets.'}"</p>
                                  <p className="border-t border-white/5 pt-2 mt-2"><strong className="text-[9px] font-mono uppercase text-emerald-400 mr-1.5">[18-20s CTA]:</strong><strong className="text-white font-semibold">"{promo.script.cta || `Check out ${publicBrandSettings.websiteUrl} directly.`}"</strong></p>
                                </div>
                              </div>

                              {/* Target hashtags display */}
                              <div className="flex flex-wrap gap-1">
                                {(promo.hashtags || ['Nexorax', 'NexoraXAI']).map((hash, i) => (
                                  <span key={i} className="text-[10px] font-mono text-purple-400">
                                    #{hash}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Dynamic interactive platforms selection queue status */}
                            <div className="lg:col-span-5 bg-black/45 border border-white/5 p-4 rounded-2xl space-y-3 text-left">
                              <div className="flex justify-between items-center border-b border-white/5 pb-1.5">
                                <strong className="text-[10px] font-mono uppercase text-slate-400 tracking-wider">
                                  Connected Platforms Selection
                                </strong>
                                <span className="text-[8px] font-mono text-zinc-500 uppercase">Click to toggle</span>
                              </div>

                              <div className="grid grid-cols-2 xs:grid-cols-3 gap-1.5">
                                {['YouTube Shorts', 'Instagram Reels', 'TikTok', 'LinkedIn', 'X / Twitter', 'Blog', 'Newsletter'].map((platName) => {
                                  const isSelected = selectedPlatforms.includes(platName);
                                  
                                  // Locate platform oauth status
                                  const matchConn = connectors.find(c => c.name.toLowerCase() === platName.toLowerCase() || c.id.toLowerCase() === platName.toLowerCase().replace(/\s+/g, ''));
                                  
                                  const isConnected = matchConn ? matchConn.status === 'Ready to publish' : false;

                                  return (
                                    <button
                                      key={platName}
                                      onClick={() => {
                                        let updatedList;
                                        if (isSelected) {
                                          updatedList = selectedPlatforms.filter(p => p !== platName);
                                        } else {
                                          updatedList = [...selectedPlatforms, platName];
                                        }
                                        updatePromoBackend(promo.id, { platformsSelected: updatedList });
                                        showToast(`Platform configuration updated for ${promo.title}`);
                                      }}
                                      className={`p-2 rounded-lg border text-left text-[10px] font-mono transition-all cursor-pointer ${
                                        isSelected 
                                          ? "bg-purple-950/40 border-purple-500/40 text-purple-200" 
                                          : "bg-black/20 border-white/5 text-zinc-550"
                                      }`}
                                    >
                                      <div className="flex justify-between items-center">
                                        <span className="font-sans font-bold line-clamp-1">{platName}</span>
                                        <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-emerald-400' : 'bg-red-400'}`} />
                                      </div>
                                      <span className="text-[8px] block mt-0.5 text-zinc-500 font-mono scale-90 origin-left">
                                        {isConnected ? 'Connected' : 'Connection Required'}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>

                              {/* Validation health details blocks */}
                              <div className="flex justify-between items-center text-[10px] font-mono pt-1 text-slate-400">
                                <span>Failed/Inactive Nodes:</span>
                                <strong className={`font-black uppercase ${failedChannelCount > 0 ? 'text-amber-450' : 'text-emerald-400'}`}>
                                  {failedChannelCount} Warnings
                                </strong>
                              </div>

                              {/* Alert state if platform missing */}
                              {failedChannelCount > 0 && (
                                <div className="text-[9px] font-mono text-amber-500 italic leading-snug bg-amber-950/20 p-2 rounded-lg border border-amber-500/10">
                                  ⚠️ Note: {failedChannelCount} format(s) will bypass live API dispatch and sit as draft files in workspace because "Connection Required" status is flagged on your connectors database.
                                </div>
                              )}
                            </div>
                          </div>

                          {/* INLINE EDIT SCRIPT DRAWER */}
                          {editingScriptId === promo.id && (
                            <motion.div 
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              className="mt-4 bg-zinc-950/70 border border-purple-500/20 p-4 rounded-2xl space-y-3.5 text-xs text-left"
                            >
                              <div className="border-b border-white/5 pb-1.5 flex justify-between items-center">
                                <strong className="text-[10px] font-mono text-[#e840f3] uppercase tracking-wider">
                                  Fine-Tune Script Matrix (Lead Director Override)
                                </strong>
                                <button 
                                  onClick={() => setEditingScriptId(null)}
                                  className="text-[9px] font-mono text-zinc-400 hover:text-white uppercase font-bold"
                                >
                                  Close Editor
                                </button>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 font-mono">
                                <div className="space-y-1">
                                  <label className="text-[9px] text-zinc-500 uppercase">Hook line [0-3s]</label>
                                  <input 
                                    type="text" 
                                    value={editingScriptValues.hook}
                                    onChange={(e) => setEditingScriptValues(prev => ({ ...prev, hook: e.target.value }))}
                                    className="w-full bg-black border border-white/5 rounded-xl px-3 py-2 text-white font-sans text-xs focus:outline-none"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[9px] text-zinc-500 uppercase">Problem line [3-8s]</label>
                                  <input 
                                    type="text" 
                                    value={editingScriptValues.problem}
                                    onChange={(e) => setEditingScriptValues(prev => ({ ...prev, problem: e.target.value }))}
                                    className="w-full bg-black border border-white/5 rounded-xl px-3 py-2 text-white font-sans text-xs focus:outline-none"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[9px] text-zinc-500 uppercase">Solution line [8-14s]</label>
                                  <input 
                                    type="text" 
                                    value={editingScriptValues.solution}
                                    onChange={(e) => setEditingScriptValues(prev => ({ ...prev, solution: e.target.value }))}
                                    className="w-full bg-black border border-white/5 rounded-xl px-3 py-2 text-white font-sans text-xs focus:outline-none"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[9px] text-zinc-500 uppercase">Benefit line [14-18s]</label>
                                  <input 
                                    type="text" 
                                    value={editingScriptValues.benefit}
                                    onChange={(e) => setEditingScriptValues(prev => ({ ...prev, benefit: e.target.value }))}
                                    className="w-full bg-black border border-white/5 rounded-xl px-3 py-2 text-white font-sans text-xs focus:outline-none"
                                  />
                                </div>
                                <div className="space-y-1 md:col-span-2">
                                  <label className="text-[9px] text-zinc-500 uppercase">CTA line [18-20s] (Includes contacts)</label>
                                  <input 
                                    type="text" 
                                    value={editingScriptValues.cta}
                                    onChange={(e) => setEditingScriptValues(prev => ({ ...prev, cta: e.target.value }))}
                                    className="w-full bg-black border border-white/5 rounded-xl px-3 py-2 text-white font-sans text-xs focus:outline-none"
                                  />
                                </div>
                              </div>

                              <div className="flex justify-end gap-2 pt-1">
                                <button 
                                  onClick={() => setEditingScriptId(null)}
                                  className="px-3 py-1.5 rounded-lg bg-zinc-900 font-mono text-[9px] uppercase cursor-pointer"
                                >
                                  Cancel
                                </button>
                                <button 
                                  onClick={async () => {
                                    await updatePromoBackend(promo.id, { script: { ...promo.script, ...editingScriptValues } });
                                    setEditingScriptId(null);
                                    showToast("NexoraX Script Matrix updated and cached.");
                                  }}
                                  className="px-4 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-mono text-[9px] uppercase font-bold cursor-pointer transition-all"
                                >
                                  Apply Overrides
                                </button>
                              </div>
                            </motion.div>
                          )}

                          {/* INLINE SCHEDULER DRAWER */}
                          {activeSchedulingId === promo.id && (
                            <motion.div 
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              className="mt-4 bg-zinc-950/70 border border-cyan-500/20 p-4 rounded-2xl space-y-3 text-xs text-left"
                            >
                              <div className="border-b border-white/5 pb-1.5 flex justify-between items-center">
                                <strong className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider">
                                  Campaign Scheduler Setup
                                </strong>
                                <button 
                                  onClick={() => setActiveSchedulingId(null)}
                                  className="text-[9px] font-mono text-zinc-400 hover:text-white uppercase font-bold"
                                >
                                  Close
                                </button>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 font-mono">
                                <div className="space-y-1">
                                  <label className="text-[9px] text-zinc-500 uppercase">Publish Date</label>
                                  <input 
                                    type="date" 
                                    defaultValue="2026-06-20"
                                    className="w-full bg-black border border-white/5 rounded-xl px-3 py-2 text-white font-sans text-xs focus:outline-none"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[9px] text-zinc-500 uppercase">Target Peak Time</label>
                                  <select className="w-full bg-black border border-white/5 rounded-xl px-3 py-2 text-slate-300 font-mono text-xs focus:outline-none">
                                    <option>09:00 (Morning Traffic)</option>
                                    <option>12:00 (Lunch Rush)</option>
                                    <option>18:00 (NexoraX Peak Hour)</option>
                                    <option>21:00 (Late Night Grind)</option>
                                  </select>
                                </div>
                                <div className="space-y-1">
                                  <label className="text-[9px] text-zinc-500 uppercase">Stagger Interval</label>
                                  <select className="w-full bg-black border border-white/5 rounded-xl px-3 py-2 text-slate-300 font-mono text-xs focus:outline-none">
                                    <option>Instant (No delays)</option>
                                    <option>15 Minute gaps</option>
                                    <option>1 Hour gaps</option>
                                    <option>1 Day stagger limit</option>
                                  </select>
                                </div>
                              </div>

                              <div className="flex justify-end gap-2 pt-1 font-mono">
                                <button 
                                  onClick={async () => {
                                    await updatePromoBackend(promo.id, { status: 'Draft Generated' });
                                    setActiveSchedulingId(null);
                                    showToast("Campaign schedule deleted.");
                                  }}
                                  className="px-3 py-1.5 rounded-lg bg-red-950/20 text-red-400 border border-red-500/20 text-[9px] uppercase cursor-pointer"
                                >
                                  Revert Date
                                </button>
                                <button 
                                  onClick={async () => {
                                    await updatePromoBackend(promo.id, { status: 'Scheduled' });
                                    setActiveSchedulingId(null);
                                    showToast(`Successfully scheduled content campaign targeting: Peak times.`);
                                  }}
                                  className="px-4 py-1.5 rounded-lg bg-cyan-950 text-cyan-400 border border-cyan-500/20 text-[9px] uppercase font-bold cursor-pointer transition-all"
                                >
                                  Confirm Schedule
                                </button>
                              </div>
                            </motion.div>
                          )}

                          {/* Bottom controls action grid */}
                          <div className="flex flex-wrap gap-2.5 justify-end mt-5 pt-4 border-t border-white/5">
                            <button
                              onClick={() => handleOpenPreview(promo, '9:16')}
                              className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono font-bold bg-[#e840f3]/15 hover:bg-[#e840f3]/25 border border-[#e840f3]/35 text-white rounded-xl transition-all cursor-pointer"
                            >
                              <Play className="w-3.5 h-3.5" /> Preview Format
                            </button>

                            <button 
                              onClick={() => {
                                setEditingScriptValues({
                                  hook: promo.script.hook,
                                  problem: promo.script.problem || 'Standard manual inefficiencies bottleneck scalability.',
                                  solution: promo.script.solution,
                                  benefit: promo.script.benefit || 'Achieve enterprise-grade performance targets.',
                                  cta: promo.script.cta || `Check out ${publicBrandSettings.websiteUrl} directly.`
                                });
                                setEditingScriptId(editingScriptId === promo.id ? null : promo.id);
                              }}
                              className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono text-zinc-400 border border-zinc-800 rounded-xl hover:text-white hover:border-zinc-700 transition-all cursor-pointer"
                            >
                              <FileText className="w-3.5 h-3.5" /> Edit Script
                            </button>

                            <button 
                              onClick={() => {
                                setActiveSchedulingId(activeSchedulingId === promo.id ? null : promo.id);
                              }}
                              className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono text-zinc-400 border border-zinc-800 rounded-xl hover:text-white hover:border-zinc-700 transition-all cursor-pointer"
                            >
                              <Calendar className="w-3.5 h-3.5" /> Schedule
                            </button>

                            <button
                              onClick={async () => {
                                showToast(`Overwriting and regenerating visual assets model logs for ${promo.title}...`);
                                try {
                                  const res = await fetch(`/api/promos/regenerate/${promo.id}`, { method: 'POST' });
                                  if (res.ok) {
                                    const data = await res.json();
                                    if (data.status === 'success' && data.promo) {
                                      setPromos(prev => prev.map(p => p.id === promo.id ? data.promo : p));
                                      showToast(`✓ Success: Regenerated voiceovers and scripts for "${promo.title}".`);
                                    }
                                  }
                                } catch (err) {
                                  console.error("Regenerate action error:", err);
                                }
                              }}
                              className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono text-zinc-400 border border-zinc-800 rounded-xl hover:text-white hover:border-zinc-705 transition-all cursor-pointer"
                            >
                              <RefreshCw className="w-3.5 h-3.5" /> Regenerate
                            </button>

                            {promo.status !== 'Approved' && (
                              <button 
                                onClick={async () => {
                                  await updatePromoStatus(promo.id, 'Approved');
                                }}
                                className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-mono font-bold bg-emerald-950 text-emerald-400 border border-emerald-500/40 rounded-xl hover:bg-emerald-900 transition-all cursor-pointer"
                              >
                                <Check className="w-3.5 h-3.5" /> Approve Asset
                              </button>
                            )}

                            <button 
                              onClick={async () => {
                                // STRICT VALIDATION LAWS:
                                // CHECK CONTACT DETAILS:
                                if (!publicBrandSettings.websiteUrl || !publicBrandSettings.publicEmail || !publicBrandSettings.publicPhone || !publicBrandSettings.bookingLink || !publicBrandSettings.supportLink) {
                                  showToast("⚠️ Action Blocked: Public Contact Details Required! Please configure your settings tab.");
                                  return;
                                }

                                // If pricing / legal campaign trigger, warn:
                                if (isPricingOrLegal) {
                                  showToast("🛡️ ALERT: pricing / legal change triggers STRICTLY require manual confirmation by Director Lead Director!");
                                }

                                try {
                                  const res = await fetch(`/api/promos/publish/${promo.id}`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ selectedPlatforms })
                                  });
                                  if (res.ok) {
                                    const data = await res.json();
                                    if (data.status === 'success') {
                                      setPromos(prev => prev.map(p => p.id === promo.id ? { ...p, status: 'Published' } : p));
                                      showToast(data.message);
                                    } else {
                                      showToast(`❌ Publish Rejected: ${data.message}`);
                                    }
                                  } else {
                                    showToast("❌ Unable to establish handshake with outbound social endpoints.");
                                  }
                                } catch (err) {
                                  console.error("Publish action error:", err);
                                  showToast("❌ An unexpected error occurred while publishing.");
                                }
                              }}
                              className="flex items-center gap-1.5 px-4 py-2 text-xs font-mono font-bold bg-cyan-950 text-cyan-400 border border-cyan-500/40 rounded-xl hover:bg-cyan-900 transition-all cursor-pointer"
                            >
                              <Send className="w-3.5 h-3.5" /> Publish Multi-Channel
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </motion.div>
            )}

            {/* TAB 3: AUTO WEBSITE WATCHER */}
            {activeTab === 'watcher' && (
              <motion.div
                key="watcher-panel"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-6"
              >
                {/* Watcher introduction card */}
                <div className="bg-black/40 border border-cyan-500/15 rounded-3xl p-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Radio className="w-4 h-4 text-[#00f0ff] animate-pulse" />
                        <h2 className="text-base font-mono font-extrabold uppercase tracking-widest text-[#00f0ff]">
                          Website Update Watcher
                        </h2>
                      </div>
                      <p className="text-xs text-slate-400 font-sans max-w-xl">
                        This automated engine monitors sitemaps, CMS updates, and page content logs. When any changes or new features are deployed, it instantly queues fresh automated script suggestions.
                      </p>
                    </div>
                    
                    <button
                      onClick={startWebsiteScan}
                      disabled={isScanning}
                      className="px-5 py-3 rounded-xl bg-cyan-500 text-black hover:bg-cyan-450 tracking-wider font-mono font-black text-xs uppercase flex items-center gap-2 shadow-[0_0_20px_rgba(0,240,255,0.3)] transition-all cursor-pointer disabled:opacity-50"
                    >
                      {isScanning ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin text-black" />
                          Scanning Pages...
                        </>
                      ) : (
                        <>
                          <Radio className="w-4 h-4 text-black" />
                          Trig. Compare Scan
                        </>
                      )}
                    </button>
                  </div>

                  {/* Scanning progress display */}
                  {isScanning && (
                    <div className="mt-6 space-y-2">
                      <div className="flex justify-between items-center text-[10px] font-mono text-cyan-400">
                        <span>ESTABLISHING CONTENT HASH COMPILATION...</span>
                        <span>{scanProgress}%</span>
                      </div>
                      <div className="w-full bg-cyan-950/50 rounded-full h-1.5 border border-cyan-500/10 overflow-hidden">
                        <div className="bg-cyan-500 h-full transition-all duration-300" style={{ width: `${scanProgress}%` }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* ACTIVE UPDATE AUTOMATION TRIGGER GRID DECK (10 ACTIVE TRIGGERS) */}
                <div className="bg-purple-950/15 border border-[#e840f3]/15 rounded-3xl p-5 space-y-3.5 relative overflow-hidden backdrop-blur-sm z-10">
                  <div className="border-b border-white/5 pb-2">
                    <h3 className="text-xs font-mono font-black uppercase text-white tracking-widest flex items-center gap-1.5">
                      <Radio className="w-4 h-4 text-[#e840f3] animate-pulse" />
                      Active Update Trigger Simulation Deck (10 Options)
                    </h3>
                    <p className="text-[11px] font-sans text-slate-400 mt-1">
                      Directly simulate structural system edits or sitemap alterations. Clicking any option launches an active scan comparison to synthesize specific 15-20s social promos.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5">
                    {triggersList.map((trig, index) => (
                      <button
                        key={index}
                        onClick={() => simulateTrigger(trig)}
                        disabled={isScanning}
                        className={`group p-3 rounded-xl border text-left flex flex-col justify-between h-[85px] transition-all cursor-pointer ${
                          isScanning 
                            ? "bg-black/20 border-white/5 opacity-40 cursor-not-allowed" 
                            : "bg-[#04081c]/70 border-purple-550/20 hover:border-[#e840f3]/50 hover:shadow-[0_0_15px_rgba(232,64,243,0.15)]"
                        }`}
                      >
                        <span className="text-[8px] font-mono text-slate-400 uppercase tracking-tighter block line-clamp-1 group-hover:text-cyan-400 transition-colors">
                          {trig.label}
                        </span>
                        <span className="text-[10px] font-sans font-bold text-white leading-tight line-clamp-2 block mt-1 tracking-tight">
                          {trig.spec}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Scan Console logs screen */}
                {scanLogs.length > 0 && (
                  <div className="bg-zinc-950 border border-cyan-500/15 rounded-2xl p-4 font-mono text-[10px] leading-relaxed text-cyan-400/80 max-h-[160px] overflow-y-auto custom-scrollbar shadow-inner">
                    <div className="text-[9px] uppercase font-black text-cyan-500 border-b border-cyan-500/10 pb-1 mb-2 tracking-widest flex items-center gap-1.5">
                      <span>WATCHER_SHELL_OUTPUT</span>
                    </div>
                    {scanLogs.map((log, i) => (
                      <div key={i} className="whitespace-pre-wrap">{log}</div>
                    ))}
                  </div>
                )}

                {/* Detected Changes & Actions */}
                <div className="space-y-4">
                  <h3 className="text-sm font-mono font-bold uppercase tracking-wider text-slate-350 flex items-center gap-1.5">
                    <Layers className="w-4 h-4 text-purple-400" />
                    <span>Latest Content Compare Results</span>
                  </h3>

                  {detectedChanges.length === 0 ? (
                    <div className="bg-[#030611]/60 border border-white/5 p-8 rounded-2xl text-center space-y-2 text-slate-500 text-xs">
                      <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto" />
                      <h4 className="font-bold text-slate-400 uppercase">Perimeter fully synced</h4>
                      <p>No new changes detected. All active guide videos up-to-date with German bare-metal servers.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {detectedChanges.map(change => (
                        <div 
                          key={change.id}
                          className="bg-purple-950/10 border border-purple-500/20 rounded-2xl p-5 relative overflow-hidden"
                        >
                          <div className="absolute top-0 right-0 p-3 bg-purple-950/40 border-l border-b border-purple-500/20 text-[9px] font-mono uppercase font-black text-[#e840f3]">
                            NEW_ASSET_TRIGGER
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex flex-wrap gap-2 items-center text-xs font-mono">
                              <span className="text-zinc-400">Directory:</span>
                              <strong className="text-purple-400 block">{change.page}</strong>
                              <span className="text-zinc-500">|</span>
                              <span className="text-slate-300">Action:</span>
                              <strong className="text-amber-400 block uppercase">{change.type}</strong>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-b border-white/5 py-3 text-xs leading-relaxed font-sans">
                              <div>
                                <span className="text-[10px] font-mono text-zinc-500 uppercase block tracking-wider">Field Name:</span>
                                <strong className="text-white text-sm block mt-0.5">{change.field}</strong>
                              </div>
                              <div>
                                <span className="text-[10px] font-mono text-zinc-500 uppercase block tracking-wider">Content Summary:</span>
                                <p className="text-slate-300 mt-0.5 italic">"{change.newValue}"</p>
                              </div>
                            </div>

                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-1">
                              <span className="text-[10px] font-mono text-amber-450 uppercase font-bold flex items-center gap-1">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                {change.impact}
                              </span>

                              <div className="flex gap-2">
                                <button
                                  onClick={() => generateVideoFromChange(change, 'guide')}
                                  className="px-3 py-1.5 rounded-lg bg-[#e840f3]/25 hover:bg-[#e840f3]/40 border border-[#e840f3]/35 text-white text-[10px] font-mono font-bold uppercase transition-all cursor-pointer"
                                >
                                  + Outpost Guide Draft
                                </button>
                                <button
                                  onClick={() => generateVideoFromChange(change, 'promo')}
                                  className="px-3 py-1.5 rounded-lg bg-cyan-950/40 hover:bg-cyan-900/40 border border-cyan-500/30 text-cyan-400 text-[10px] font-mono font-bold uppercase transition-all cursor-pointer"
                                >
                                  + Outpost Promo Draft
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* History list */}
                <div className="space-y-3 pt-4">
                  <h3 className="text-xs font-mono font-bold uppercase tracking-widest text-[#00f0ff] flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-[#00f0ff]" />
                    <span>Watcher scan logs index</span>
                  </h3>
                  <div className="border border-white/5 rounded-2xl overflow-hidden bg-black/25">
                    {watcherHistory.map(hist => (
                      <div 
                        key={hist.id} 
                        className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3.5 border-b border-white/5 last:border-b-0 text-xs font-mono"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-zinc-500">{hist.date}</span>
                            <strong className="text-slate-350">{hist.type}</strong>
                          </div>
                          <span className="text-zinc-500 block">{hist.details}</span>
                        </div>

                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider ${
                          hist.status === 'No changes' ? 'text-zinc-500 bg-zinc-950 border border-white/5' : 'text-[#f574fe] bg-[#f574fe]/10 border border-[#f574fe]/20'
                        }`}>
                          {hist.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* TAB 4: APPROVAL CENTER */}
            {activeTab === 'approval' && (
              <motion.div
                key="approval-panel"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-6"
              >
                <div className="bg-black/30 border border-[#e840f3]/15 rounded-3xl p-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-[#e840f3]/5 rounded-full blur-3xl pointer-events-none" />
                  <div className="space-y-2">
                    <h2 className="text-base font-mono font-extrabold uppercase tracking-widest text-[#e840f3] flex items-center gap-1.5">
                      <UserCheck className="w-5 h-5" />
                      NexoraX Video Approval Matrix
                    </h2>
                    <p className="text-xs text-slate-400 font-sans leading-relaxed">
                      All video marketing drafts are securely registered internally before public deployment. Approve scripts, aspect proportions, captions, and brand details with complete management.
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  {promos.filter(p => p.status !== 'Published' && p.status !== 'Archived' && p.status !== 'Scheduled').map(draft => (
                    <div 
                      key={draft.id}
                      className="bg-[#050915]/80 border border-purple-500/15 rounded-3xl p-6 space-y-6 relative overflow-hidden"
                    >
                      {/* Grid background effect */}
                      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e840f305_1px,transparent_1px),linear-gradient(to_bottom,#e840f305_1px,transparent_1px)] bg-[size:16px_16px] pointer-events-none" />

                      {/* Card header with info */}
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-white/5 pb-4 relative z-10">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[9px] font-mono bg-purple-950/40 text-[#f574fe] border border-purple-500/30 px-2 py-0.5 rounded uppercase font-bold tracking-widest">
                              {draft.selectedTemplate || 'Cyber City Launch'}
                            </span>
                            <span className="text-[9px] font-mono bg-cyan-950/40 text-cyan-400 border border-cyan-500/30 px-2 py-0.5 rounded uppercase font-bold tracking-widest">
                              {draft.format || '9:16 Vertical'}
                            </span>
                          </div>
                          <h3 className="text-base font-sans font-black text-white tracking-tight mt-1">
                            {draft.title}
                          </h3>
                        </div>

                        <div className="flex items-center gap-3">
                          <span className="text-[10px] font-mono text-zinc-500">Auto Guard: Verified</span>
                          <span className="px-2.5 py-1 rounded-full text-[9px] font-mono font-black uppercase bg-amber-950/40 text-amber-400 border border-amber-500/20 shadow-[0_0_10px_rgba(245,158,11,0.05)]">
                            {draft.status}
                          </span>
                        </div>
                      </div>

                      {/* Main visual & deep review layout */}
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 relative z-10">
                        {/* LEFT COLUMN (GRID SIZE 5): Thumbnail Rendering & 10-Platform Outposts */}
                        <div className="lg:col-span-5 space-y-4">
                          <span className="text-[10px] font-mono uppercase tracking-wider text-purple-400 font-extrabold block">Video Thumbnail & Proportions:</span>
                          
                          {/* Rich visual thumbnail mockup based on template selection */}
                          <div className="relative aspect-[16/10] w-full rounded-2xl overflow-hidden border border-white/5 bg-[#03050c] shadow-inner flex flex-col justify-between p-4 group">
                            {/* Grid overlay */}
                            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff02_1px,transparent_1px),linear-gradient(to_bottom,#ffffff02_1px,transparent_1px)] bg-[size:12px_12px]" />
                            
                            <div className="flex justify-between items-start z-10">
                              <span className="text-[10px] bg-black/60 border border-white/10 text-white font-mono font-bold px-2 py-0.5 rounded">
                                NEXORAX AI
                              </span>
                              <span className="text-[9px] font-mono text-cyan-400 bg-black/80 px-2 py-0.5 rounded border border-cyan-500/20">
                                {draft.duration || '18s'}
                              </span>
                            </div>

                            {/* Template aesthetic backdrop */}
                            <div className="absolute inset-x-0 top-1/4 bottom-1/4 flex flex-col items-center justify-center text-center px-4 py-2 bg-gradient-to-r from-purple-950/20 via-cyan-950/20 to-purple-950/20 border-y border-white/5 backdrop-blur-xs z-0">
                              <span className="text-[8px] font-mono text-[#00f6ff] tracking-widest uppercase mb-1 font-bold">UHD MASTERING</span>
                              <h4 className="text-sm font-sans font-black text-white leading-tight uppercase tracking-tight shadow-md">
                                {draft.thumbnailTitle || draft.title}
                              </h4>
                              <p className="text-[8px] text-zinc-400 font-mono tracking-wide mt-1">CINEMATIC OUTPOST TEMPLATE</p>
                            </div>

                            <div className="flex justify-between items-end z-10">
                              <span className="text-[9px] font-mono text-zinc-500">PRODUCED BY NODE-07</span>
                              <div className="flex gap-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                <div className="w-1.5 h-1.5 rounded-full bg-[#e840f3]/40" />
                              </div>
                            </div>
                          </div>

                          {/* 10 Connected Publication Platforms Selector */}
                          <div className="space-y-2 pt-2">
                            <label className="text-[10px] font-mono text-zinc-550 uppercase tracking-wider block font-bold">10-Channel Publication Outpost Previews</label>
                            
                            {/* Scrollable platforms tabs list */}
                            <div className="flex gap-1 overflow-x-auto pb-1.5 scrollbar-thin scrollbar-thumb-zinc-800">
                              {['YouTube', 'YouTube Shorts', 'TikTok', 'Instagram Reels', 'Facebook Reels', 'Facebook Page', 'LinkedIn', 'X / Twitter', 'Blog', 'Newsletter'].map((plat) => (
                                <button
                                  key={plat}
                                  onClick={() => setSelectedPreviewPlatform(plat)}
                                  className={`px-2.5 py-1 text-[9px] font-mono font-bold uppercase rounded border transition-all shrink-0 cursor-pointer ${
                                    selectedPreviewPlatform === plat
                                      ? 'bg-purple-950/30 border-purple-500/50 text-[#f574fe]'
                                      : 'bg-black/30 border-transparent text-zinc-500 hover:text-zinc-300'
                                  }`}
                                >
                                  {plat}
                                </button>
                              ))}
                            </div>

                            {/* Platform Custom Captions Viewport Container */}
                            <div className="bg-black/40 border border-white/5 p-3.5 rounded-2xl text-xs space-y-2 text-left">
                              <div className="flex justify-between items-center border-b border-white/5 pb-1.5">
                                <span className="text-[9px] font-mono text-[#00f6ff] uppercase font-bold tracking-wider">{selectedPreviewPlatform} TARGET CONFIGURATION</span>
                                <span className="text-[8px] text-emerald-400 bg-emerald-950/20 px-1.5 py-0.5 rounded uppercase font-mono font-bold font-semibold">Ready</span>
                              </div>

                              <div className="space-y-1">
                                <span className="text-[8px] font-mono text-zinc-500 uppercase block">Outpost Title / Subject:</span>
                                <p className="font-sans font-bold text-white text-[11px]">
                                  {selectedPreviewPlatform === 'YouTube' || selectedPreviewPlatform === 'Blog'
                                    ? `[NexoraX Digital] - ${draft.title}`
                                    : selectedPreviewPlatform === 'Newsletter'
                                    ? `🚀 NexoraX Digital: Brand Update - ${draft.title}`
                                    : `${draft.title} | NexoraX AI`
                                  }
                                </p>
                              </div>

                              <div className="space-y-1 pt-1.5">
                                <span className="text-[8px] font-mono text-zinc-500 uppercase block">Dynamic Caption / Card Payload:</span>
                                <p className="text-[10.5px] text-zinc-300 font-sans italic leading-relaxed">
                                  "{selectedPreviewPlatform === 'LinkedIn' 
                                    ? `${draft.platformCaption || draft.script.hook} Connected via SecurePayment NexoraX systems. Contact Lead Director at support@nexoraxdigital.co.uk.`
                                    : selectedPreviewPlatform === 'TikTok' || selectedPreviewPlatform === 'YouTube Shorts'
                                    ? `${draft.platformCaption || draft.script.hook} #AI #Automation #SaaS #Business`
                                    : selectedPreviewPlatform === 'Blog'
                                    ? `<h2>${draft.title}</h2><p>Here is our latest high-frequency system deployment update. Watch the automated video overview below.</p><div class="nexorax-embed-frame"></div>`
                                    : selectedPreviewPlatform === 'Newsletter'
                                    ? `Dear Partners,\n\nWe have automatically updated our operational matrix with brand new capabilities. \n\nClick link to watch video: https://nexoraxdigital.co.uk`
                                    : `${draft.platformCaption || draft.script.hook}`
                                  }"
                                </p>
                              </div>

                              <div className="flex gap-2 pt-1">
                                {draft.hashtags.map(tag => (
                                  <span key={tag} className="text-[8px] font-mono text-purple-400">#{tag}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* RIGHT COLUMN (GRID SIZE 7): Spoken Script, Voiceover text & Contact Details verification */}
                        <div className="lg:col-span-7 space-y-4">
                          
                          {/* Script Inline Editor & Display */}
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-[10px] font-mono uppercase tracking-wider text-cyan-400 font-extrabold block">Spoken Audio Script Sections:</span>
                              <button
                                onClick={() => {
                                  if (editingScriptId === draft.id) {
                                    setEditingScriptId(null);
                                  } else {
                                    setEditingScriptId(draft.id);
                                    setEditingScriptValues({
                                      hook: draft.script.hook || '',
                                      problem: draft.script.problem || '',
                                      solution: draft.script.solution || '',
                                      benefit: draft.script.benefit || '',
                                      cta: draft.script.cta || ''
                                    });
                                  }
                                }}
                                className="text-[10px] font-mono text-pink-400 hover:text-pink-300 underline cursor-pointer"
                              >
                                {editingScriptId === draft.id ? "Cancel Editor" : "Edit Script Lines"}
                              </button>
                            </div>

                            {editingScriptId === draft.id ? (
                              <div className="bg-black/60 border border-pink-500/20 p-4 rounded-2xl space-y-3">
                                <span className="text-[9px] font-mono text-pink-400 block uppercase font-bold">Interactive Script Composer</span>
                                <div className="space-y-2 text-xs font-mono">
                                  <div className="space-y-1">
                                    <label className="text-zinc-500 text-[9px] uppercase font-bold block">0-3s Hook</label>
                                    <input 
                                      type="text" 
                                      value={editingScriptValues.hook}
                                      onChange={(e) => setEditingScriptValues(prev => ({ ...prev, hook: e.target.value }))}
                                      className="w-full bg-black/60 border border-zinc-800 rounded-lg p-2 text-white font-sans text-[11px]" 
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-zinc-500 text-[9px] uppercase font-bold block">3-8s Problem</label>
                                    <input 
                                      type="text" 
                                      value={editingScriptValues.problem}
                                      onChange={(e) => setEditingScriptValues(prev => ({ ...prev, problem: e.target.value }))}
                                      className="w-full bg-black/60 border border-zinc-800 rounded-lg p-2 text-white font-sans text-[11px]" 
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-zinc-500 text-[9px] uppercase font-bold block">8-14s Solution</label>
                                    <input 
                                      type="text" 
                                      value={editingScriptValues.solution}
                                      onChange={(e) => setEditingScriptValues(prev => ({ ...prev, solution: e.target.value }))}
                                      className="w-full bg-black/60 border border-zinc-800 rounded-lg p-2 text-white font-sans text-[11px]" 
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-zinc-500 text-[9px] uppercase font-bold block">14-18s Benefit</label>
                                    <input 
                                      type="text" 
                                      value={editingScriptValues.benefit}
                                      onChange={(e) => setEditingScriptValues(prev => ({ ...prev, benefit: e.target.value }))}
                                      className="w-full bg-black/60 border border-zinc-800 rounded-lg p-2 text-white font-sans text-[11px]" 
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-zinc-500 text-[9px] uppercase font-bold block">18-20s CTA</label>
                                    <input 
                                      type="text" 
                                      value={editingScriptValues.cta}
                                      onChange={(e) => setEditingScriptValues(prev => ({ ...prev, cta: e.target.value }))}
                                      className="w-full bg-black/60 border border-zinc-800 rounded-lg p-2 text-white font-sans text-[11px]" 
                                    />
                                  </div>
                                </div>
                                <button
                                  onClick={async () => {
                                    await updatePromoBackend(draft.id, { 
                                      script: { ...draft.script, ...editingScriptValues },
                                      voiceoverScript: `Hook: ${editingScriptValues.hook} Problem: ${editingScriptValues.problem} Solution: ${editingScriptValues.solution} Benefit: ${editingScriptValues.benefit} Outro: ${editingScriptValues.cta}`
                                    });
                                    setEditingScriptId(null);
                                    showToast("NexoraX script lines updated seamlessly!");
                                  }}
                                  className="w-full py-2 rounded-xl bg-pink-950 text-pink-400 border border-pink-500/40 text-[10px] font-mono uppercase font-bold transition-all cursor-pointer"
                                >
                                  Save New Script Setup
                                </button>
                              </div>
                            ) : (
                              <div className="bg-black/50 p-4 rounded-xl border border-white/5 space-y-2 text-xs text-left">
                                <div className="space-y-1.5 font-sans divide-y divide-white/5">
                                  <p className="pb-1"><strong className="text-zinc-500 font-mono text-[9px] mr-2 uppercase tracking-tight inline-block w-20 shrink-0">0-3s Hook:</strong> <span className="text-zinc-200">"{draft.script.hook}"</span></p>
                                  <p className="py-1"><strong className="text-zinc-500 font-mono text-[9px] mr-2 uppercase tracking-tight inline-block w-20 shrink-0 font-semibold">3-8s Problem:</strong> <span className="text-zinc-200">"{draft.script.problem}"</span></p>
                                  <p className="py-1"><strong className="text-zinc-500 font-mono text-[9px] mr-2 uppercase tracking-tight inline-block w-20 shrink-0 font-semibold">8-14s Solution:</strong> <span className="text-zinc-200">"{draft.script.solution}"</span></p>
                                  <p className="py-1"><strong className="text-zinc-500 font-mono text-[9px] mr-2 uppercase tracking-tight inline-block w-20 shrink-0 font-semibold">14-18s Benefit:</strong> <span className="text-zinc-200">"{draft.script.benefit}"</span></p>
                                  <p className="pt-1"><strong className="text-zinc-500 font-mono text-[9px] mr-2 uppercase tracking-tight inline-block w-20 shrink-0 font-semibold">18-20s CTA:</strong> <span className="text-zinc-200">"{draft.script.cta}"</span></p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Synthetic Voiceover & Script Readout */}
                          <div className="space-y-1 bg-black/20 border border-white/5 p-3.5 rounded-2xl text-left">
                            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 font-extrabold block">Synthetic Voiceover Prompt Script:</span>
                            <p className="text-[10.5px] text-slate-350 leading-relaxed font-sans normal-case italic">
                              "{draft.voiceoverScript || `Attention enterprise networks! We have initialized a vital update on ${draft.title}. Secure your corporate landing coordinates via nexoraxdigital.co.uk today.`}"
                            </p>
                          </div>

                          {/* Brand Profile Contact Coordinates and Rules Checklist */}
                          <div className="bg-[#030611]/70 border border-white/5 p-4 rounded-2xl text-left space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-[10px] font-mono text-cyan-400 font-black uppercase tracking-wider">
                                Brand Profile Credentials Verification
                              </span>
                              <span className="text-[9px] font-mono text-zinc-500 uppercase">Audit state</span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[10px] font-mono">
                              <div className="bg-black/40 border border-white/5 p-2 rounded-lg flex flex-col justify-between">
                                <span className="text-zinc-500">Website URL</span>
                                <strong className="text-zinc-200 truncate mt-0.5">{publicBrandSettings.websiteUrl || 'Missing'}</strong>
                              </div>
                              <div className="bg-black/40 border border-white/5 p-2 rounded-lg flex flex-col justify-between">
                                <span className="text-zinc-500">Public Phone</span>
                                <strong className="text-zinc-200 truncate mt-0.5">{publicBrandSettings.publicPhone || 'Missing'}</strong>
                              </div>
                              <div className="bg-black/40 border border-white/5 p-2 rounded-lg flex flex-col justify-between">
                                <span className="text-zinc-500">Public Email</span>
                                <strong className="text-zinc-200 truncate mt-0.5">{publicBrandSettings.publicEmail || 'Missing'}</strong>
                              </div>
                            </div>

                            {(!publicBrandSettings.websiteUrl || !publicBrandSettings.publicEmail || !publicBrandSettings.publicPhone) ? (
                              <div className="bg-red-950/40 border border-red-500/30 p-2.5 rounded-xl flex items-center gap-2 text-[10px] text-red-400 font-mono">
                                <ShieldAlert className="w-4 h-4 shrink-0" />
                                <span>⚠️ PUBLIC CONTACT DETAILS REQUIRED BEFORE PUBLIC DISPATCH</span>
                              </div>
                            ) : (
                              <div className="bg-emerald-950/20 border border-emerald-500/20 p-2.5 rounded-xl flex items-center gap-2 text-[10px] text-emerald-400 font-mono">
                                <Check className="w-4 h-4 shrink-0" />
                                <span>🛡️ Profile aligned. Certified for multi-platform outbound post</span>
                              </div>
                            )}

                            {/* Interactive Scheduler Setup inline inside card */}
                            {activeSchedulingId === draft.id ? (
                              <div className="border border-purple-500/20 bg-purple-950/15 p-3 rounded-xl space-y-3 pt-3.5 mt-2">
                                <div className="flex justify-between items-center border-b border-purple-500/10 pb-2">
                                  <span className="text-[10px] font-mono text-purple-400 font-black uppercase">Schedule Outpost Date & Time</span>
                                  <button onClick={() => setActiveSchedulingId(null)} className="text-[9px] text-zinc-500 hover:text-zinc-300">Close</button>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-[10px] font-mono">
                                  <div>
                                    <label className="text-zinc-500 block mb-1 uppercase">Date</label>
                                    <input 
                                      type="date"
                                      value={schedulerConfig.scheduleTime.substring(0,10)}
                                      onChange={(e) => {
                                        const time = schedulerConfig.scheduleTime.substring(11);
                                        setSchedulerConfig(p => ({ ...p, scheduleTime: `${e.target.value}T${time}` }));
                                      }}
                                      className="w-full bg-black border border-white/10 rounded p-1 text-slate-300"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-zinc-500 block mb-1 uppercase">Time</label>
                                    <input 
                                      type="time"
                                      value={schedulerConfig.scheduleTime.substring(11)}
                                      onChange={(e) => {
                                        const date = schedulerConfig.scheduleTime.substring(0,10);
                                        setSchedulerConfig(p => ({ ...p, scheduleTime: `${date}T${e.target.value}` }));
                                      }}
                                      className="w-full bg-black border border-white/10 rounded p-1 text-slate-300"
                                    />
                                  </div>
                                </div>
                                <button
                                  onClick={async () => {
                                    await updatePromoBackend(draft.id, { status: 'Scheduled' });
                                    setActiveSchedulingId(null);
                                    showToast(`Asset successfully scheduled for automated post: ${schedulerConfig.scheduleTime}`);
                                  }}
                                  className="w-full py-1.5 font-mono text-[9px] text-purple-400 bg-purple-950 hover:bg-purple-900 border border-purple-500/30 uppercase font-black rounded-lg transition-all"
                                >
                                  Lock Schedule Coordinates
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {/* ACTION BUTTON PANEL (Matches requested: Approve, Edit Script, Regenerate, Publish Now, Schedule) */}
                      <div className="flex flex-wrap gap-2 justify-end pt-4 border-t border-white/5 relative z-10">
                        {/* Edit Script toggle */}
                        <button 
                          onClick={() => {
                            if (editingScriptId === draft.id) {
                              setEditingScriptId(null);
                            } else {
                              setEditingScriptId(draft.id);
                              setEditingScriptValues({
                                hook: draft.script.hook || '',
                                problem: draft.script.problem || '',
                                solution: draft.script.solution || '',
                                benefit: draft.script.benefit || '',
                                cta: draft.script.cta || ''
                              });
                            }
                          }}
                          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono font-bold rounded-xl transition-all cursor-pointer ${
                            editingScriptId === draft.id
                              ? 'bg-pink-950 text-pink-400 border border-pink-500/50'
                              : 'bg-black/40 hover:bg-black text-zinc-400 border border-zinc-800'
                          }`}
                        >
                          <Edit className="w-3.5 h-3.5 text-pink-550" /> Edit Script
                        </button>

                        {/* Schedule toggle */}
                        <button 
                          onClick={() => {
                            if (activeSchedulingId === draft.id) {
                              setActiveSchedulingId(null);
                            } else {
                              setActiveSchedulingId(draft.id);
                            }
                          }}
                          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono font-bold rounded-xl transition-all cursor-pointer ${
                            activeSchedulingId === draft.id
                              ? 'bg-purple-950 text-purple-400 border border-purple-500/50'
                              : 'bg-black/40 hover:bg-black text-zinc-450 border border-zinc-800'
                          }`}
                        >
                          <Calendar className="w-3.5 h-3.5 text-purple-400" /> Schedule
                        </button>

                        {/* Regenerate simulation */}
                        <button
                          onClick={() => {
                            showToast(`Regenerating UHD visual frames and synthesising professional dialogue with chosen timbre...`);
                          }}
                          className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono hover:text-white text-zinc-400 bg-black/40 border border-zinc-805 rounded-xl hover:border-zinc-700 transition-all cursor-pointer"
                        >
                          <RefreshCw className="w-3.5 h-3.5 text-cyan-400" /> Regenerate
                        </button>

                        {/* Preview Player launcher */}
                        <button 
                          onClick={() => handleOpenPreview(draft, '9:16')}
                          className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono font-black bg-[#e840f3]/10 hover:bg-[#e840f3]/20 border border-[#e840f3]/30 text-white rounded-xl transition-all cursor-pointer"
                        >
                          <Eye className="w-3.5 h-3.5 text-[#e840f3]" /> Preview Player
                        </button>

                        {/* Approve Asset inline status update */}
                        <button 
                          onClick={() => {
                            if (!publicBrandSettings.websiteUrl || !publicBrandSettings.publicEmail || !publicBrandSettings.publicPhone) {
                              showToast("⚠️ Blocked: Please configure all Public Brand Details in settings first.");
                              return;
                            }
                            updatePromoStatus(draft.id, 'Approved');
                            showToast(`Promo update asset approved for multi-channel outbound post.`);
                          }}
                          disabled={!publicBrandSettings.websiteUrl || !publicBrandSettings.publicEmail || !publicBrandSettings.publicPhone}
                          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-mono font-black rounded-xl transition-all cursor-pointer ${
                            (!publicBrandSettings.websiteUrl || !publicBrandSettings.publicEmail || !publicBrandSettings.publicPhone)
                              ? 'bg-zinc-900 text-zinc-600 border border-zinc-800 cursor-not-allowed opacity-50'
                              : 'bg-emerald-950 hover:bg-emerald-900 text-emerald-400 border border-emerald-500/35'
                          }`}
                        >
                          <Check className="w-3.5 h-3.5" /> Approve Asset
                        </button>
                        
                        {/* Instant Publish Now */}
                        <button 
                          onClick={() => {
                            if (!publicBrandSettings.websiteUrl || !publicBrandSettings.publicEmail || !publicBrandSettings.publicPhone) {
                              showToast("⚠️ Blocked: Please configure all Public Brand Details in settings first.");
                              return;
                            }
                            updatePromoStatus(draft.id, 'Published');
                            showToast(`Asset successfully published instantly across selected social channels!`);
                          }}
                          disabled={!publicBrandSettings.websiteUrl || !publicBrandSettings.publicEmail || !publicBrandSettings.publicPhone}
                          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-mono font-black rounded-xl transition-all cursor-pointer ${
                            (!publicBrandSettings.websiteUrl || !publicBrandSettings.publicEmail || !publicBrandSettings.publicPhone)
                              ? 'bg-zinc-900 text-zinc-650 border border-zinc-850 cursor-not-allowed opacity-50'
                              : 'bg-cyan-950 hover:bg-cyan-900 text-cyan-400 border border-cyan-500/35'
                          }`}
                        >
                          <Send className="w-3.5 h-3.5" /> Publish Now
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* TAB 5: VIDEO ANALYTICS */}
            {activeTab === 'analytics' && (
              <motion.div
                key="analytics-panel"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-6"
              >
                {/* Purple Glass KPI metric grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: "Aggregate Views", val: "18,495", pct: "+18.2%", icon: Eye, color: "text-cyan-400" },
                    { label: "Aggregate Watch Time", val: "154.2 hrs", pct: "+9.5%", icon: Clock, color: "text-purple-400" },
                    { label: "Click-Through Rate", val: "4.82%", pct: "+2.1%", icon: BarChart2, color: "text-amber-400" },
                    { label: "Leads Generated", val: "124", pct: "+24.0%", icon: UserCheck, color: "text-emerald-400" }
                  ].map((kpi, i) => {
                    const Icon = kpi.icon;
                    return (
                      <div key={i} className="bg-[#050915]/60 border border-white/5 rounded-2xl p-4 space-y-2 relative overflow-hidden">
                        <div className="flex justify-between items-center text-zinc-500">
                          <span className="text-[10px] font-mono uppercase tracking-wider">{kpi.label}</span>
                          <Icon className={`w-4 h-4 ${kpi.color}`} />
                        </div>
                        <div className="flex justify-between items-baseline">
                          <strong className="text-xl font-sans tracking-tight text-white">{kpi.val}</strong>
                          <span className="text-[9px] font-mono text-emerald-400 font-bold">{kpi.pct}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Engagement trend layout using simple pure Tailwind graph heights */}
                <div className="bg-[#04081c]/75 border border-purple-950/40 rounded-3xl p-6 space-y-4">
                  <div className="flex justify-between items-center border-b border-white/5 pb-2">
                    <div>
                      <h3 className="text-sm font-mono font-bold uppercase text-white tracking-wider">
                        Engagement Yield Stream
                      </h3>
                      <span className="text-[10px] font-mono text-slate-500 uppercase block">Daily tracking of viewer retention logs</span>
                    </div>

                    <div className="flex gap-2">
                      <span className="inline-flex items-center gap-1.5 text-[9px] font-mono text-[#00ffff]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00ffff] animate-ping" />
                        Live updates
                      </span>
                    </div>
                  </div>

                  {/* Horizontal and Vertical Chart with 12 Days bars */}
                  <div className="h-44 flex items-end justify-between gap-2 pt-6 px-2 relatvive">
                    {[34, 45, 62, 41, 78, 89, 54, 91, 110, 85, 120, 145].map((val, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-2 group cursor-pointer h-full justify-end">
                        <div className="relative w-full flex justify-center h-full items-end">
                          <div className="absolute bottom-full mb-1 bg-black text-[#00f0ff] border border-cyan-550/30 text-[8px] font-mono px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            {val}
                          </div>
                          <div 
                            className="w-full bg-gradient-to-t from-purple-800 to-cyan-400 rounded-t-md hover:from-purple-600 hover:to-cyan-300 transition-all" 
                            style={{ height: `${(val / 150) * 100}%` }}
                          />
                        </div>
                        <span className="text-[8px] font-mono text-zinc-500 uppercase tracking-tighter">Day {i+1}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Best performances log lists */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-zinc-950/40 border border-white/5 p-5 rounded-2xl space-y-3">
                    <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-cyan-450">Top Performing Guides</h4>
                    <div className="space-y-2 text-xs">
                      {[
                        { title: "How to set up your brand profile", rate: "94.2% completed", views: 2510 },
                        { title: "How to generate a video", rate: "89.5% completed", views: 1980 },
                        { title: "How to request your first campaign", rate: "81.0% completed", views: 1540 }
                      ].map((item, i) => (
                        <div key={i} className="flex justify-between items-center bg-black/30 p-2.5 rounded-lg border border-white/5">
                          <span className="text-zinc-300 font-sans truncate pr-4">{item.title}</span>
                          <span className="text-[10px] font-mono text-cyan-400 shrink-0 font-bold">{item.rate}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-zinc-950/40 border border-white/5 p-5 rounded-2xl space-y-3">
                    <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-purple-450">Top Social Promo Formats</h4>
                    <div className="space-y-2 text-xs">
                      {[
                        { platform: "TikTok (9:16)", conversion: "12.8% conv.", views: 8400 },
                        { platform: "Instagram Reels (9:16)", conversion: "10.5% conv.", views: 7210 },
                        { platform: "YouTube Shorts (9:16)", conversion: "9.2% conv.", views: 6150 }
                      ].map((item, i) => (
                        <div key={i} className="flex justify-between items-center bg-black/30 p-2.5 rounded-lg border border-white/5">
                          <span className="text-zinc-300 font-sans font-bold">{item.platform}</span>
                          <span className="text-[10px] font-mono text-purple-400 font-bold">{item.conversion}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* TAB 6: BRAND PROFILE AND CONFIGURATION */}
            {activeTab === 'settings' && (
              <motion.div
                key="settings-panel"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-6"
              >
                {/* Safe public brand contact details */}
                <div className="bg-purple-950/15 border border-purple-500/20 rounded-3xl p-6 space-y-4">
                  <div className="border-b border-white/5 pb-2.5">
                    <h3 className="text-base font-sans font-extrabold uppercase text-white tracking-wider flex items-center gap-1.5">
                      <Globe className="w-5 h-5 text-purple-400" />
                      Public Brand Profile coordinates
                    </h3>
                    <span className="text-[10.5px] font-mono text-purple-450 block uppercase">NEXORAX DIGITAL BRANDING SETTINGS</span>
                  </div>

                  {/* Contact form inputs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs font-mono">
                    <div className="space-y-1.5">
                      <label className="text-slate-450 uppercase block">Public Website URL</label>
                      <input 
                        type="text" 
                        value={publicBrandSettings.websiteUrl}
                        onChange={(e) => setPublicBrandSettings(prev => ({ ...prev, websiteUrl: e.target.value }))}
                        className="w-full bg-black/50 border border-purple-550/20 hover:border-purple-550/40 rounded-xl px-3 py-2.5 text-white font-sans focus:outline-none focus:border-purple-400 transition-all font-semibold"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-slate-450 uppercase block">Public Support Email</label>
                      <input 
                        type="text" 
                        value={publicBrandSettings.publicEmail}
                        onChange={(e) => setPublicBrandSettings(prev => ({ ...prev, publicEmail: e.target.value }))}
                        className="w-full bg-black/50 border border-purple-550/20 hover:border-purple-550/40 rounded-xl px-3 py-2.5 text-white font-sans focus:outline-none focus:border-purple-400 transition-all font-semibold"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-slate-450 uppercase block">Public Contact Phone</label>
                      <input 
                        type="text" 
                        value={publicBrandSettings.publicPhone}
                        onChange={(e) => setPublicBrandSettings(prev => ({ ...prev, publicPhone: e.target.value }))}
                        className="w-full bg-black/50 border border-purple-550/20 hover:border-purple-550/40 rounded-xl px-3 py-2.5 text-white font-sans focus:outline-none focus:border-purple-400 transition-all font-semibold"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-slate-440 uppercase block">Public Booking Link</label>
                      <input 
                        type="text" 
                        value={publicBrandSettings.bookingLink}
                        onChange={(e) => setPublicBrandSettings(prev => ({ ...prev, bookingLink: e.target.value }))}
                        className="w-full bg-black/50 border border-purple-550/20 hover:border-purple-550/40 rounded-xl px-3 py-2.5 text-white font-sans focus:outline-none focus:border-purple-400 transition-all font-semibold"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-slate-440 uppercase block">Public Support Link</label>
                      <input 
                        type="text" 
                        value={publicBrandSettings.supportLink}
                        onChange={(e) => setPublicBrandSettings(prev => ({ ...prev, supportLink: e.target.value }))}
                        className="w-full bg-black/50 border border-purple-550/20 hover:border-purple-550/40 rounded-xl px-3 py-2.5 text-white font-sans focus:outline-none focus:border-purple-400 transition-all font-semibold"
                      />
                    </div>
                  </div>

                  {/* DYNAMIC VALIDATION CHECK BANNER FOR INCOMPLETE DETAILS */}
                  {(!publicBrandSettings.websiteUrl || !publicBrandSettings.publicEmail || !publicBrandSettings.publicPhone || !publicBrandSettings.bookingLink || !publicBrandSettings.supportLink) ? (
                    <div className="bg-red-950/45 border-2 border-red-500/70 p-4 rounded-xl flex items-start gap-3 shadow-[0_0_15px_rgba(239,68,68,0.2)]">
                      <ShieldAlert className="w-5 h-5 text-red-500 shrink-0 mt-0.5 animate-bounce" />
                      <div className="space-y-1">
                        <strong className="text-red-400 text-xs font-mono uppercase block">⚠️ PUBLIC CONTACT DETAILS REQUIRED:</strong>
                        <p className="text-[10.5px] text-zinc-350 leading-relaxed font-sans normal-case">
                          We detected missing public brand coordinates! Publishing across connected social media platforms has been strictly blocked and paused for alignment. Fill in all profile parameters to resume automated distribution.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-emerald-950/20 border border-emerald-500/30 p-4 rounded-xl flex items-start gap-3">
                      <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <strong className="text-emerald-400 text-xs font-mono uppercase block">🛡️ BRAND INTEGRITY ALIGNED:</strong>
                        <p className="text-[10.5px] text-zinc-350 leading-relaxed font-sans normal-case">
                          Safe public credentials successfully verified. Generated social media cards and voice synthetic lines are fully compatible with external publishing requirements.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Safe context notice */}
                  <div className="bg-red-950/20 border border-red-500/20 p-4 rounded-xl flex items-start gap-3">
                    <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <strong className="text-red-400 text-xs font-mono uppercase block">SECURITY & ISOLATION PROTOCOLS:</strong>
                      <p className="text-[10.5px] text-slate-350 leading-relaxed font-sans normal-case">
                        Only public brand contact details specified above will be inserted into generated social media frames, captions, and script loops. Do NOT record inner NexoraX telecommunication structures, direct mobile extensions, or personal banking ledger identifiers.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Admin configuration triggers */}
                <div className="bg-zinc-950/40 border border-white/5 rounded-3xl p-6 space-y-4">
                  <div className="border-b border-white/5 pb-2.5">
                    <h3 className="text-base font-mono font-bold uppercase text-white tracking-wider flex items-center gap-1.5">
                      <Settings className="w-5 h-5 text-cyan-400" />
                      Marketing generation config controls
                    </h3>
                    <span className="text-[10px] font-mono text-zinc-500 block uppercase">CONFIGURE CHANNELS AND FREQUENCIES</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs font-mono">
                    
                    {/* Select box voiceover style */}
                    <div className="space-y-1.5">
                      <label className="text-zinc-500 uppercase block">Vocal timbre profile</label>
                      <select 
                        value={adminControls.voiceoverStyle} 
                        onChange={(e) => setAdminControls(prev => ({ ...prev, voiceoverStyle: e.target.value }))}
                        className="w-full bg-black border border-zinc-800 rounded-xl px-3 py-2.5 text-slate-300 focus:outline-none"
                      >
                        <option>British Director Echo</option>
                        <option>Vanguard Closer Male</option>
                        <option>High-velocity Autonomy Female</option>
                        <option>NexoraX Synced Oracle Accent</option>
                      </select>
                    </div>

                    {/* Scan frequency select */}
                    <div className="space-y-1.5">
                      <label className="text-zinc-500 uppercase block">Automatic check scanning interval</label>
                      <select 
                        value={adminControls.scanFrequency} 
                        onChange={(e) => setAdminControls(prev => ({ ...prev, scanFrequency: e.target.value }))}
                        className="w-full bg-black border border-zinc-800 rounded-xl px-3 py-2.5 text-slate-300 focus:outline-none"
                      >
                        <option>On deployment webhook</option>
                        <option>Every 6 hours</option>
                        <option>Daily</option>
                        <option>Weekly</option>
                        <option>Manual only</option>
                      </select>
                    </div>
                  </div>

                  {/* Default Promo Style Templates selection (NexoraX Hybrid Blend) */}
                  <div className="space-y-3 border-t border-white/5 pt-4">
                    <label className="text-[11px] font-mono text-cyan-400 uppercase tracking-wider block font-bold">Default Promo Style Templates (Hybrid Blend Option)</label>
                    <p className="text-[10px] text-zinc-550 uppercase font-mono mt-0.5">Select templates to group-blend for automated video generation events</p>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                      {styleTemplatesList.map((tpl) => {
                        const isChecked = selectedDefaultStyles.includes(tpl.name);
                        return (
                          <div 
                            key={tpl.id}
                            onClick={() => {
                              if (isChecked) {
                                if (selectedDefaultStyles.length > 1) {
                                  setSelectedDefaultStyles(prev => prev.filter(s => s !== tpl.name));
                                  showToast(`Removed default style: ${tpl.name}`);
                                } else {
                                  showToast("⚠️ Standard Rule: At least 1 template must be marked default.");
                                }
                              } else {
                                setSelectedDefaultStyles(prev => [...prev, tpl.name]);
                                showToast(`Added default style: ${tpl.name}`);
                              }
                            }}
                            className={`p-3 rounded-2xl border text-left cursor-pointer transition-all flex flex-col justify-between ${
                              isChecked 
                                ? 'bg-purple-950/20 border-purple-500/40 text-white' 
                                : 'bg-[#030611]/40 border-white/5 text-zinc-500 hover:text-zinc-300'
                            }`}
                          >
                            <div className="flex justify-between items-start">
                              <span className="text-sm bg-black/40 px-1.5 py-0.5 rounded border border-white/5">{tpl.thumbnail}</span>
                              <input 
                                type="checkbox" 
                                checked={isChecked}
                                readOnly
                                className="w-3.5 h-3.5 accent-purple-500 rounded focus:ring-0 pointer-events-none" 
                              />
                            </div>
                            <div className="mt-2.5">
                              <h4 className="text-[10px] font-sans font-extrabold uppercase tracking-wide text-white">{tpl.name}</h4>
                              <p className="text-[9px] text-zinc-400 font-sans tracking-tight line-clamp-1 mt-0.5">{tpl.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Toggle structures */}
                  <div className="space-y-3 pt-2 text-xs font-mono">
                    {[
                      { key: 'autoGenerateGuides', title: "Auto-Generate Guides On Change", desc: "Instantly draft step scripts when new sub-directory nodes are live." },
                      { key: 'autoGeneratePromos', title: "Auto-Generate Promo Drafts", desc: "Prepare social-ready 15s UGC video drafts upon hash changes." },
                      { key: 'requireApprovalBeforePublish', title: "Enforce Editorial Approval Gate", desc: "Halt automation dispatch until the Director approves drafts in the center." }
                    ].map((tog) => (
                      <div key={tog.key} className="flex justify-between items-center bg-black/20 p-3 rounded-xl border border-white/5">
                        <div className="space-y-0.5 text-left">
                          <h5 className="font-bold text-slate-300">{tog.title}</h5>
                          <span className="text-[10px] text-zinc-505 block">{tog.desc}</span>
                        </div>
                        <input 
                          type="checkbox" 
                          checked={(adminControls as any)[tog.key]} 
                          onChange={(e) => setAdminControls(prev => ({ ...prev, [tog.key]: e.target.checked }))}
                          className="w-4 h-4 rounded border-zinc-800 focus:ring-0 accent-purple-555 cursor-pointer"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Save button simulation */}
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={() => {
                        showToast("Branding configurations written to Local Cache. Realtime parameters applied instantly.");
                      }}
                      className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 font-mono font-bold text-xs uppercase cursor-pointer transition-all"
                    >
                      Save Configuration
                    </button>
                  </div>
                </div>

                {/* AUTOMATED SOCIAL PUBLISHING RULES CARD (Requested) */}
                <div className="bg-[#04081c]/60 border border-[#e840f3]/15 rounded-3xl p-6 space-y-4">
                  <div className="border-b border-white/5 pb-2.5">
                    <h3 className="text-sm font-mono font-black uppercase text-white tracking-widest flex items-center gap-1.5">
                      <Radio className="w-5 h-5 text-[#e840f3] animate-pulse" />
                      Fine-Grained Auto-Publish Rules
                    </h3>
                    <span className="text-[10px] font-mono text-purple-400 block uppercase">CONSTRAIN AUTONOMOUS API POSTS</span>
                  </div>

                  <div className="space-y-3.5 text-xs font-sans">
                    {[
                      { key: 'requireApproval', title: "Require Manual Director Approval (Recommended)", desc: "All synthesized video drafts sit in the Approval Matrix until confirmed by Lead Director." },
                      { key: 'autoPostSelectedPlatformsOnly', title: "Auto-Post Only to Selected Platforms", desc: "Instantly deploy to active excellent-health connections and keep offline ones in sandbox." },
                      { key: 'autoPostLowRiskOnly', title: "Auto-Post Only for Low-Risk Patches & Content", desc: "Minor blog or section edits auto-deploy immediately, bypassing queues." },
                      { key: 'neverAutoPostPricingLegal', title: "Halt Auto-Post on Pricing or Legal Reviews", desc: "Never allow automated triggers from /tiers or regulatory changes to deploy untested." },
                      { key: 'pauseIfContactDetailsMissing', title: "Pause Dispatch If Public Credentials Incomplete", desc: "Strictly checks brand safety settings. Freezes publish if booking or support paths are blank." },
                      { key: 'pauseIfQualityScoreBelowThreshold', title: "Pause Dispatch If Quality Score Is < 90%", desc: "Autonomous frames with low text overlap, low resolution indicators or clipping are parked." },
                      { key: 'pauseIfCaptionsCtaMissing', title: "Pause Dispatch If Platform Caption or CTA Missing", desc: "Ensures no blank descriptions get distributed to active channels." }
                    ].map((rule) => (
                      <div key={rule.key} className="flex justify-between items-start bg-black/35 p-3.5 rounded-xl border border-white/5 text-left">
                        <div className="space-y-1">
                          <h4 className="font-bold text-slate-200">{rule.title}</h4>
                          <p className="text-[10px] text-zinc-400 leading-normal">{rule.desc}</p>
                        </div>
                        <input 
                          type="checkbox" 
                          checked={(autoPublishRules as any)[rule.key]} 
                          onChange={(e) => setAutoPublishRules(prev => ({ ...prev, [rule.key]: e.target.checked }))}
                          className="w-4.5 h-4.5 rounded border-zinc-800 focus:ring-0 accent-purple-550 cursor-pointer mt-0.5"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* SOCIAL POSTING CONNECTOR DASHBOARD (Requested) */}
                <div className="bg-zinc-950/80 border border-cyan-500/15 rounded-3xl p-6 space-y-4">
                  <div className="border-b border-white/5 pb-2.5 flex justify-between items-center flex-wrap gap-2">
                    <div>
                      <h3 className="text-sm font-mono font-black uppercase text-white tracking-widest flex items-center gap-1.5">
                        <Layers className="w-5 h-5 text-cyan-400" />
                        Social Posting Connector Dashboard
                      </h3>
                      <span className="text-[10px] font-mono text-cyan-500 block uppercase">API STATUS AND OAUTH HEALTH CHANNELS</span>
                    </div>
                    <span className="text-[9px] font-mono bg-cyan-950 text-cyan-400 border border-cyan-500/20 px-2 py-0.5 rounded uppercase font-bold tracking-widest animate-pulse">
                      Live Connection Shield Active
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {connectors.map((conn) => {
                      // Color mapping according to health
                      const healthColor = 
                        conn.health === 'Excellent' ? 'text-emerald-400' :
                        conn.health === 'Good' ? 'text-cyan-400' :
                        conn.health === 'Critical' ? 'text-red-400 animate-pulse' :
                        'text-zinc-550';

                      // Status tag background style
                      const statusTagStyle = 
                        conn.status === 'Ready to publish' ? 'bg-emerald-950/35 text-emerald-400 border-emerald-500/20' :
                        conn.status === 'Permission missing' ? 'bg-amber-950/35 text-amber-450 border-amber-500/20' :
                        conn.status === 'Token expired' ? 'bg-red-950/35 text-red-400 border-red-500/20' :
                        'bg-zinc-900 text-zinc-400 border-zinc-800';

                      return (
                        <div 
                          key={conn.id}
                          className="bg-[#030612]/80 border border-white/5 rounded-2xl p-4 flex flex-col justify-between space-y-3 hover:border-cyan-500/20 transition-all"
                        >
                          {/* Title and stats header */}
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="text-xs font-sans font-black text-white">{conn.name}</h4>
                              <span className="text-[9px] font-mono text-zinc-500 block mt-0.5">Last update dispatched: {conn.lastPublished}</span>
                            </div>
                            <span className={`text-[8px] font-mono font-bold uppercase px-2 py-0.5 rounded border tracking-wide block ${statusTagStyle}`}>
                              {conn.status}
                            </span>
                          </div>

                          {/* Connection health parameter row */}
                          <div className="flex justify-between items-center text-[10px] font-mono bg-black/40 px-3 py-1.5 rounded-lg">
                            <span className="text-zinc-500">API Health Index:</span>
                            <strong className={`font-black uppercase tracking-wider ${healthColor}`}>{conn.health}</strong>
                          </div>

                          {/* ACTION BUTTON WRAPPER FOR CONNECTION INTERACTIVITY */}
                          <div className="grid grid-cols-2 gap-1.5 pt-1 text-[9px] font-mono">
                            <button 
                              onClick={() => {
                                showToast(`Initiating OAuth protocol handshake with ${conn.name} Server... API integration established!`);
                                setConnectors(prev => prev.map(c => c.id === conn.id ? { ...c, status: 'Ready to publish', health: 'Excellent', connected: true } : c));
                              }}
                              className="px-2 py-1.5 rounded-md bg-cyan-950/30 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-900/35 cursor-pointer uppercase font-bold text-center transition-all block"
                            >
                              Reconnect
                            </button>
                            <button 
                              onClick={() => {
                                showToast(`Dispatched test payload of 15s UGC clip to ${conn.name}... 200 OK. Connection functional.`);
                              }}
                              className="px-2 py-1.5 rounded-md bg-purple-950/30 text-purple-450 border border-purple-500/20 hover:bg-purple-900/35 cursor-pointer uppercase font-bold text-center transition-all block"
                            >
                              Test Connection
                            </button>
                            <button 
                              onClick={() => {
                                showToast(`Authorized Scopes for ${conn.name}: [publish_video, write_post, read_statistics, content_synthesis_sync]`);
                              }}
                              className="px-2 py-1.5 rounded-md bg-zinc-900 text-zinc-400 border border-zinc-800 hover:text-white cursor-pointer uppercase text-center transition-all block"
                            >
                              View Scopes
                            </button>
                            <button 
                              onClick={() => {
                                const actionType = conn.connected ? 'Disabled auto-publish' : 'Enabled auto-publish';
                                showToast(`${actionType} for ${conn.name} distribution channel.`);
                                setConnectors(prev => prev.map(c => c.id === conn.id ? { ...c, connected: !c.connected, status: c.connected ? 'Disabled' : 'Ready to publish' } : c));
                              }}
                              className={`px-2 py-1.5 rounded-md border cursor-pointer uppercase font-bold text-center transition-all block ${
                                conn.connected 
                                  ? "bg-red-950/20 text-red-400 border-red-500/25 hover:bg-red-900/10" 
                                  : "bg-emerald-950/20 text-emerald-400 border-emerald-500/25 hover:bg-emerald-900/10"
                              }`}
                            >
                              {conn.connected ? "Disable" : "Enable"} Auto-Post
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {/* TAB 7: NEXORAX GUIDE VIDEO FACTORY (NODE-07.5) */}
            {activeTab === 'factory' && (
              <motion.div
                key="factory-panel"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-6"
                id="nexorax-guide-video-factory"
              >
                {/* SUB HEADER BRAND STRIP */}
                <div className="bg-[#020512]/95 border border-purple-500/30 rounded-3xl p-6 relative overflow-hidden shadow-[0_0_20px_rgba(168,85,247,0.15)]">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
                  <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-mono tracking-widest text-[#00f0ff] uppercase bg-cyan-950/40 border border-cyan-500/30 px-2 py-0.5 rounded">
                          NODE-07.5 ENGINE ACTIVE
                        </span>
                        <span className="text-[10px] font-mono tracking-widest text-amber-500 uppercase bg-amber-950/40 border border-amber-500/30 px-2 py-0.5 rounded">
                          SAFE-SHIELD ENGAGED
                        </span>
                      </div>
                      <h2 className="text-xl font-sans font-black uppercase text-white tracking-tight">
                        NexoraX Guide Video Factory
                      </h2>
                      <p className="text-xs text-slate-350 mt-1 max-w-xl font-sans leading-relaxed">
                        Generate product walkthroughs, tutorials, captions, and social clips from one guide template.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2 shrink-0">
                      <button
                        id="btn-trigger-refresh-factory"
                        onClick={() => {
                          refreshPromoState();
                          showToast("Dynamic ledger synchronized.");
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-semibold font-mono uppercase bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-300 hover:text-white transition-all cursor-pointer"
                      >
                        <RefreshCw className="w-3 h-3 text-[#00f0ff]" />
                        Sync state
                      </button>
                    </div>
                  </div>

                  {/* MINI BAR NAV */}
                  <div className="flex flex-wrap gap-1 mt-6 pt-4 border-t border-white/5 font-mono text-[10px] uppercase font-bold tracking-wider">
                    {[
                      { id: 'dashboard', label: 'Overview Grid', icon: LayoutGrid },
                      { id: 'templates', label: '14 Product Templates', icon: Layers },
                      { id: 'studio', label: 'Script & Storyboard Studio', icon: FileText },
                      { id: 'render-queue', label: 'Renderer Queue', icon: Clock },
                      { id: 'approval-center', label: 'Approval Deck', icon: CheckSquare },
                      { id: 'social', label: 'Social Clips Pack', icon: Share2 }
                    ].map((st) => {
                      const SubIcon = st.icon;
                      const isSubActive = factoryActiveSubTab === st.id;
                      return (
                        <button
                          key={st.id}
                          id={`factory-nav-${st.id}`}
                          onClick={() => setFactoryActiveSubTab(st.id as any)}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all cursor-pointer ${
                            isSubActive
                              ? 'bg-purple-950/40 text-purple-400 border-purple-500/35 shadow-[0_0_10px_rgba(168,85,247,0.1)]'
                              : 'bg-black/30 text-zinc-400 border-transparent hover:border-zinc-850 hover:bg-zinc-950/30'
                          }`}
                        >
                          <SubIcon className="w-3.5 h-3.5" />
                          <span>{st.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 1. FACTORY DASHBOARD OVERVIEW GRID */}
                {factoryActiveSubTab === 'dashboard' && (
                  <motion.div
                    className="space-y-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    {/* METRICS ROW */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        {
                          id: 'card-metric-total',
                          label: 'Videos Generated',
                          value: rawVideos.filter(v => v.renderStatus === 'Render complete' || v.renderStatus === 'Approved' || v.renderStatus === 'Published').length,
                          desc: 'Renderer completions',
                          borderColor: 'border-purple-500/20'
                        },
                        {
                          id: 'card-metric-drafts',
                          label: 'Draft Scripts',
                          value: rawVideos.filter(v => v.script && v.publishedStatus !== 'Published').length,
                          desc: 'Platform custom briefs',
                          borderColor: 'border-cyan-500/20'
                        },
                        {
                          id: 'card-metric-queue',
                          label: 'Render Queue',
                          value: rawVideos.filter(v => v.renderStatus === 'Waiting for render' || v.renderStatus === 'Rendering').length,
                          desc: 'Jobs pending shunting',
                          borderColor: 'border-amber-500/20'
                        },
                        {
                          id: 'card-metric-approvals',
                          label: 'Awaiting Approval',
                          value: rawVideos.filter(v => v.approvalStatus === 'Pending' && v.script).length,
                          desc: 'Director checkpoints',
                          borderColor: 'border-purple-500/20'
                        },
                        {
                          id: 'card-metric-published',
                          label: 'Published Guides',
                          value: rawVideos.filter(v => v.publishedStatus === 'Published').length,
                          desc: 'Exposed to active readers',
                          borderColor: 'border-emerald-500/20'
                        },
                        {
                          id: 'card-metric-social',
                          label: 'Social Clips Ready',
                          value: rawVideos.filter(v => v.approvalStatus === 'Approved').length,
                          desc: 'Pushed to automation queue',
                          borderColor: 'border-blue-500/20'
                        },
                        {
                          id: 'card-metric-missing',
                          label: 'Missing Video Sources',
                          value: rawVideos.filter(v => !v.videoSrc).length,
                          desc: 'Video Coming Soon triggers',
                          borderColor: 'border-red-500/20'
                        },
                        {
                          id: 'card-metric-failed',
                          label: 'Failed Renders',
                          value: 0,
                          desc: '0% operational drag',
                          borderColor: 'border-zinc-800'
                        }
                      ].map((card, cIndex) => (
                        <div
                          key={card.id}
                          id={card.id}
                          className={`bg-[#030612]/70 border ${card.borderColor} p-4 rounded-2xl flex flex-col justify-between`}
                        >
                          <span className="text-[10px] font-mono text-zinc-400 block uppercase font-bold">{card.label}</span>
                          <div className="my-2.5">
                            <span className="text-2xl font-sans font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                              {card.value}
                            </span>
                          </div>
                          <span className="text-[9px] font-mono text-zinc-500 block">{card.desc}</span>
                        </div>
                      ))}
                    </div>

                    {/* QUICK ACTION STEERING MATRIX */}
                    <div className="p-6 bg-purple-950/10 border border-purple-550/20 rounded-3xl space-y-4">
                      <div>
                        <h3 className="text-sm font-sans font-black uppercase text-white">Central Operations Launcher</h3>
                        <p className="text-[10.5px] font-mono text-purple-400 mt-0.5">DIRECT DIRECTIVES FROM THE OFFICE OF THE DIRECTOR</p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        <button
                          id="btn-quick-generate-script"
                          onClick={() => {
                            setFactoryActiveSubTab('studio');
                            showToast("Select target product and click compile script.");
                          }}
                          className="flex items-center gap-2.5 p-3 rounded-xl bg-[#090b1e] border border-cyan-500/20 hover:border-cyan-500/50 hover:bg-cyan-950/10 transition-all text-left group cursor-pointer"
                        >
                          <div className="w-8 h-8 rounded-lg bg-cyan-950/40 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                            <Sparkles className="w-4 h-4" />
                          </div>
                          <div>
                            <span className="text-xs font-sans font-bold text-white group-hover:text-cyan-400 block transition-colors">Generate Guide Video</span>
                            <span className="text-[9px] font-mono text-zinc-500 uppercase">Construct brand scripts</span>
                          </div>
                        </button>

                        <button
                          id="btn-quick-create-walkthrough"
                          onClick={() => {
                            setNewTemplateForm(prev => ({ ...prev, videoMode: 'Full Walkthrough' }));
                            setFactoryActiveSubTab('templates');
                            showToast("Walkthrough schema primed. Select product template.");
                          }}
                          className="flex items-center gap-2.5 p-3 rounded-xl bg-[#090b1e] border border-purple-500/20 hover:border-purple-500/50 hover:bg-purple-950/10 transition-all text-left group cursor-pointer"
                        >
                          <div className="w-8 h-8 rounded-lg bg-purple-950/40 border border-purple-500/30 flex items-center justify-center text-purple-400">
                            <Layers className="w-4 h-4" />
                          </div>
                          <div>
                            <span className="text-xs font-sans font-bold text-white group-hover:text-purple-400 block transition-colors">Create Product Walkthrough</span>
                            <span className="text-[9px] font-mono text-zinc-500 uppercase">Full high-fidelity format</span>
                          </div>
                        </button>

                        <button
                          id="btn-quick-create-clip"
                          onClick={() => {
                            setNewTemplateForm(prev => ({ ...prev, videoMode: 'Social Clip' }));
                            setFactoryActiveSubTab('templates');
                            showToast("Social Clip preset configuration active.");
                          }}
                          className="flex items-center gap-2.5 p-3 rounded-xl bg-[#090b1e] border border-amber-500/20 hover:border-amber-500/50 hover:bg-amber-950/10 transition-all text-left group cursor-pointer"
                        >
                          <div className="w-8 h-8 rounded-lg bg-amber-950/40 border border-amber-500/30 flex items-center justify-center text-amber-400">
                            <Radio className="w-4 h-4" />
                          </div>
                          <div>
                            <span className="text-xs font-sans font-bold text-white group-hover:text-amber-400 block transition-colors">Create Short Clip</span>
                            <span className="text-[9px] font-mono text-zinc-500 uppercase">15-30s micro conversions</span>
                          </div>
                        </button>

                        <button
                          id="btn-quick-create-thumbnail"
                          onClick={() => {
                            setFactoryActiveSubTab('studio');
                            showToast("Enter studio config, generate captions & graphic descriptions.");
                          }}
                          className="flex items-center gap-2.5 p-3 rounded-xl bg-[#090b1e] border border-blue-500/20 hover:border-blue-500/50 hover:bg-blue-950/10 transition-all text-left group cursor-pointer"
                        >
                          <div className="w-8 h-8 rounded-lg bg-blue-950/40 border border-blue-500/30 flex items-center justify-center text-blue-400">
                            <Tv className="w-4 h-4" />
                          </div>
                          <div>
                            <span className="text-xs font-sans font-bold text-white group-hover:text-blue-400 block transition-colors">Create Thumbnail Prompt</span>
                            <span className="text-[9px] font-mono text-zinc-500 uppercase">Midjourney / Octane graphic shunts</span>
                          </div>
                        </button>

                        <button
                          id="btn-quick-open-approval"
                          onClick={() => {
                            setFactoryActiveSubTab('approval-center');
                            showToast("Review center active. Confirm public safe alignment.");
                          }}
                          className="flex items-center gap-2.5 p-3 rounded-xl bg-[#090b1e] border border-emerald-500/20 hover:border-emerald-500/50 hover:bg-emerald-950/10 transition-all text-left group cursor-pointer"
                        >
                          <div className="w-8 h-8 rounded-lg bg-emerald-950/40 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                            <CheckSquare className="w-4 h-4" />
                          </div>
                          <div>
                            <span className="text-xs font-sans font-bold text-white group-hover:text-emerald-400 block transition-colors">Open Approval Queue</span>
                            <span className="text-[9px] font-mono text-zinc-500 uppercase">Confirm compliance</span>
                          </div>
                        </button>

                        <button
                          id="btn-quick-publish-approved"
                          onClick={() => {
                            setFactoryActiveSubTab('render-queue');
                            showToast("View render log database and approve shunts.");
                          }}
                          className="flex items-center gap-2.5 p-3 rounded-xl bg-[#090b1e] border border-zinc-850 hover:border-zinc-700 hover:bg-zinc-950/30 transition-all text-left group cursor-pointer"
                        >
                          <div className="w-8 h-8 rounded-lg bg-zinc-900 border border-zinc-850 flex items-center justify-center text-zinc-400">
                            <ArrowRight className="w-4 h-4" />
                          </div>
                          <div>
                            <span className="text-xs font-sans font-bold text-white block">Publish Approved Video</span>
                            <span className="text-[9px] font-mono text-zinc-500 uppercase">Expose live instantly</span>
                          </div>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* 2. PRODUCT VIDEO TEMPLATES SELECTOR (14 MAJOR PRODUCTS) */}
                {factoryActiveSubTab === 'templates' && (
                  <motion.div
                    className="space-y-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="bg-[#030612]/90 border border-purple-550/25 p-6 rounded-3xl space-y-4">
                      <div className="border-b border-white/5 pb-3 flex justify-between items-center">
                        <div>
                          <h3 className="text-sm font-sans font-black uppercase text-white">Platform Template Grid</h3>
                          <p className="text-[10px] font-mono text-slate-400 mt-0.5">PROVISION VIDEO WALKTHROUGHS FOR 14 NEXORAX UTILITIES</p>
                        </div>
                        <span className="text-[10px] font-mono bg-purple-950/30 text-purple-400 px-2 py-0.5 rounded border border-purple-500/20">
                          14 Templates Synced
                        </span>
                      </div>

                      {/* SELECT DROPDOWN */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1.5 font-mono text-xs">
                          <label className="text-slate-400 uppercase font-black block">Active Product Template Target</label>
                          <select
                            id="factory-product-select"
                            value={selectedProductTemplate}
                            onChange={(e) => {
                              setSelectedProductTemplate(e.target.value);
                              // Auto sync template properties to input form
                              const matched = rawVideos.find(v => v.product === e.target.value);
                              if (matched) {
                                setNewTemplateForm({
                                  product: matched.product,
                                  title: matched.title,
                                  category: matched.category,
                                  targetAudience: matched.targetAudience,
                                  difficulty: matched.difficulty,
                                  durationTarget: matched.durationTarget,
                                  videoMode: matched.videoMode,
                                  ctaLabel: matched.ctaLabel,
                                  ctaTarget: matched.ctaTarget
                                });
                              }
                            }}
                            className="w-full bg-black border border-purple-550/20 rounded-xl px-3 py-2.5 text-white font-sans focus:outline-none focus:border-purple-400 transition-all font-semibold cursor-pointer"
                          >
                            {[
                              'Nexus Search', 'Nexus Hosting', 'Mail & Domains', 'Nexus Chat',
                              'AI Automation', 'Social Media Automation', 'Voice Matrix', 'WorkSuite',
                              'Media Studio', 'Billing & Subscriptions', 'Privacy & Consent', 'Galaxy Lab',
                              'Marketplace', 'Client Dashboard'
                            ].map(prodName => (
                              <option key={prodName} value={prodName}>{prodName}</option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-1.5 font-mono text-xs">
                          <label className="text-[#00f0ff] uppercase font-black block">Generation Status Indicator</label>
                          <div className="w-full bg-black/40 border border-zinc-800 rounded-xl px-3 py-2.5 flex items-center justify-between font-sans">
                            {(() => {
                              const currentVid = rawVideos.find(v => v.product === selectedProductTemplate);
                              if (!currentVid) return <span className="text-zinc-500 text-xs font-mono">Unregistered template</span>;
                              return (
                                <>
                                  <span className="text-xs font-bold text-white">{currentVid.renderStatus}</span>
                                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded uppercase border ${
                                    currentVid.approvalStatus === 'Approved' ? 'bg-emerald-950/25 text-emerald-400 border-emerald-500/20' : 'bg-amber-950/25 text-amber-500 border-amber-500/20'
                                  }`}>
                                    Checks: {currentVid.approvalStatus}
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* COMPREHENSIVE CURRENT SPEC */}
                      {(() => {
                        const videoSpec = rawVideos.find(v => v.product === selectedProductTemplate);
                        if (!videoSpec) return null;
                        return (
                          <div className="bg-black/40 p-5 rounded-2xl border border-white/5 space-y-4 font-sans text-xs">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-left font-mono">
                              <div>
                                <span className="text-zinc-500 block uppercase text-[8.5px]">Product Name</span>
                                <span className="text-white font-bold">{videoSpec.product}</span>
                              </div>
                              <div>
                                <span className="text-zinc-500 block uppercase text-[8.5px]">Target Audience</span>
                                <span className="text-purple-300 font-bold">{videoSpec.targetAudience}</span>
                              </div>
                              <div>
                                <span className="text-zinc-500 block uppercase text-[8.5px]">Duration target</span>
                                <span className="text-amber-400 font-bold">{videoSpec.durationTarget}</span>
                              </div>
                              <div>
                                <span className="text-zinc-500 block uppercase text-[8.5px]">Difficulty level</span>
                                <span className="text-cyan-400 font-bold">{videoSpec.difficulty}</span>
                              </div>
                            </div>

                            <div className="text-left space-y-1.5 border-t border-white/5 pt-3">
                              <span className="text-zinc-500 font-mono block uppercase text-[8.5px]">Video Title</span>
                              <p className="text-sm text-white font-black leading-snug">{videoSpec.title}</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left font-mono border-t border-white/5 pt-3 text-[11px]">
                              <div>
                                <span className="text-zinc-500 block uppercase text-[8.5px]">Call To Action label</span>
                                <span className="text-[#00f0ff] font-bold">{videoSpec.ctaLabel || "Learn More"}</span>
                              </div>
                              <div>
                                <span className="text-zinc-500 block uppercase text-[8.5px]">Call To Action target shunt</span>
                                <span className="text-white font-bold text-[9.5px] break-all">{videoSpec.ctaTarget || "/home"}</span>
                              </div>
                            </div>

                            <div className="pt-3 border-t border-white/5 flex gap-2">
                              <button
                                id="btn-templates-modify"
                                onClick={() => {
                                  setFactoryActiveSubTab('studio');
                                  showToast(`Lunched ${videoSpec.product} spec inside Studio.`);
                                }}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold uppercase tracking-wider text-[10px] items-center gap-1.5 flex transition-all cursor-pointer shadow-lg"
                              >
                                <Sparkles className="w-3.5 h-3.5" />
                                Run Compiler Studio
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* REGISTER NEW WALKTHROUGH SCHEMAS */}
                    <div className="bg-[#030612]/90 border border-zinc-850 p-6 rounded-3xl space-y-4">
                      <div>
                        <h4 className="text-sm font-sans font-black uppercase text-white">Lodge Custom Walkthrough Spec</h4>
                        <span className="text-[10px] font-mono text-zinc-500 block mt-0.5">APPENDS A CUSTOM RECORD MATRIX SYNCED TO HOST CLUSTERS</span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 font-mono text-[10.5px]">
                        <div className="space-y-1">
                          <label className="text-slate-450 block uppercase">Product Core</label>
                          <input
                            id="custom-input-product"
                            type="text"
                            value={newTemplateForm.product}
                            onChange={(e) => setNewTemplateForm(prev => ({ ...prev, product: e.target.value }))}
                            className="w-full bg-black/60 border border-zinc-800 rounded-lg px-2.5 py-2 text-white font-sans focus:outline-none focus:border-purple-400"
                            placeholder="e.g. Galaxy Lab"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-slate-450 block uppercase">Video Guide Title</label>
                          <input
                            id="custom-input-title"
                            type="text"
                            value={newTemplateForm.title}
                            onChange={(e) => setNewTemplateForm(prev => ({ ...prev, title: e.target.value }))}
                            className="w-full bg-black/60 border border-zinc-800 rounded-lg px-2.5 py-2 text-white font-sans focus:outline-none focus:border-purple-400"
                            placeholder="e.g. Advanced Quantum Analysis"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-slate-450 block uppercase">Target Audience</label>
                          <input
                            id="custom-input-audience"
                            type="text"
                            value={newTemplateForm.targetAudience}
                            onChange={(e) => setNewTemplateForm(prev => ({ ...prev, targetAudience: e.target.value }))}
                            className="w-full bg-black/60 border border-zinc-800 rounded-lg px-2.5 py-2 text-white font-sans focus:outline-none focus:border-purple-400"
                            placeholder="System analysts"
                          />
                        </div>
                      </div>

                      <div className="pt-3 border-t border-white/5 text-right">
                        <button
                          id="btn-register-custom-template"
                          onClick={async () => {
                            if (!newTemplateForm.product || !newTemplateForm.title) {
                              showToast("❌ Product and Video Title are mandatory.");
                              return;
                            }
                            try {
                              const res = await fetch('/api/guides/video-template', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify(newTemplateForm)
                              });
                              if (res.ok) {
                                showToast(`✅ Spec logged cleanly on server!`);
                                setSelectedProductTemplate(newTemplateForm.product);
                                refreshPromoState();
                              }
                            } catch (err: any) {
                              showToast("❌ Connection blocked by environment proxy configuration.");
                            }
                          }}
                          className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-700 text-white hover:from-emerald-500 hover:to-teal-600 text-[10px] font-bold font-mono uppercase tracking-wider rounded-lg transition-all cursor-pointer shadow-lg"
                        >
                          Register walkthrough ledger record
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* 3 & 4 & 5. VIDEO STUDIO: SCRIPT & STORYBOARD & CAPTIONS */}
                {factoryActiveSubTab === 'studio' && (
                  <motion.div
                    className="space-y-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    {/* STUDIO HEADER */}
                    <div className="p-5 bg-black/85 border border-purple-550/20 rounded-3xl text-left space-y-3 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl pointer-events-none" />
                      <div className="flex justify-between items-center flex-wrap gap-2">
                        <div>
                          <span className="text-[10px] font-mono uppercase text-[#00f0ff] font-black">Interactive compiler</span>
                          <h3 className="text-base font-sans font-black text-white uppercase">Automated Walkthrough Synthesizer</h3>
                        </div>
                        <div className="flex gap-2 font-mono text-[9px]">
                          <span className="bg-zinc-950 border border-zinc-800 px-2 py-1 text-slate-400 rounded">TARGET: {selectedProductTemplate}</span>
                        </div>
                      </div>

                      {/* MODE SWITCHER */}
                      <div className="grid grid-cols-3 gap-2 bg-zinc-950/30 p-1.5 rounded-xl border border-white/5 text-center font-mono text-[9.5px] uppercase font-bold tracking-wider">
                        {[
                          { id: 'Full Walkthrough', label: 'Full Guide (2–4m)', format: 'Website 16:9' },
                          { id: 'Short Explainer', label: 'Explainer (45–60s)', format: 'Shorts 9:16' },
                          { id: 'Social Clip', label: 'Social Hook (15–30s)', format: 'LinkedIn 1:1' }
                        ].map((modeItem) => {
                          const activeVid = rawVideos.find(v => v.product === selectedProductTemplate);
                          const isSel = activeVid?.videoMode === modeItem.id;
                          return (
                            <button
                              key={modeItem.id}
                              id={`btn-mode-select-${modeItem.id.replace(/\s+/g, '')}`}
                              onClick={async () => {
                                if (!activeVid) return;
                                try {
                                  const res = await fetch('/api/guides/video-template', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ ...activeVid, videoMode: modeItem.id })
                                  });
                                  if (res.ok) {
                                    showToast(`Changed target format output: ${modeItem.label}`);
                                    refreshPromoState();
                                  }
                                } catch (_) {}
                              }}
                              className={`py-2 rounded-lg cursor-pointer transition-all ${
                                isSel 
                                  ? 'bg-purple-950/40 text-purple-400 border border-purple-500/30' 
                                  : 'text-zinc-500 hover:text-zinc-300'
                              }`}
                            >
                              <span className="block">{modeItem.label}</span>
                              <span className="text-[7.5px] text-zinc-500 lowercase">({modeItem.format})</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* CONCURRENT SYSTEM GENERATION PANEL */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 pt-2">
                        <button
                          id="btn-trigger-script-gen"
                          disabled={isCompilingScript}
                          onClick={async () => {
                            const currentTarget = rawVideos.find(v => v.product === selectedProductTemplate);
                            if (!currentTarget) return;
                            setIsCompilingScript(true);
                            try {
                              const res = await fetch('/api/guides/generate-script', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ videoId: currentTarget.id })
                              });
                              if (res.ok) {
                                const data = await res.json();
                                showToast(`✅ Guide script formulated successfully!`);
                                refreshPromoState();
                              }
                            } catch (_) {
                              showToast("❌ Script compiling error, executing fallback.");
                            } finally {
                              setIsCompilingScript(false);
                            }
                          }}
                          className="w-full flex items-center justify-center gap-2 py-3 bg-cyan-950/20 hover:bg-cyan-950/40 border border-cyan-500/25 text-cyan-400 font-mono text-[10px] font-bold uppercase rounded-xl transition-all cursor-pointer"
                        >
                          <Sparkles className={`w-3.5 h-3.5 ${isCompilingScript ? 'animate-spin' : ''}`} />
                          {isCompilingScript ? "Compiling script..." : "1. Register/Compile Script"}
                        </button>

                        <button
                          id="btn-trigger-storyboard-gen"
                          disabled={isCompilingStoryboard}
                          onClick={async () => {
                            const currentTarget = rawVideos.find(v => v.product === selectedProductTemplate);
                            if (!currentTarget) return;
                            if (!currentTarget.script) {
                              showToast("⚠️ Compile a script matrix first.");
                              return;
                            }
                            setIsCompilingStoryboard(true);
                            try {
                              const res = await fetch('/api/guides/generate-storyboard', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ videoId: currentTarget.id })
                              });
                              if (res.ok) {
                                showToast("✅ Visual Storyboard mapped successfully!");
                                refreshPromoState();
                              }
                            } catch (_) {
                              showToast("❌ Storyboard compiling fallback executed.");
                            } finally {
                              setIsCompilingStoryboard(false);
                            }
                          }}
                          className="w-full flex items-center justify-center gap-2 py-3 bg-purple-950/20 hover:bg-purple-950/40 border border-purple-500/25 text-purple-400 font-mono text-[10px] font-bold uppercase rounded-xl transition-all cursor-pointer"
                        >
                          <Layers className={`w-3.5 h-3.5 ${isCompilingStoryboard ? 'animate-spin' : ''}`} />
                          {isCompilingStoryboard ? "Mapping scenes..." : "2. Map Storyboard Scenes"}
                        </button>

                        <button
                          id="btn-trigger-captions-gen"
                          disabled={isCompilingCaptions}
                          onClick={async () => {
                            const currentTarget = rawVideos.find(v => v.product === selectedProductTemplate);
                            if (!currentTarget) return;
                            if (!currentTarget.script) {
                              showToast("⚠️ Compile script first.");
                              return;
                            }
                            setIsCompilingCaptions(true);
                            try {
                              const res = await fetch('/api/guides/generate-captions', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ videoId: currentTarget.id })
                              });
                              if (res.ok) {
                                showToast("✅ Timed Subtitles & Graphic Prompt formulated!");
                                refreshPromoState();
                              }
                            } catch (_) {} finally {
                              setIsCompilingCaptions(false);
                            }
                          }}
                          className="w-full flex items-center justify-center gap-2 py-3 bg-amber-950/20 hover:bg-amber-950/40 border border-amber-500/25 text-amber-400 font-mono text-[10px] font-bold uppercase rounded-xl transition-all cursor-pointer"
                        >
                          <Radio className={`w-3.5 h-3.5 ${isCompilingCaptions ? 'animate-spin' : ''}`} />
                          {isCompilingCaptions ? "Formulating subtitles..." : "3. Formulate Subtitles"}
                        </button>
                      </div>
                    </div>

                    {/* TWO-COLUMN SCRIPT & STORYBOARD PREVIEW PANEL */}
                    {(() => {
                      const activeVid = rawVideos.find(v => v.product === selectedProductTemplate);
                      if (!activeVid) return null;
                      return (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-left">
                          
                          {/* SCRIPT DISPLAY */}
                          <div className="bg-[#030612]/90 border border-white/5 rounded-3xl p-5 space-y-4">
                            <div className="border-b border-white/5 pb-2.5 flex justify-between items-center">
                              <div>
                                <h4 className="text-xs font-sans font-black text-white uppercase">Generated Walkthrough Script</h4>
                                <span className="text-[8.5px] font-mono text-zinc-500 uppercase">Platform Tone Shield: active</span>
                              </div>
                              <span className="text-[8.5px] font-mono text-cyan-400 bg-cyan-950/30 border border-cyan-500/20 px-2 py-0.5 rounded uppercase">
                                Verified Public-Safe
                              </span>
                            </div>

                            {activeVid.script ? (
                              <div className="space-y-3 text-xs leading-relaxed font-sans max-h-[350px] overflow-y-auto pr-1">
                                {[
                                  { label: 'Hook Segment', text: activeVid.script.hook, color: 'text-purple-400' },
                                  { label: 'Introduction Block', text: activeVid.script.intro, color: 'text-white' },
                                  { label: 'Problem Brief', text: activeVid.script.problem, color: 'text-red-300' },
                                  { label: 'Interface Walkthrough', text: activeVid.script.walkthrough, color: 'text-zinc-300' },
                                  { label: 'Process Steps', text: activeVid.script.stepByStep, color: 'text-cyan-300' },
                                  { label: 'Product Benefits', text: activeVid.script.benefits, color: 'text-slate-350' },
                                  { label: 'Call To Action', text: activeVid.script.cta, color: 'text-amber-400 font-bold' },
                                  { label: 'Director Closing', text: activeVid.script.closing, color: 'text-emerald-400 font-bold' }
                                ].map((seg, sIdx) => (
                                  <div key={sIdx} className="bg-zinc-950/40 p-3 rounded-xl border border-white/5 space-y-1">
                                    <span className="text-[8px] font-mono uppercase text-zinc-500 block">{seg.label}</span>
                                    <p className={seg.color}>"{seg.text}"</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="py-12 text-center text-zinc-500 font-mono text-xs">
                                No script compiled yet. Trigger "Register/Compile Script" to begin.
                              </div>
                            )}
                          </div>

                          {/* STORYBOARD & IMAGES */}
                          <div className="bg-[#030612]/90 border border-white/5 rounded-3xl p-5 space-y-4">
                            <div className="border-b border-white/5 pb-2.5 flex justify-between items-center">
                              <div>
                                <h4 className="text-xs font-sans font-black text-white uppercase">Visual Scene Storyboard</h4>
                                <span className="text-[8.5px] font-mono text-zinc-500 uppercase">Sequenced Frame Directives</span>
                              </div>
                              <span className="text-[8.5px] font-mono text-purple-400 bg-purple-950/30 border border-purple-500/20 px-2 py-0.5 rounded uppercase">
                                Glassmorphism Style
                              </span>
                            </div>

                            {activeVid.storyboard ? (
                              <div className="space-y-4 max-h-[350px] overflow-y-auto pr-1">
                                {activeVid.storyboard.map((scene: any, index: number) => (
                                  <div key={index} className="bg-zinc-950/40 p-3.5 rounded-xl border border-white/5 text-xs text-left space-y-2">
                                    <div className="flex justify-between items-center font-mono text-[8px] uppercase border-b border-white/5 pb-1.5 mb-1.5">
                                      <span className="text-purple-400 font-black">Scene {scene.sceneNumber || index + 1} - {scene.title}</span>
                                      <span className="text-zinc-500">Duration: {scene.duration}</span>
                                    </div>
                                    <p className="text-zinc-350"><strong className="text-white uppercase font-mono text-[9px] block">Visual Setup:</strong> {scene.visual}</p>
                                    <p className="text-zinc-300 italic"><strong className="text-slate-400 uppercase font-mono text-[9px] block">Voiceover Voice:</strong> "{scene.voiceover}"</p>
                                    <div className="grid grid-cols-2 gap-2 text-[8px] font-mono text-zinc-500 pt-1">
                                      <span>On-screen: <strong className="text-cyan-400">{scene.text}</strong></span>
                                      <span>UI Context: <strong className="text-amber-400">{scene.uiReference}</strong></span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="py-12 text-center text-zinc-500 font-mono text-xs">
                                No visual scene storyboard generated yet. Configure a script then map the storyboard.
                              </div>
                            )}
                          </div>

                        </div>
                      );
                    })()}
                  </motion.div>
                )}

                {/* 6. RENDERER QUEUE DETAILS */}
                {factoryActiveSubTab === 'render-queue' && (
                  <motion.div
                    className="space-y-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="p-6 bg-black/85 border border-purple-550/20 rounded-3xl space-y-4 text-left">
                      <div className="border-b border-white/5 pb-3">
                        <span className="text-[10px] font-mono uppercase text-[#00f0ff] font-black block">Active Render Core Status</span>
                        <h3 className="text-sm font-sans font-black text-white uppercase">NexoraX Cloud Synthesizer System</h3>
                      </div>

                      {/* ALWAYS ON SAFETY ALERT */}
                      <div className="p-4 bg-amber-950/15 border border-amber-500/25 rounded-2xl flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-xs font-sans font-black text-amber-400 uppercase">Render engine not connected yet</h4>
                          <p className="text-[10px] font-mono text-zinc-300 mt-1 leading-relaxed">
                            Script, storyboard, captions, and thumbnail prompt are ready. System safe (No crashing, no backend logs).
                            The central video synthesizer remains isolated. When fully shunted, containers automatically compile.
                          </p>
                        </div>
                      </div>

                      {/* ACTIVE QUEUE LOG LIST */}
                      <div className="space-y-2.5">
                        <span className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest block">Active ledger compilation logs (Platform Database)</span>
                        
                        <div className="space-y-2 text-xs font-mono">
                          {rawVideos.map((job) => {
                            const isRendering = job.renderStatus === 'Waiting for render' || job.renderStatus === 'Rendering';
                            return (
                              <div
                                key={job.id}
                                className="bg-[#030612]/80 border border-white/5 rounded-xl p-3 flex flex-col md:flex-row justify-between items-start md:items-center gap-3"
                              >
                                <div>
                                  <span className="text-zinc-500 uppercase text-[8px] block">JOB-{job.id}</span>
                                  <strong className="text-white text-[11.5px] font-sans leading-snug">{job.product} / {job.title}</strong>
                                </div>

                                <div className="flex items-center gap-3 shrink-0">
                                  <span className={`text-[8.5px] font-bold px-2.5 py-0.5 rounded uppercase border ${
                                    job.renderStatus === 'Render complete' ? 'bg-emerald-950/25 text-emerald-400 border-emerald-500/20' :
                                    isRendering ? 'bg-cyan-950 text-cyan-400 border-cyan-500/20 animate-pulse' :
                                    'bg-zinc-950 border-zinc-800 text-zinc-400'
                                  }`}>
                                    {job.renderStatus}
                                  </span>

                                  <button
                                    id={`btn-render-job-${job.id}`}
                                    disabled={isRendering || job.renderStatus === 'Render complete'}
                                    onClick={async () => {
                                      showToast(`Queueing video generate job on cluster node...`);
                                      try {
                                        const res = await fetch('/api/guides/create-render-job', {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ videoId: job.id })
                                        });
                                        if (res.ok) {
                                          showToast(`Container started. Refresh queue in moments.`);
                                          refreshPromoState();
                                        }
                                      } catch (_) {}
                                    }}
                                    className={`px-3 py-1.5 rounded-lg font-bold uppercase text-[9px] transition-all cursor-pointer ${
                                      job.renderStatus === 'Render complete'
                                        ? "bg-zinc-900 border border-zinc-800 text-zinc-500 cursor-not-allowed"
                                        : "bg-purple-600 hover:bg-purple-700 text-white shadow-md"
                                    }`}
                                  >
                                    Trigger Render Job
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* 7. APPROVAL CENTER */}
                {factoryActiveSubTab === 'approval-center' && (
                  <motion.div
                    className="space-y-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="bg-[#030612]/90 border border-purple-550/25 p-5 rounded-3xl space-y-4 text-left">
                      <div className="border-b border-white/5 pb-2.5">
                        <span className="text-[10px] font-mono uppercase text-purple-400 font-extrabold block">Director Checkpoint Ledger</span>
                        <h3 className="text-sm font-sans font-black text-white uppercase">Walkthrough Audit Center</h3>
                        <p className="text-[10.5px] font-mono text-slate-400 mt-1">IMPORTANT: DO NOT AUTO-PUBLISH VIDEOS WITHOUT ADMIN APPROVAL</p>
                      </div>

                      <div className="space-y-3">
                        {rawVideos.filter(v => v.renderStatus !== 'Published').map((appr) => (
                          <div
                            key={appr.id}
                            className="p-4 bg-black/40 border border-white/5 rounded-2xl text-xs space-y-3 hover:border-purple-500/25 transition-all text-left"
                          >
                            <div className="flex justify-between items-start flex-wrap gap-2 border-b border-white/5 pb-2">
                              <div>
                                <span className="text-[8px] font-mono uppercase text-[#00f0ff] font-bold bg-cyan-950/40 px-2 py-0.5 rounded border border-cyan-500/20">
                                  {appr.product}
                                </span>
                                <h4 className="text-xs font-sans font-black text-white mt-1 leading-snug">{appr.title}</h4>
                              </div>
                              <span className={`text-[8.5px] font-mono uppercase px-2 py-0.5 rounded border ${
                                appr.approvalStatus === 'Approved' ? 'bg-emerald-950/25 text-emerald-400 border-emerald-500/20' : 'bg-red-950/25 text-red-400 border-red-500/20'
                              }`}>
                                Approval Target: {appr.approvalStatus}
                              </span>
                            </div>

                            {/* AUDITED SECTORS */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[11px] font-sans">
                              <div className="space-y-1 bg-zinc-950/40 p-3 rounded-lg border border-white/5 max-h-[100px] overflow-y-auto">
                                <span className="text-zinc-500 text-[8px] font-mono uppercase block">Script Walkthrough Preview</span>
                                <p className="text-zinc-300 italic">"{appr.script?.hook || "No script written yet."}"</p>
                              </div>

                              <div className="space-y-1 bg-zinc-950/40 p-3 rounded-lg border border-white/5">
                                <span className="text-zinc-500 text-[8px] font-mono uppercase block">Compliance Checklist</span>
                                <div className="space-y-1 text-slate-350 text-[10px] font-mono">
                                  <div className="flex items-center gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                                    <span>No sensitive banking/UTR data</span>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                                    <span>Public-safe product wording</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* AUDIT ACTIONS */}
                            <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5 justify-end">
                              <button
                                id={`btn-approve-video-${appr.id}`}
                                onClick={async () => {
                                  try {
                                    const res = await fetch('/api/guides/approve-video', {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ videoId: appr.id, action: "Approve" })
                                    });
                                    if (res.ok) {
                                      showToast("Video check confirmed. Approved!");
                                      refreshPromoState();
                                    }
                                  } catch (_) {}
                                }}
                                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold font-mono uppercase text-[9.5px] cursor-pointer"
                              >
                                Approve Video
                              </button>

                              <button
                                id={`btn-reject-video-${appr.id}`}
                                onClick={async () => {
                                  try {
                                    const res = await fetch('/api/guides/approve-video', {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ videoId: appr.id, action: "Request Edits" })
                                    });
                                    if (res.ok) {
                                      showToast("Platform feedback dispatched. Status set to request edits.");
                                      refreshPromoState();
                                    }
                                  } catch (_) {}
                                }}
                                className="px-3 py-1.5 bg-red-900/60 hover:bg-red-800 text-red-200 border border-red-500/25 rounded-lg font-bold font-mono uppercase text-[9.5px] cursor-pointer"
                              >
                                Request Edits
                              </button>

                              <button
                                id={`btn-publish-guide-${appr.id}`}
                                disabled={appr.approvalStatus !== 'Approved'}
                                onClick={async () => {
                                  try {
                                    const res = await fetch('/api/guides/publish-video', {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ videoId: appr.id })
                                    });
                                    if (res.ok) {
                                      showToast("🎉 Published instantly into Live Guide Video Library!");
                                      refreshPromoState();
                                    }
                                  } catch (err: any) {
                                    showToast("⚠️ Video must be approved before layout publishing.");
                                  }
                                }}
                                className={`px-3 py-1.5 rounded-lg font-bold font-mono uppercase text-[9.5px] transition-all cursor-pointer ${
                                  appr.approvalStatus === 'Approved'
                                    ? "bg-cyan-600 hover:bg-cyan-700 text-white"
                                    : "bg-zinc-900 border border-zinc-800 text-zinc-500 cursor-not-allowed"
                                }`}
                              >
                                Publish to Guides and Videos
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* 9. SOCIAL CLIP GENERATOR */}
                {factoryActiveSubTab === 'social' && (
                  <motion.div
                    className="space-y-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="bg-[#030612]/90 border border-purple-550/25 p-5 rounded-3xl space-y-4 text-left">
                      <div className="border-b border-white/5 pb-2.5 flex justify-between items-center flex-wrap gap-2">
                        <div>
                          <h3 className="text-sm font-sans font-black text-white uppercase flex items-center gap-2">
                            <Share2 className="w-4 h-4 text-purple-400" />
                            UGC Clip Formulations
                          </h3>
                          <span className="text-[10px] font-mono text-zinc-500 block uppercase">EXTRACT MULTIPLEX PROMOTION CLIPS DISPATCHABLE TO SOC-AUTO QUEUES</span>
                        </div>
                        <select
                          id="social-target-select"
                          value={selectedProductTemplate}
                          onChange={(e) => setSelectedProductTemplate(e.target.value)}
                          className="bg-black text-[10px] font-mono border border-purple-500/25 py-1 px-2.5 rounded text-white"
                        >
                          {rawVideos.map(v => (
                            <option key={v.id} value={v.product}>{v.product}</option>
                          ))}
                        </select>
                      </div>

                      {(() => {
                        const targetVid = rawVideos.find(v => v.product === selectedProductTemplate);
                        if (!targetVid) return null;
                        
                        return (
                          <div className="space-y-4 text-xs font-sans text-left">
                            <div className="p-3 bg-zinc-950/40 rounded-xl border border-white/5 space-y-1.5 flex justify-between items-center">
                              <div>
                                <span className="text-[8px] font-mono text-zinc-500 block uppercase">Audit clearance check</span>
                                <span className="text-[11.5px] font-sans font-bold text-white">
                                  Current spec status: <span className="text-cyan-400">{targetVid.approvalStatus}</span>
                                </span>
                              </div>
                              <button
                                id={`btn-trigger-social-sync-${targetVid.id}`}
                                disabled={targetVid.approvalStatus !== 'Approved'}
                                onClick={async () => {
                                  try {
                                    const res = await fetch('/api/guides/send-to-social', {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ videoId: targetVid.id })
                                    });
                                    if (res.ok) {
                                      showToast("🔥 Social Package generated & sent to Social Media Automation!");
                                      refreshPromoState();
                                    }
                                  } catch (_) {}
                                }}
                                className={`px-3 py-1.5 rounded-lg font-bold font-mono uppercase text-[9.5px] transition-all cursor-pointer ${
                                  targetVid.approvalStatus === 'Approved'
                                    ? "bg-purple-600 hover:bg-purple-700 text-white shadow-md shadow-purple-500/10"
                                    : "bg-zinc-900 border border-zinc-850 text-zinc-500 cursor-not-allowed"
                                }`}
                              >
                                Compile Social Pack
                              </button>
                            </div>

                            {targetVid.socialPack ? (
                              <div className="space-y-4 font-mono text-[10.5px]">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <div className="bg-zinc-950/40 p-3 rounded-lg border border-white/5 space-y-1">
                                    <span className="text-[8px] text-purple-400 uppercase block font-bold">15s Hook Clip Info</span>
                                    <p className="text-zinc-300 leading-relaxed text-[9.5px]">"{targetVid.socialPack.hookClip15s}"</p>
                                  </div>
                                  <div className="bg-zinc-950/40 p-3 rounded-lg border border-white/5 space-y-1">
                                    <span className="text-[8px] text-cyan-400 uppercase block font-bold">30s Explainer Info</span>
                                    <p className="text-zinc-300 leading-relaxed text-[9.5px]">"{targetVid.socialPack.explainer30s}"</p>
                                  </div>
                                  <div className="bg-zinc-950/40 p-3 rounded-lg border border-white/5 space-y-1">
                                    <span className="text-[8px] text-amber-550 uppercase block font-bold">60s Walkthrough Info</span>
                                    <p className="text-zinc-300 leading-relaxed text-[9.5px]">"{targetVid.socialPack.walkthrough60s}"</p>
                                  </div>
                                </div>

                                <div className="bg-zinc-950/40 p-4 rounded-xl border border-white/5 space-y-3">
                                  <div className="border-b border-white/5 pb-1.5 flex justify-between items-center text-[8px] text-zinc-500 uppercase">
                                    <span>Copyable copy package</span>
                                    <span className="text-emerald-400">READY</span>
                                  </div>

                                  <div className="space-y-2">
                                    <div>
                                      <span className="text-[8.5px] uppercase text-zinc-500 block">Cap Pack & Hashtags</span>
                                      <p className="text-zinc-300 bg-black/40 p-2 rounded border border-white/5 select-all text-xs">{targetVid.socialPack.captionPack}</p>
                                    </div>
                                    <div>
                                      <span className="text-[8.5px] uppercase text-zinc-500 block">LinkedIn Spec</span>
                                      <p className="text-zinc-300 bg-black/40 p-2 rounded border border-white/5 select-all text-xs">{targetVid.socialPack.linkedInPost}</p>
                                    </div>
                                    <div>
                                      <span className="text-[8.5px] uppercase text-zinc-500 block">Instagram Text</span>
                                      <p className="text-zinc-300 bg-black/40 p-2 rounded border border-white/5 select-all text-xs">{targetVid.socialPack.instagramCaption}</p>
                                    </div>
                                    <div>
                                      <span className="text-[8.5px] uppercase text-zinc-500 block">TikTok Brief</span>
                                      <p className="text-zinc-300 bg-black/40 p-2 rounded border border-white/5 select-all text-xs">{targetVid.socialPack.tikTokCaption}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="py-12 text-center text-zinc-500 font-mono text-xs">
                                Choose an approved guide target, click "Compile Social Pack" to generate high-conversion distribution packets.
                              </div>
                            )}

                          </div>
                        );
                      })()}
                    </div>
                  </motion.div>
                )}

              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* RIGHT COLUMN: ACTIVE PREVIEW HUD DISPLAY - HIDDEN */}
        <div className="hidden">
          
          {/* INTERACTIVE VIDEO HUD DRAWER PLAYER */}
          <div className="bg-[#050915]/95 border border-purple-550/20 rounded-3xl p-5 shadow-2xl relative overflow-hidden backdrop-blur-md">
            <div className="absolute top-0 left-0 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl pointer-events-none" />
            
            <div className="flex justify-between items-center border-b border-white/5 pb-2.5 mb-4">
              <span className="text-[10px] font-mono text-purple-400 font-black uppercase tracking-widest flex items-center gap-1.5">
                <Play className="w-3.5 h-3.5" />
                <span>NexoraX Active Synthesis Monitor</span>
              </span>
              <span className="text-[8px] font-mono text-zinc-500 uppercase bg-zinc-950 px-1.5 py-0.5 rounded border border-white/5">
                HUD_RENDER
              </span>
            </div>

            {previewingVideo ? (
              <div className="space-y-4">
                {/* CHECK If selected is a Guide OR a Promo campaign */}
                {(previewingVideo.id?.startsWith('g-') || previewingVideo.id?.startsWith('guide-') || previewingVideo.id in GUIDE_VIDEO_ASSETS) ? (
                  // GUIDE PLAYBACK & DETAIL MATRIX
                  <div className="space-y-4 font-sans text-left">
                    
                    {/* VIDEO CONTAINER */}
                    <div className="relative w-full bg-black rounded-2xl flex items-center justify-center overflow-hidden border border-purple-550/25 shadow-inner group" style={{ aspectRatio: '16/9' }}>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%)] bg-[length:100%_4px] pointer-events-none z-10" />

                      {previewingVideo.status === 'published' && previewingVideo.videoSrc ? (
                        <div className="absolute inset-0 z-0">
                          <video 
                            ref={videoRef}
                            src={previewingVideo.videoSrc}
                            className="w-full h-full object-cover rounded-2xl"
                            playsInline
                            controls
                            preload="auto"
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onWaiting={() => {
                              if (typeof window !== 'undefined') {
                                const prevDiag = (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__ || {};
                                const diagnostic = {
                                  ...prevDiag,
                                  status: 'buffering',
                                  timestamp: new Date().toLocaleTimeString()
                                };
                                (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__ = diagnostic;
                                window.dispatchEvent(new CustomEvent('nexorax-video-diagnostic', { detail: diagnostic }));
                              }
                            }}
                            onPlaying={() => {
                              if (typeof window !== 'undefined') {
                                const prevDiag = (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__ || {};
                                const diagnostic = {
                                  ...prevDiag,
                                  status: 'playing',
                                  timestamp: new Date().toLocaleTimeString()
                                };
                                (window as any).__NEXORAX_VIDEO_DIAGNOSTIC__ = diagnostic;
                                window.dispatchEvent(new CustomEvent('nexorax-video-diagnostic', { detail: diagnostic }));
                              }
                            }}
                            onError={(e) => {
                              const videoEl = e.currentTarget;
                              const err = videoEl.error;
                              const message = err ? `Error Code: ${err.code}. Msg: ${err.message || 'Format unsupported, CORS block, or stream offline.'}` : 'Source URL loading failed.';
                              emitVideoErrorDiagnostic(err?.code || 4, message);
                            }}
                          />
                        </div>
                      ) : (
                        <div className="text-center p-6 space-y-3 relative z-10 flex flex-col items-center">
                          <div className="w-14 h-14 rounded-full bg-purple-950/60 border border-purple-500/40 flex items-center justify-center text-xl shadow-lg relative">
                            <span className="absolute animate-ping inline-flex h-full w-full rounded-full bg-purple-500/10 opacity-75"></span>
                            ⚡
                          </div>
                          <div className="space-y-1">
                            <h4 className="text-xs font-mono font-black uppercase text-amber-400 tracking-wider">Video Coming Soon</h4>
                            <p className="text-[10px] text-zinc-300 max-w-[320px] leading-relaxed mx-auto">
                              Script, steps, and transcript are ready. Video render is waiting for approval or render engine connection.
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Logo watermark */}
                      <div className="absolute top-3 right-3 text-[9px] font-sans font-extrabold uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#00ffff] to-[#e840f3] z-20">
                        NEXORAX DIGITAL
                      </div>
                    </div>

                    {/* METADATA SUMMARY */}
                    <div className="bg-[#030615]/85 border border-white/5 p-4 rounded-2xl space-y-3 font-sans">
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-[9px] font-mono uppercase bg-[#1a0f30]/65 border border-purple-500/20 px-2 py-0.5 rounded text-purple-400 font-bold">
                            {previewingVideo.category}
                          </span>
                          <span className="text-[8px] font-mono text-zinc-500 uppercase tracking-widest">
                            {previewingVideo.status === 'published' ? (
                              <span className="text-cyan-400 font-black">● ACTIVE PLAYBACK</span>
                            ) : (
                              <span className="text-purple-400 font-black">● PREVIEW COMING SOON</span>
                            )}
                          </span>
                        </div>
                        <h4 className="text-sm font-sans font-black text-white">{previewingVideo.title}</h4>
                        <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
                          {previewingVideo.description}
                        </p>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-mono py-2 border-t border-b border-white/5">
                        <div className="bg-zinc-950/50 p-1.5 rounded-lg border border-white/5">
                          <span className="text-zinc-500 block text-[8px] uppercase">Difficulty</span>
                          <strong className="text-slate-300 font-extrabold">{previewingVideo.difficulty}</strong>
                        </div>
                        <div className="bg-zinc-950/50 p-1.5 rounded-lg border border-white/5">
                          <span className="text-zinc-500 block text-[8px] uppercase">Duration</span>
                          <strong className="text-slate-300 font-extrabold">{previewingVideo.duration}</strong>
                        </div>
                        <div className="bg-zinc-950/50 p-1.5 rounded-lg border border-white/5">
                          <span className="text-zinc-500 block text-[8px] uppercase">Updated</span>
                          <strong className="text-slate-300 font-extrabold text-[9px] whitespace-nowrap">{previewingVideo.updatedAt || previewingVideo.lastUpdated}</strong>
                        </div>
                      </div>

                      {/* TABS SWAPPER */}
                      <div className="flex border-b border-white/5 text-[10px] font-mono">
                        <button 
                          onClick={() => setActiveGuideDetailTab('steps')}
                          className={`flex-1 py-1.5 text-center font-bold uppercase transition-all border-b-2 ${
                            activeGuideDetailTab === 'steps' 
                              ? 'border-cyan-400 text-cyan-400' 
                              : 'border-transparent text-zinc-500 hover:text-zinc-400'
                          }`}
                        >
                          Step-By-Step Checklist
                        </button>
                        <button 
                          onClick={() => setActiveGuideDetailTab('transcript')}
                          className={`flex-1 py-1.5 text-center font-bold uppercase transition-all border-b-2 ${
                            activeGuideDetailTab === 'transcript' 
                              ? 'border-purple-400 text-purple-400' 
                              : 'border-transparent text-zinc-500 hover:text-zinc-400'
                          }`}
                        >
                          Walkthrough Transcript
                        </button>
                      </div>

                      {activeGuideDetailTab === 'steps' ? (
                        <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
                          {previewingVideo.steps?.map((step: string, sIdx: number) => {
                            const isDone = !!completedSteps[previewingVideo.id]?.[sIdx];
                            return (
                              <div 
                                key={sIdx}
                                onClick={() => {
                                  setCompletedSteps(prev => {
                                    const gSteps = prev[previewingVideo.id] || {};
                                    return {
                                      ...prev,
                                      [previewingVideo.id]: {
                                        ...gSteps,
                                        [sIdx]: !gSteps[sIdx]
                                      }
                                    };
                                  });
                                }}
                                className={`flex items-start gap-2.5 p-2 bg-black/40 rounded-xl border border-white/5 hover:bg-black/60 transition-all cursor-pointer text-xs ${
                                  isDone ? 'opacity-55' : ''
                                }`}
                              >
                                <div className={`w-4 h-4 rounded mt-0.5 flex items-center justify-center border shrink-0 transition-all ${
                                  isDone 
                                    ? 'bg-emerald-950 border-emerald-500 text-emerald-400' 
                                    : 'border-zinc-850 text-zinc-600 hover:border-zinc-700'
                                }`}>
                                  {isDone && <Check className="w-3 h-3" />}
                                </div>
                                <span className={`leading-normal ${isDone ? 'line-through text-zinc-500' : 'text-slate-300'}`}>
                                  {step}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="bg-black/50 p-3 rounded-xl border border-white/5 max-h-[180px] overflow-y-auto">
                          <p className="text-xs text-slate-400 leading-relaxed italic pr-1 whitespace-pre-wrap">
                            "{previewingVideo.transcript}"
                          </p>
                        </div>
                      )}

                      {/* RELATED PRODUCTS */}
                      {previewingVideo.relatedProducts && previewingVideo.relatedProducts.length > 0 && (
                        <div className="space-y-1.5 pt-1.5 font-sans">
                          <span className="text-[9px] text-zinc-500 uppercase tracking-widest font-mono font-bold">Related Solutions</span>
                          <div className="flex flex-wrap gap-1.5">
                            {previewingVideo.relatedProducts.map((prod: string, pIdx: number) => {
                              return (
                                <button
                                  key={pIdx}
                                  onClick={() => {
                                    const mapCtaToTab = (ctaTarget: string): string | null => {
                                      if (!ctaTarget) return null;
                                      const matches = ctaTarget.toLowerCase();
                                      if (matches.includes('search')) return 'NexoraX Multiverse Search Engine';
                                      if (matches.includes('hosting')) return 'NexoraX Backup Storage Blocks';
                                      if (matches.includes('mail') || matches.includes('domain')) return 'Mail & Domains Platform';
                                      if (matches.includes('chat')) return 'NexoraX AI Agent Automation';
                                      if (matches.includes('social')) return 'Social Media Automation';
                                      if (matches.includes('ai-automation')) return 'NexoraX AI Agent Automation';
                                      if (matches.includes('voice')) return 'NexoraX Voice';
                                      if (matches.includes('workspace') || matches.includes('tasks')) return 'NexoraX Workspace Suite';
                                      if (matches.includes('media') || matches.includes('studio')) return 'Nexorax Studio';
                                      if (matches.includes('billing') || matches.includes('tiers') || matches.includes('payment')) return 'Tiers';
                                      if (matches.includes('contact') || matches.includes('book')) return 'Contact';
                                      if (matches.includes('ecosystem')) return 'Ecosystem';
                                      return null;
                                    };
                                    const targetTab = mapCtaToTab(prod);
                                    if (targetTab) {
                                      if (onNavigateLandingTab) {
                                        onNavigateLandingTab(targetTab);
                                      } else {
                                        showToast(`Launching ${prod} panel on dashboard lobby...`);
                                      }
                                    } else {
                                      showToast(`Solution connected: ${prod}`);
                                    }
                                  }}
                                  className="text-[9px] font-mono bg-cyan-950/20 text-cyan-400 hover:text-white border border-cyan-500/20 px-2 py-1 rounded-md transition-all cursor-pointer uppercase"
                                >
                                  {prod}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* PRIMARY ACTION CTA BUTTON */}
                      {previewingVideo.ctaLabel && (
                        <button 
                          onClick={() => {
                            const mapCtaToTab = (ctaTarget: string): string | null => {
                              if (!ctaTarget) return null;
                              const matches = ctaTarget.toLowerCase();
                              if (matches.includes('search')) return 'NexoraX Multiverse Search Engine';
                              if (matches.includes('hosting')) return 'NexoraX Backup Storage Blocks';
                              if (matches.includes('mail') || matches.includes('domain')) return 'Mail & Domains Platform';
                              if (matches.includes('chat')) return 'NexoraX AI Agent Automation';
                              if (matches.includes('social')) return 'Social Media Automation';
                              if (matches.includes('ai-automation')) return 'NexoraX AI Agent Automation';
                              if (matches.includes('voice')) return 'NexoraX Voice';
                              if (matches.includes('workspace') || matches.includes('tasks')) return 'NexoraX Workspace Suite';
                              if (matches.includes('media') || matches.includes('studio')) return 'Nexorax Studio';
                              if (matches.includes('billing') || matches.includes('tiers') || matches.includes('payment')) return 'Tiers';
                              if (matches.includes('contact') || matches.includes('book')) return 'Contact';
                              if (matches.includes('ecosystem')) return 'Ecosystem';
                              return null;
                            };
                            const targetTab = mapCtaToTab(previewingVideo.ctaTarget || previewingVideo.ctaLabel);
                            if (targetTab) {
                              if (onNavigateLandingTab) {
                                onNavigateLandingTab(targetTab);
                              } else {
                                showToast(`Launching target platform: ${targetTab}`);
                              }
                            } else {
                              showToast(`Executing CTA action: ${previewingVideo.ctaLabel}`);
                            }
                          }}
                          className="w-full mt-2 flex items-center justify-center gap-1.5 py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-indigo-650 hover:from-cyan-550 hover:to-indigo-550 border border-cyan-500/30 text-white font-mono font-bold text-xs uppercase tracking-wide cursor-pointer shadow-[0_0_15px_rgba(0,195,255,0.2)] transition-all transform hover:-translate-y-0.5"
                        >
                          {previewingVideo.ctaLabel}
                          <ArrowRight className="w-3.5 h-3.5 text-white animate-pulse" />
                        </button>
                      )}

                      {/* CLOSE PREVIEW BUTTON */}
                      <button 
                        onClick={() => setPreviewingVideo(null)}
                        className="w-full py-1 text-center font-mono text-[9px] text-zinc-500 hover:text-zinc-300 uppercase tracking-widest block"
                      >
                        [ CLOSE PREVIEW PANEL ]
                      </button>
                    </div>

                  </div>
                ) : (
                  // ORIGINAL PROMOTIONAL VIDEO GENERIC RENDERING PIPELINE (KEPT EXACTLY INTACT)
                  <div className="space-y-4">
                    
                    {/* Interactive responsive player preview shell */}
                    <div className="relative w-full bg-black rounded-2xl flex items-center justify-center overflow-hidden border border-purple-500/20 shadow-inner group" style={{
                      aspectRatio: activePlaybackFormat === '9:16' ? '9/16' : activePlaybackFormat === '1:1' ? '1/1' : '16/9',
                      maxHeight: activePlaybackFormat === '9:16' ? '460px' : 'none'
                    }}>
                      {/* Subtle retro overlay lines */}
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%)] bg-[length:100%_4px] pointer-events-none z-10" />

                      {/* Dynamic background visualization */}
                      {isPlaying ? (
                        <div className="absolute inset-0 z-0 flex flex-col items-center justify-center overflow-hidden">
                          {/* Black canvas background */}
                          <div className="absolute inset-0 bg-[#02030a]" />

                          {/* Timeline status overlay in the view */}
                          <div className="absolute bottom-4 left-4 right-4 z-20 bg-black/85 border border-white/5 py-1 px-3 rounded-lg text-center flex items-center justify-between pointer-events-none">
                            <span className="text-[8px] font-mono text-[#00ffff] uppercase font-bold tracking-widest">
                              {playerProgress < 10 ? "0-2s: Brand Intro" :
                               playerProgress < 25 ? "2-5s: Problem Hook" :
                               playerProgress < 50 ? "5-10s: Feature Reveal" :
                               playerProgress < 75 ? "10-15s: Benefit Pitch" :
                               "15-20s: Outro CTA"}
                            </span>
                            <div className="flex gap-1">
                              <span className={`w-1.5 h-1.5 rounded-full ${playerProgress < 10 ? 'bg-cyan-400 animate-ping' : 'bg-cyan-500/20'}`} />
                              <span className={`w-1.5 h-1.5 rounded-full ${playerProgress >= 10 && playerProgress < 25 ? 'bg-purple-400 animate-ping' : 'bg-purple-500/20'}`} />
                              <span className={`w-1.5 h-1.5 rounded-full ${playerProgress >= 25 && playerProgress < 50 ? 'bg-[#ff00ff] animate-ping' : 'bg-[#ff00ff]/20'}`} />
                              <span className={`w-1.5 h-1.5 rounded-full ${playerProgress >= 50 && playerProgress < 75 ? 'bg-emerald-400 animate-ping' : 'bg-emerald-500/20'}`} />
                              <span className={`w-1.5 h-1.5 rounded-full ${playerProgress >= 75 ? 'bg-[#f574fe] animate-ping' : 'bg-[#f574fe]/20'}`} />
                            </div>
                          </div>

                          {/* Template specific backgrounds */}
                          {(previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Cyber City Launch' && (
                            <div className="absolute inset-0 opacity-40">
                              {/* Grid line scroll */}
                              <div className="absolute inset-0 bg-[linear-gradient(to_right,#00ffff0e_1px,transparent_1px),linear-gradient(to_bottom,#00ffff0e_1px,transparent_1px)] bg-[size:24px_24px]" />
                              {/* Cyan vertical trails */}
                              <div className="absolute bottom-0 left-1/4 w-0.5 h-32 bg-gradient-to-t from-cyan-400 to-transparent animate-bounce" style={{ animationDuration: '2.5s' }} />
                              <div className="absolute bottom-0 left-2/3 w-0.5 h-44 bg-gradient-to-t from-purple-400 to-transparent animate-bounce" style={{ animationDuration: '3.8s' }} />
                              <div className="absolute bottom-0 left-1/2 w-0.5 h-20 bg-gradient-to-t from-[#ff00ff] to-transparent animate-bounce" style={{ animationDuration: '1.7s' }} />
                              {/* Glowing sphere */}
                              <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-cyan-500/10 rounded-full filter blur-xl animate-pulse" style={{ animationDuration: '4s' }} />
                            </div>
                          )}

                          {(previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Neural Brain Pulse' && (
                            <div className="absolute inset-0 opacity-40 flex items-center justify-center">
                              {/* Glowing network lines connection simulation */}
                              <svg className="w-full h-full text-purple-500/30" viewBox="0 0 100 100" preserveAspectRatio="none">
                                <line x1="20" y1="20" x2="50" y2="50" stroke="currentColor" strokeWidth={0.5} strokeDasharray="1 1" className="animate-pulse" />
                                <line x1="80" y1="20" x2="50" y2="50" stroke="currentColor" strokeWidth={0.5} strokeDasharray="1 1" />
                                <line x1="10" y1="80" x2="50" y2="50" stroke="currentColor" strokeWidth={0.5} />
                                <line x1="90" y1="80" x2="50" y2="50" stroke="currentColor" strokeWidth={0.5} />
                                <circle cx="20" cy="20" r="2" fill="#00ffff" className="animate-ping" style={{ animationDuration: '1.2s' }} />
                                <circle cx="80" cy="20" r="2" fill="#e840f3" className="animate-ping" style={{ animationDuration: '2s' }} />
                                <circle cx="50" cy="50" r="3" fill="#ffffff" />
                              </svg>
                              <div className="absolute w-44 h-44 bg-purple-700/5 rounded-full filter blur-2xl animate-pulse" />
                            </div>
                          )}

                          {(previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Purple Glass Product Reveal' && (
                            <div className="absolute inset-0 opacity-40 flex items-center justify-center">
                              {/* Glass wireframe overlays */}
                              <div className="w-4/5 h-2/3 border border-[#e840f3]/20 bg-[#e840f3]/5 rounded-xl flex flex-col justify-between p-3 relative transform rotate-x-12 rotate-y-12 animate-pulse">
                                <div className="h-4 w-1/2 bg-white/10 rounded" />
                                <div className="space-y-1.5">
                                  <div className="h-2 w-full bg-cyan-400/20 rounded" />
                                  <div className="h-2 w-4/5 bg-cyan-400/20 rounded" />
                                </div>
                                <div className="h-6 w-1/4 bg-white/5 rounded self-end border border-white/10" />
                              </div>
                            </div>
                          )}

                          {(previewingVideo.selectedTemplate || 'Cyber City Launch') === 'AI Automation Explainer' && (
                            <div className="absolute inset-0 opacity-30 flex items-center justify-center">
                              <div className="flex gap-6">
                                <div className="w-12 h-12 rounded-full border-2 border-dashed border-cyan-400 flex items-center justify-center animate-spin" style={{ animationDuration: '10s' }}>
                                  <span className="text-[10px] font-mono text-cyan-400">⚡</span>
                                </div>
                                <div className="w-16 h-16 rounded-full border-2 border-dashed border-purple-400 flex items-center justify-center animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }}>
                                  <span className="text-sm">⚙️</span>
                                </div>
                              </div>
                            </div>
                          )}

                          {(previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Social Media Growth Promo' && (
                            <div className="absolute inset-0 opacity-40">
                              {/* Climbing green chart lines */}
                              <svg className="absolute bottom-8 left-0 right-0 h-1/2 w-full text-emerald-400/20" viewBox="0 0 100 50" preserveAspectRatio="none">
                                <path d="M 0 45 Q 25 30 50 20 T 100 5 L 100 50 L 0 50 Z" fill="currentColor" />
                              </svg>
                              <span className="absolute top-1/4 left-10 text-xs animate-bounce" style={{ animationDuration: '1s' }}>🚀</span>
                              <span className="absolute top-1/3 right-12 text-xs animate-bounce" style={{ animationDuration: '1.4s' }}>📈</span>
                              <span className="absolute bottom-1/3 left-1/3 text-xs animate-bounce" style={{ animationDuration: '1.8s' }}>❤️</span>
                            </div>
                          )}

                          {(previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Premium Enterprise Promo' && (
                            <div className="absolute inset-0 opacity-40">
                              {/* Radial golden mesh sweeping */}
                              <div className="absolute inset-0 bg-gradient-radial from-amber-500/5 via-transparent to-transparent animate-pulse" style={{ animationDuration: '5s' }} />
                              <div className="absolute top-0 bottom-0 left-0 right-0 bg-[linear-gradient(45deg,#000055_25%,transparent_25%,transparent_75%,#000055_75%,#000055),linear-gradient(45deg,#000055_25%,transparent_25%,transparent_75%,#000055_75%,#000055)] bg-[size:40px_40px] opacity-10" />
                            </div>
                          )}

                          {/* Central animated contents */}
                          <div className="relative text-center p-4 space-y-4 max-w-xs z-10 flex flex-col items-center">
                            {/* Audio Wave anim elements */}
                            <div className="flex justify-center items-end gap-1 px-3 py-1.5 bg-black/40 border border-white/5 rounded-full h-8 mb-2 backdrop-blur-sm z-30">
                              {waveformBars.map((val, idx) => (
                                <div 
                                  key={idx} 
                                  className={`w-1 rounded-t-sm transition-all duration-100 ${
                                    (previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Cyber City Launch' ? 'bg-[#00ffff]' :
                                    (previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Neural Brain Pulse' ? 'bg-[#9d4edd]' :
                                    (previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Purple Glass Product Reveal' ? 'bg-[#ff00ff]' :
                                    (previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Social Media Growth Promo' ? 'bg-[#10b981]' :
                                    'bg-[#8a2be2]'
                                  }`} 
                                  style={{ height: `${Math.min(val, 24)}px` }}
                                />
                              ))}
                            </div>
                            
                            {/* Dynamic Text Captions Overlay with slide in */}
                            <div className="bg-black/95 border border-[#e840f3]/20 shadow-[0_0_20px_rgba(232,64,243,0.15)] px-4 py-3 rounded-2xl backdrop-blur-xl transition-all duration-300 transform scale-100 max-w-[265px]">
                              <span className="text-[7px] font-mono text-cyan-400 block tracking-widest uppercase mb-1 font-bold animate-pulse">
                                {playerProgress < 10 ? "BRAND SYNAPSE INITIATED" :
                                 playerProgress < 25 ? "IMPACT VECTOR HOOK" :
                                 playerProgress < 50 ? "SYSTEM REVEAL COUPLING" :
                                 playerProgress < 75 ? "EFFICIENCY MARGIN GAIN" :
                                 "MANDATORY ACTIVATION OUTRO"}
                              </span>
                              <p className="text-[11px] font-sans font-black text-center text-white tracking-wide leading-snug normal-case">
                                {playerProgress < 10 ? (previewingVideo.title || "NEXORAX AUTOMATION") :
                                  playerProgress < 25 ? (previewingVideo.script.hook || previewingVideo.script.intro) :
                                  playerProgress < 50 ? (previewingVideo.script.solution || previewingVideo.script.step1) :
                                  playerProgress < 75 ? (previewingVideo.script.benefit || previewingVideo.script.step3) :
                                  (previewingVideo.script.cta || previewingVideo.script.outro)
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center p-6 space-y-3 relative z-10 flex flex-col items-center">
                          <div className="w-12 h-12 rounded-full bg-purple-950/50 border border-purple-500/40 flex items-center justify-center text-lg shadow-lg">
                            🎬
                          </div>
                          <div className="space-y-1">
                            <h4 className="text-xs font-mono font-black uppercase text-purple-400">VIDEO STANDBY</h4>
                            <p className="text-[10px] text-zinc-500">Render loop paused. Click Play below.</p>
                          </div>
                        </div>
                      )}

                      {/* Format watermarks */}
                      <div className="absolute top-3 left-3 px-2 py-0.5 rounded bg-black/75 border border-white/10 text-[8px] font-mono uppercase font-black text-slate-400 z-20 flex items-center gap-1">
                        <Laptop className="w-2.5 h-2.5" /> {activePlaybackFormat} FORMAT
                      </div>

                      {/* Logo watermark */}
                      <div className="absolute top-3 right-3 text-[9px] font-sans font-extrabold uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#00ffff] to-[#e840f3] z-20">
                        NEXORAX DIGITAL
                      </div>

                      {/* Safe info warning overlays */}
                      <div className="absolute bottom-3 right-3 px-1.5 py-0.5 rounded bg-emerald-950/80 border border-emerald-500/30 text-[7px] font-mono text-emerald-400 z-10">
                        SECURED PUBLIC DETAILS ONLY
                      </div>
                    </div>

                    {/* Simulated player controller tools */}
                    <div className="bg-zinc-950/90 border border-white/5 p-3 rounded-xl space-y-3 font-mono text-left">
                      <div className="flex justify-between items-center">
                        <button 
                          onClick={() => setIsPlaying(!isPlaying)}
                          className="px-3.5 py-1.5 bg-purple-650 hover:bg-purple-550 border border-purple-500/30 text-white rounded-lg text-[10px] font-bold uppercase transition-all flex items-center gap-1 cursor-pointer"
                        >
                          {isPlaying ? (
                            <>
                              <Pause className="w-3 h-3 text-white" /> Pause Stream
                            </>
                          ) : (
                            <>
                              <Play className="w-3 h-3 text-white" /> Play Render
                            </>
                          )}
                        </button>
                        
                        <span className="text-[10px] text-zinc-500 block">
                          Refining: {Math.floor(playerProgress)}%
                        </span>
                      </div>

                      {/* Progress tracker bar */}
                      <div className="w-full bg-zinc-900 rounded-full h-1 overflow-hidden">
                        <div className="bg-purple-500 h-full transition-all" style={{ width: `${playerProgress}%` }} />
                      </div>

                      {/* Aspect configuration selectors */}
                      <div className="space-y-1">
                        <span className="text-[8px] text-zinc-500 block uppercase">Target Multi-Aspect Refactoring:</span>
                        <div className="flex gap-1 text-[8.5px] text-center font-bold">
                          {[
                            { id: '16:9', label: "Widescreen 16:9", icon: Laptop },
                            { id: '9:16', label: "Vertical 9:16", icon: Smartphone },
                            { id: '1:1', label: "Square 1:1", icon: Radio }
                          ].map((item) => (
                            <button
                              key={item.id}
                              onClick={() => setActivePlaybackFormat(item.id as any)}
                              className={`flex-1 flex flex-col items-center py-1 rounded border transition-all cursor-pointer ${
                                activePlaybackFormat === item.id 
                                  ? "bg-purple-950/45 border-purple-500/40 text-[#f574fe]" 
                                  : "bg-black/30 border-transparent text-zinc-400 hover:text-zinc-200"
                              }`}
                            >
                              <item.icon className="w-3.5 h-3.5 mb-0.5" />
                              <span>{item.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* captions outline */}
                      <div className="border-t border-white/5 pt-2 text-xs">
                        <span className="text-[9px] text-[#00f0ff] uppercase block tracking-wider font-extrabold mb-1">
                          Platform Meta & Description Packages:
                        </span>
                        <p className="text-[10px] text-slate-400 font-sans leading-normal normal-case">
                          <strong>Active Captions:</strong> Bypass manual digital drag. Automate workflows using NexoraX deep system nodes with zero-cost Access grants.
                        </p>
                        <p className="text-[9.5px] text-purple-400 block font-mono mt-1">
                          HASHTAGS: #{previewingVideo.hashtags?.join(' #') || 'NexoraX'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-24 text-center text-zinc-550 space-y-2 font-mono">
                <Video className="w-10 h-10 text-zinc-600 mx-auto opacity-40 animate-pulse-slow" />
                <h4 className="text-xs uppercase font-extrabold">Active Stream Offline</h4>
                <p className="text-[10px]">Select any Guide or Promotional video to mount custom visual frames instantly.</p>
              </div>
            )}
          </div>

          {/* NexoraX billing records MULTI-AGENTS ALIGNMENT DETAILS */}
          <div className="bg-[#030713]/60 border border-[#00ffff]/20 rounded-3xl p-5 relative overflow-hidden backdrop-blur-md">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#00ffff]/5 rounded-full blur-2xl pointer-events-none" />
            <div className="flex gap-2 items-center text-cyan-400 mb-2 border-b border-cyan-500/10 pb-2">
              <ShieldAlert className="w-4 h-4" />
              <h4 className="text-xs font-mono font-bold uppercase tracking-widest text-[#00f0ff]">
                NexoraX billing records secure links
              </h4>
            </div>

            <p className="text-[11px] font-sans text-slate-400 leading-relaxed mb-3">
              All promotional videos generated by this module adhere strictly to NexoraX billing records program parameters. Compensation placements and direct-billing metrics use verified public channels to secure absolute client compliance.
            </p>

            <div className="bg-black/40 border border-white/5 p-3 rounded-xl font-mono text-[9px] text-zinc-500 space-y-1.5 uppercase">
              <div className="flex justify-between">
                <span>Remittance Reference</span>
                <span className="text-slate-300">Secure Payment Provider Grant NH******B</span>
              </div>
              <div className="flex justify-between">
                <span>Account Sync Hook</span>
                <span className="text-indigo-400">Secure Payment Provider ******15</span>
              </div>
              <div className="flex justify-between">
                <span>Office Designation</span>
                <span className="text-slate-300">NexoraX Digital Ltd (SC887095)</span>
              </div>
            </div>
          </div>

        </div>

      </div>

      {/* BOTTOM OUTRO BRANDING CTA */}
      <div className="text-center bg-gradient-to-b from-purple-950/20 to-black/90 border border-purple-550/15 p-8 rounded-3xl backdrop-blur-md space-y-4 mb-8">
        <div className="flex justify-center flex-wrap gap-2.5 text-cyan-450 font-mono text-[10px] tracking-[0.25em] font-black uppercase">
          <span>LEARN</span>
          <span>•</span>
          <span>CREATE</span>
          <span>•</span>
          <span>PROMOTE</span>
          <span>•</span>
          <span>GROW</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-sans font-black uppercase tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-300 to-indigo-300 max-w-2xl mx-auto leading-tight">
          Guides That Teach. Videos That Sell. Updates That Promote Themselves.
        </h2>
        <p className="text-xs text-slate-400 max-w-xl mx-auto font-mono">
          Nexorax NexoraX media studio: Automated marketing funnels configured for 15-2-8 minute God-Mode conversions.
        </p>
      </div>

      {/* MONOCHROME CINEMATIC ADVANCED PROMO VIDEO SYNTHESIS MODAL PLAYER */}
      <AnimatePresence>
        {previewingVideo && !(previewingVideo.id?.startsWith('g-') || previewingVideo.id?.startsWith('guide-') || previewingVideo.id in GUIDE_VIDEO_ASSETS) && (
          <div className="fixed inset-0 bg-black/95 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-lg w-full bg-[#050915]/95 border border-purple-550/30 rounded-3xl p-6 shadow-2xl space-y-4 my-8"
              onClick={(e) => e.stopPropagation()}
            >
              {/* HEADER SECTION */}
              <div className="flex justify-between items-center border-b border-white/5 pb-3">
                <span className="text-[10px] font-mono text-purple-400 font-bold uppercase tracking-widest flex items-center gap-1.5">
                  <Play className="w-4 h-4 text-purple-400 animate-pulse" />
                  <span>NexoraX Active Synthesis Monitor</span>
                </span>
                <button 
                  onClick={() => {
                    setIsPlaying(false);
                    setPreviewingVideo(null);
                  }}
                  className="px-2.5 py-1 bg-white/5 hover:bg-white/10 rounded-lg text-[10px] font-mono text-zinc-400 hover:text-white transition-all uppercase cursor-pointer"
                >
                  [ CLOSE player ]
                </button>
              </div>

              {/* FORMAT SELECTOR SWITCHER inside the modal */}
              <div className="flex justify-between items-center bg-black/45 p-2 border border-white/5 rounded-2xl">
                <span className="text-[9px] font-mono text-zinc-500 uppercase font-black">Select Aspect Ratio</span>
                <div className="flex gap-1.5">
                  {[
                    { id: '16:9', label: '16:9 Horizontal' },
                    { id: '9:16', label: '9:16 Vertical' },
                    { id: '1:1', label: '1:1 Square' }
                  ].map((fmt) => (
                    <button
                      key={fmt.id}
                      onClick={() => setActivePlaybackFormat(fmt.id as any)}
                      className={`px-2 py-1 rounded text-[9px] font-mono transition-all cursor-pointer border ${
                        activePlaybackFormat === fmt.id
                          ? 'bg-purple-950 text-purple-400 border-purple-500/30 font-bold'
                          : 'bg-black/20 text-zinc-500 border-transparent hover:border-zinc-850'
                      }`}
                    >
                      {fmt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* PLAYER VIEWPORT SHELL */}
              <div className="relative w-full bg-black rounded-2xl flex items-center justify-center overflow-hidden border border-purple-500/25 shadow-inner" style={{
                aspectRatio: activePlaybackFormat === '9:16' ? '9/16' : activePlaybackFormat === '1:1' ? '1/1' : '16/9',
                maxHeight: activePlaybackFormat === '9:16' ? '400px' : 'none'
              }}>
                {/* Subtle retro scan overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%)] bg-[length:100%_4px] pointer-events-none z-10" />

                {isPlaying ? (
                  <div className="absolute inset-0 z-0 flex flex-col items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 bg-[#02030a]" />

                    {/* Timeline segment indicator overlay */}
                    <div className="absolute bottom-4 left-4 right-4 z-20 bg-black/85 border border-white/5 py-1 px-3 rounded-lg text-center flex items-center justify-between pointer-events-none">
                      <span className="text-[8px] font-mono text-[#00ffff] uppercase font-bold tracking-widest">
                        {playerProgress < 10 ? "0-2s: Brand Intro" :
                         playerProgress < 25 ? "2-5s: Problem Hook" :
                         playerProgress < 50 ? "5-10s: Feature Reveal" :
                         playerProgress < 75 ? "10-15s: Benefit Pitch" :
                         "15-20s: Outro CTA"}
                      </span>
                      <div className="flex gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${playerProgress < 10 ? 'bg-cyan-400 animate-ping' : 'bg-cyan-550/20'}`} />
                        <span className={`w-1.5 h-1.5 rounded-full ${playerProgress >= 10 && playerProgress < 25 ? 'bg-purple-400 animate-ping' : 'bg-purple-500/20'}`} />
                        <span className={`w-1.5 h-1.5 rounded-full ${playerProgress >= 25 && playerProgress < 50 ? 'bg-[#ff00ff] animate-ping' : 'bg-[#ff00ff]/20'}`} />
                        <span className={`w-1.5 h-1.5 rounded-full ${playerProgress >= 50 && playerProgress < 75 ? 'bg-emerald-400 animate-ping' : 'bg-emerald-500/20'}`} />
                        <span className={`w-1.5 h-1.5 rounded-full ${playerProgress >= 75 ? 'bg-[#f574fe] animate-ping' : 'bg-[#f574fe]/20'}`} />
                      </div>
                    </div>

                    {/* Template dynamic layout animations */}
                    {(previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Cyber City Launch' && (
                      <div className="absolute inset-0 opacity-40">
                        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00ffff0e_1px,transparent_1px),linear-gradient(to_bottom,#00ffff0e_1px,transparent_1px)] bg-[size:24px_24px]" />
                        <div className="absolute bottom-0 left-1/4 w-0.5 h-32 bg-gradient-to-t from-cyan-400 to-transparent animate-bounce" style={{ animationDuration: '2.5s' }} />
                        <div className="absolute bottom-0 left-2/3 w-0.5 h-44 bg-gradient-to-t from-purple-400 to-transparent animate-bounce" style={{ animationDuration: '3.8s' }} />
                        <div className="absolute bottom-0 left-1/2 w-0.5 h-20 bg-gradient-to-t from-[#ff00ff] to-transparent animate-bounce" style={{ animationDuration: '1.7s' }} />
                        <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-cyan-500/10 rounded-full filter blur-xl animate-pulse" style={{ animationDuration: '4s' }} />
                      </div>
                    )}

                    {(previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Neural Brain Pulse' && (
                      <div className="absolute inset-0 opacity-40 flex items-center justify-center">
                        <svg className="w-full h-full text-purple-500/30" viewBox="0 0 100 100" preserveAspectRatio="none">
                          <line x1="20" y1="20" x2="50" y2="50" stroke="currentColor" strokeWidth={0.5} strokeDasharray="1 1" className="animate-pulse" />
                          <line x1="80" y1="20" x2="50" y2="50" stroke="currentColor" strokeWidth={0.5} strokeDasharray="1 1" />
                          <circle cx="20" cy="20" r="2" fill="#00ffff" className="animate-ping" style={{ animationDuration: '1.2s' }} />
                          <circle cx="80" cy="20" r="2" fill="#e840f3" className="animate-ping" style={{ animationDuration: '2s' }} />
                        </svg>
                        <div className="absolute w-44 h-44 bg-purple-700/5 rounded-full filter blur-2xl animate-pulse" />
                      </div>
                    )}

                    {(previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Purple Glass Product Reveal' && (
                      <div className="absolute inset-0 opacity-40 flex items-center justify-center">
                        <div className="w-4/5 h-2/3 border border-[#e840f3]/20 bg-[#e840f3]/5 rounded-xl flex flex-col justify-between p-3 relative transform rotate-x-12 rotate-y-12 animate-pulse">
                          <div className="h-4 w-1/2 bg-white/10 rounded" />
                          <div className="space-y-1.5">
                            <div className="h-2 w-full bg-cyan-400/20 rounded" />
                            <div className="h-2 w-4/5 bg-cyan-400/20 rounded" />
                          </div>
                        </div>
                      </div>
                    )}

                    {(previewingVideo.selectedTemplate || 'Cyber City Launch') === 'AI Automation Explainer' && (
                      <div className="absolute inset-0 opacity-30 flex items-center justify-center">
                        <div className="flex gap-6">
                          <div className="w-12 h-12 rounded-full border-2 border-dashed border-cyan-400 flex items-center justify-center animate-spin" style={{ animationDuration: '10s' }} />
                          <div className="w-16 h-16 rounded-full border-2 border-dashed border-purple-400 flex items-center justify-center animate-spin" style={{ animationDuration: '12s', animationDirection: 'reverse' }} />
                        </div>
                      </div>
                    )}

                    {(previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Social Media Growth Promo' && (
                      <div className="absolute inset-0 opacity-40">
                        <svg className="absolute bottom-8 left-0 right-0 h-1/2 w-full text-emerald-400/20" viewBox="0 0 100 50" preserveAspectRatio="none">
                          <path d="M 0 45 Q 25 30 50 20 T 100 5 L 100 50 L 0 50 Z" fill="currentColor" />
                        </svg>
                        <span className="absolute top-1/4 left-10 text-xs animate-bounce" style={{ animationDuration: '1s' }}>🚀</span>
                        <span className="absolute top-1/3 right-12 text-xs animate-bounce" style={{ animationDuration: '1.4s' }}>📈</span>
                      </div>
                    )}

                    {(previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Premium Enterprise Promo' && (
                      <div className="absolute inset-0 opacity-40">
                        <div className="absolute inset-0 bg-gradient-radial from-amber-500/5 via-transparent to-transparent animate-pulse" />
                      </div>
                    )}

                    {/* Central Synthesis Player contents */}
                    <div className="relative text-center p-4 space-y-4 max-w-xs z-10 flex flex-col items-center">
                      <div className="flex justify-center items-end gap-1 px-3 py-1.5 bg-black/40 border border-white/5 rounded-full h-8 mb-2 backdrop-blur-sm z-30">
                        {waveformBars.map((val, idx) => (
                          <div 
                            key={idx} 
                            className={`w-1 rounded-t-sm transition-all duration-100 ${
                              (previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Cyber City Launch' ? 'bg-[#00ffff]' :
                              (previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Neural Brain Pulse' ? 'bg-[#9d4edd]' :
                              (previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Purple Glass Product Reveal' ? 'bg-[#ff00ff]' :
                              (previewingVideo.selectedTemplate || 'Cyber City Launch') === 'Social Media Growth Promo' ? 'bg-[#10b981]' :
                              'bg-[#8a2be2]'
                            }`} 
                            style={{ height: `${Math.min(val, 24)}px` }}
                          />
                        ))}
                      </div>

                      {/* Active Dynamic Caption Line */}
                      <p className="text-xs font-sans font-extrabold tracking-wide text-white uppercase text-center leading-snug drop-shadow-md z-30 min-h-[36px]">
                        {playerProgress < 10 ? (previewingVideo.title || "NEXORAX AUTOMATION") :
                         playerProgress < 25 ? (previewingVideo.script?.hook || previewingVideo.script?.intro || "NEXORAX AUTOMATION") :
                         playerProgress < 50 ? (previewingVideo.script?.problem || previewingVideo.script?.step1 || "NEXORAX AUTOMATION") :
                         playerProgress < 75 ? (previewingVideo.script?.solution || previewingVideo.script?.step3 || "NEXORAX AUTOMATION") :
                         (previewingVideo.script?.cta || previewingVideo.script?.outro || "NEXORAX AUTOMATION")}
                      </p>

                      {/* Video Progress Line */}
                      <div className="w-full bg-white/15 rounded-full h-1 overflow-hidden z-20">
                        <div className="bg-purple-500 h-full transition-all duration-200" style={{ width: `${playerProgress}%` }} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-6 space-y-3 relative z-10 flex flex-col items-center">
                    <div className="w-12 h-12 rounded-full bg-purple-950/60 border border-purple-500/35 flex items-center justify-center text-lg shadow-lg">
                      ⏸️
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-xs font-mono font-black uppercase text-purple-400 tracking-wider">Playback Paused</h4>
                      <p className="text-[10px] text-zinc-400 max-w-[280px]">
                        Click the activation key below to initialize high-fidelity audio and video simulations.
                      </p>
                    </div>
                  </div>
                )}

                <div className="absolute top-3 right-3 text-[8px] font-sans font-black uppercase text-white/50 bg-black/60 px-1.5 py-0.5 rounded tracking-widest z-20">
                  NEXORAX INT-VT-07
                </div>
              </div>

              {/* ACTION BUTTON CONSOLE */}
              <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="py-2.5 rounded-xl border border-purple-500/30 hover:border-purple-500/50 text-white font-bold uppercase cursor-pointer bg-purple-950/20 hover:bg-purple-950/40 text-center"
                >
                  {isPlaying ? '⏸️ PAUSE SYST' : '▶️ PLAY RUNTIME'}
                </button>
                <button
                  onClick={() => setPlayerProgress(0)}
                  className="py-2.5 rounded-xl border border-zinc-805 hover:border-zinc-700 text-zinc-450 hover:text-white uppercase font-bold cursor-pointer bg-black/40 text-center"
                >
                  ⏳ Reset Frame
                </button>
              </div>

              {/* PLAYBACK SCRIPT BLOCK */}
              <div className="bg-black/40 border border-white/5 rounded-2xl p-4 space-y-2">
                <span className="text-[9px] font-mono text-cyan-400 font-extrabold uppercase tracking-widest block">
                  Campaign Script Outline
                </span>
                <div className="space-y-1.5 text-xs font-sans leading-relaxed text-zinc-300">
                  <p><strong className="text-[8px] font-mono text-zinc-550 uppercase mr-1">[HOOK]:</strong> "{previewingVideo.script?.hook}"</p>
                  <p><strong className="text-[8px] font-mono text-zinc-550 uppercase mr-1">[PROBLEM]:</strong> "{previewingVideo.script?.problem || 'Standard inefficiencies stall business growth.'}"</p>
                  <p><strong className="text-[8px] font-mono text-zinc-550 uppercase mr-1">[SOLUTION]:</strong> "{previewingVideo.script?.solution}"</p>
                  <p className="border-t border-white/5 pt-1 mt-1"><strong className="text-[8px] font-mono text-emerald-400 uppercase mr-1">[CTA]:</strong> "{previewingVideo.script?.cta || 'Visit nexoraxdigital.co.uk today!'}"</p>
                </div>
              </div>

              {/* OUTWARD CHANNELS SUMMARY */}
              <div className="text-[10px] font-mono p-3.5 bg-[#030611]/80 rounded-2xl border border-white/5 text-zinc-450 uppercase flex justify-between items-center">
                <span>Targets selected:</span>
                <strong className="text-zinc-200">
                  {previewingVideo.platformsSelected?.join(', ') || 'YouTube Shorts, Instagram Reels, LinkedIn'}
                </strong>
              </div>

            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Icon fallbacks inside the file
function BookOpenIcon(props: any) {
  return <Layers {...props} />;
}
function PlayCircleIcon(props: any) {
  return <Video {...props} />;
}
function WatcherIcon(props: any) {
  return <Radio {...props} />;
}
function VerifyIcon(props: any) {
  return <CheckCircle {...props} />;
}
function AnalyticsIcon(props: any) {
  return <BarChart2 {...props} />;
}
function SettingsIcon(props: any) {
  return <Settings {...props} />;
}
function SparklesIcon(props: any) {
  return <Sparkles {...props} />;
}
